<template>
  <section class="reports-page" :dir="pageDirection">
    <div class="section-head">
      <h2>{{ tr("title") }}</h2>
      <p class="desc">{{ tr("description") }}</p>
    </div>

    <div class="report-grid">
      <div
        v-for="(report, index) in localizedReports"
        :key="report.id"
        class="report-card"
      >
        <div class="rc-top">
          <span class="rc-num">
            {{ String(index + 1).padStart(2, "0") }}
          </span>

          <h4>{{ report.name }}</h4>
        </div>

        <div class="rc-goal">{{ tr("audience") }}: {{ report.audience }}</div>

        <div class="rc-meta">
          <span>{{ tr("level") }}: {{ report.level }}</span>
          <span>{{ tr("frequency") }}: {{ report.freq }}</span>
        </div>

        <button class="btn" @click="openReport(report)">
          {{ tr("createReport") }}
        </button>
      </div>
    </div>

    <div v-if="modalOpen" class="modal-backdrop" @click.self="closeReport">
      <div class="modal">
        <div class="m-head">
          <h3>{{ selectedReport?.name }}</h3>
          <button class="close-x" @click="closeReport">✕</button>
        </div>

        <div class="m-body">
          <div
            class="print-report"
            :class="{ 'r07-print-report': selectedReport?.id === 'R07' }"
          >
            <!-- الرأس الرسمي المشترك لجميع التقارير -->
            <header class="official-report-header">
              <div class="report-logos">
                <div class="report-logo-side">
                  <img
                    src="@/assets/yemen-logo.png"
                    :alt="tr('ministryLogoAlt')"
                    class="yemen-logo"
                  />
                </div>
                <div class="report-logo-center"></div>
                <div class="report-logo-side"></div>
                <!-- <div class="report-logo-center">
                  <img
                    src="@/assets/unops-logo.png"
                    alt="UNOPS"
                    class="unops-logo"
                  />
                </div>

                <div class="report-logo-side">
                  <img
                    src="@/assets/afcar-logo.png"
                    alt="AFCAR Consulting"
                    class="afcar-logo"
                  />
                </div> -->
              </div>

              <div class="official-header-line"></div>

              <div class="official-report-title">
                <span class="report-system-name">{{ tr("systemName") }}</span>

                <h1>{{ selectedReport?.name }}</h1>

                <div class="report-reference">
                  <span>{{ tr("reportCode") }}: {{ selectedReport?.id }}</span>
                  <span>{{ tr("issueDate") }}: {{ reportDate }}</span>
                </div>
              </div>
            </header>

            <div class="pr-head">
              <div class="sub">{{ tr("projectName") }}</div>
            </div>

            <h3>{{ tr("reportData") }}</h3>

            <table>
              <tbody>
                <tr>
                  <td>{{ tr("targetAudience") }}</td>
                  <td>{{ selectedReport?.audience }}</td>
                </tr>
                <tr>
                  <td>{{ tr("level") }}</td>
                  <td>
                    {{
                      isInventoryReport && reportData
                        ? inventoryLevelLabel
                        : selectedReport?.level
                    }}
                  </td>
                </tr>
                <tr>
                  <td>{{ tr("frequency") }}</td>
                  <td>{{ selectedReport?.freq }}</td>
                </tr>
                <tr>
                  <td>{{ tr("createdAt") }}</td>
                  <td>{{ reportDate }}</td>
                </tr>
              </tbody>
            </table>

            <!-- الفلتر الإداري الرباعي الموحد -->
            <div
              v-if="usesAdministrativeLevelFilter"
              class="no-print administrative-level-filter"
            >
              <div class="administrative-filter-head">
                <div>
                  <strong>مستوى عرض التقرير</strong>
                  <span> الوطني ← المحافظة ← المديرية ← المرفق الصحي </span>
                </div>
              </div>

              <div class="administrative-filter-grid">
                <div class="administrative-filter-field">
                  <label for="reportAdministrativeLevel">المستوى</label>
                  <select
                    id="reportAdministrativeLevel"
                    v-model="selectedReportLevel"
                    @change="handleAdministrativeLevelChange"
                  >
                    <option
                      v-for="option in administrativeLevelOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>

                <div
                  v-if="administrativeNeedsGovernorate"
                  class="administrative-filter-field"
                >
                  <label for="administrativeGovernorate">المحافظة</label>
                  <select
                    id="administrativeGovernorate"
                    v-model="selectedGovernorateId"
                    :disabled="loadingFilters"
                    @change="handleAdministrativeGovernorateChange"
                  >
                    <option value="" disabled>اختر المحافظة</option>
                    <option
                      v-for="governorate in governorates"
                      :key="governorate.id || governorate.code"
                      :value="
                        governorate.id ||
                        governorate.governorate_id ||
                        governorate.code
                      "
                    >
                      {{
                        governorate.name_ar ||
                        governorate.governorate_name_ar ||
                        governorate.name ||
                        governorate.code
                      }}
                    </option>
                  </select>
                </div>

                <div
                  v-if="administrativeNeedsDistrict"
                  class="administrative-filter-field"
                >
                  <label for="administrativeDistrict">المديرية</label>
                  <select
                    id="administrativeDistrict"
                    v-model="selectedDistrictCode"
                    :disabled="loadingFilters || !selectedGovernorateId"
                    @change="handleAdministrativeDistrictChange"
                  >
                    <option value="" disabled>اختر المديرية</option>
                    <option
                      v-for="district in filteredDistricts"
                      :key="
                        district.code || district.district_code || district.id
                      "
                      :value="
                        district.code || district.district_code || district.id
                      "
                    >
                      {{
                        district.name_ar ||
                        district.district_name_ar ||
                        district.name ||
                        district.code
                      }}
                    </option>
                  </select>
                </div>

                <div
                  v-if="administrativeNeedsFacility"
                  class="administrative-filter-field"
                >
                  <label for="administrativeFacility">المرفق الصحي</label>
                  <select
                    id="administrativeFacility"
                    v-model="selectedAdministrativeFacilityId"
                    :disabled="loadingFacilities || !selectedDistrictCode"
                    @change="resetAdministrativeResult"
                  >
                    <option value="" disabled>اختر المرفق الصحي</option>
                    <option
                      v-for="facility in facilities"
                      :key="facility.id"
                      :value="facility.id"
                    >
                      {{ facility.name }}
                    </option>
                  </select>
                </div>

                <div
                  v-if="selectedReport?.id === 'R06'"
                  class="administrative-filter-field"
                >
                  <label for="administrativeAxis">المحور المتخصص</label>
                  <select id="administrativeAxis" v-model="selectedAxis">
                    <option
                      v-for="axis in axesOptions"
                      :key="axis.code"
                      :value="axis.code"
                    >
                      {{ axis.code }} — {{ axis.name }}
                    </option>
                  </select>
                </div>

                <div
                  v-if="selectedReport?.id === 'R13'"
                  class="administrative-filter-field"
                >
                  <label for="administrativeEquipmentAvailability">
                    حالة توفر المعدات
                  </label>
                  <select
                    id="administrativeEquipmentAvailability"
                    @change="resetAdministrativeResult"
                    v-model="selectedEquipmentAvailability"
                  >
                    <option value="">الكل</option>
                    <option value="yes">متوفر</option>
                    <option value="no">غير متوفر</option>
                    <option value="na">لا ينطبق</option>
                  </select>
                </div>

                <div
                  v-if="selectedReport?.id === 'R14'"
                  class="administrative-filter-field"
                >
                  <label for="administrativeStaffAvailability">
                    حالة توفر الكادر
                  </label>
                  <select
                    id="administrativeStaffAvailability"
                    @change="resetAdministrativeResult"
                    v-model="selectedStaffAvailability"
                  >
                    <option value="">الكل</option>
                    <option
                      v-for="option in staffAvailabilityOptions"
                      :key="option.code"
                      :value="option.code"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>

                <div
                  v-if="selectedReport?.id === 'R15'"
                  class="administrative-filter-field"
                >
                  <label for="administrativeMedicineStock">مستوى المخزون</label>
                  <select
                    id="administrativeMedicineStock"
                    @change="resetAdministrativeResult"
                    v-model="selectedMedicineStockLevel"
                  >
                    <option value="">الكل</option>
                    <option value="sufficient">كافٍ</option>
                    <option value="insufficient">غير كافٍ</option>
                    <option value="out_of_stock">نافد من المخزون</option>
                  </select>
                </div>

                <div class="administrative-filter-action">
                  <button
                    type="button"
                    class="btn"
                    :disabled="loadingReport || !canLoadAdministrativeReport"
                    @click="loadAdministrativeReport"
                  >
                    {{ loadingReport ? "جارٍ التحميل..." : "عرض التقرير" }}
                  </button>
                </div>
              </div>
            </div>

            <!-- فلاتر التقارير R04 - R10 -->
            <div
              v-if="
                selectedReport?.requiresGovernorate &&
                !usesAdministrativeLevelFilter
              "
              class="no-print report-filter-box"
            >
              <label for="governorateSelect">{{ tr("ui.text_001") }}</label>
              <select
                id="governorateSelect"
                v-model="selectedGovernorateId"
                :disabled="loadingFilters"
                @change="handleGovernorateChange"
              >
                <option value="" disabled>{{ tr("ui.text_002") }}</option>
                <option
                  v-for="governorate in governorates"
                  :key="governorate.id || governorate.code"
                  :value="
                    governorate.id ||
                    governorate.governorate_id ||
                    governorate.code
                  "
                >
                  {{
                    governorate.name_ar ||
                    governorate.governorate_name_ar ||
                    governorate.name ||
                    governorate.code
                  }}
                </option>
              </select>
            </div>

            <div
              v-if="
                selectedReport?.requiresDistrict &&
                !usesAdministrativeLevelFilter
              "
              class="no-print report-filter-box"
            >
              <label for="districtSelect">{{ tr("ui.text_003") }}</label>
              <select
                id="districtSelect"
                v-model="selectedDistrictCode"
                :disabled="loadingFilters"
                @change="loadSelectedReport"
              >
                <option value="" disabled>{{ tr("ui.text_004") }}</option>
                <option
                  v-for="district in filteredDistricts"
                  :key="district.code || district.district_code || district.id"
                  :value="
                    district.code || district.district_code || district.id
                  "
                >
                  {{
                    district.name_ar ||
                    district.district_name_ar ||
                    district.name ||
                    district.code
                  }}
                </option>
              </select>
            </div>
            <div
              v-if="selectedReport?.requiresScenarioLevel"
              class="no-print report-filter-box report-filter-column"
            >
              <label for="scenarioLevel">{{ tr("ui.text_005") }}</label>
              <select
                id="scenarioLevel"
                v-model="scenarioLevel"
                @change="resetScenarioSelection"
              >
                <option value="facility">{{ tr("ui.text_006") }}</option>
                <option value="district">{{ tr("ui.text_007") }}</option>
              </select>

              <select
                v-if="scenarioLevel === 'facility'"
                v-model="selectedScenarioFacilityId"
                :disabled="loadingFacilities"
                @change="loadSelectedReport"
              >
                <option value="" disabled>{{ tr("ui.text_008") }}</option>
                <option
                  v-for="facility in facilities"
                  :key="facility.id"
                  :value="facility.id"
                >
                  {{ facility.name }}
                </option>
              </select>

              <select
                v-else
                v-model="selectedScenarioDistrictCode"
                :disabled="loadingFilters"
                @change="loadSelectedReport"
              >
                <option value="" disabled>{{ tr("ui.text_004") }}</option>
                <option
                  v-for="district in filteredDistricts"
                  :key="district.code || district.district_code || district.id"
                  :value="
                    district.code || district.district_code || district.id
                  "
                >
                  {{
                    district.name_ar ||
                    district.district_name_ar ||
                    district.name
                  }}
                </option>
              </select>
            </div>

            <!-- R01 + R04: نفس تصميم الموجز الوطني مع فلترة المحافظة في R04 -->
            <div v-if="['R01', 'R04'].includes(selectedReport?.id)">
              <div v-if="loadingReport" class="loading-box">
                {{ tr("ui.text_009") }}
              </div>

              <div v-else-if="reportData">
                <h3>{{ tr("ui.text_010") }}</h3>
                <p>{{ textValue(reportData.executive_summary) }}</p>

                <h3>{{ tr("ui.text_011") }}</h3>
                <table>
                  <tbody>
                    <tr>
                      <td>{{ tr("ui.text_012") }}</td>
                      <td>
                        {{ formatNumber(reportData.summary.total_facilities) }}
                      </td>
                    </tr>
                    <tr>
                      <td>{{ tr("ui.text_013") }}</td>
                      <td>
                        {{
                          formatNumber(reportData.summary.assessed_facilities)
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td>{{ tr("ui.text_014") }}</td>
                      <td>
                        {{
                          formatPercent(reportData.summary.assessment_coverage)
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td>{{ tr("ui.text_015") }}</td>
                      <td>
                        {{ formatScore(reportData.summary.national_fis) }}
                      </td>
                    </tr>
                    <tr>
                      <td>{{ tr("ui.text_016") }}</td>
                      <td>
                        {{
                          formatNumber(reportData.summary.critical_facilities)
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td>{{ tr("ui.text_017") }}</td>
                      <td>
                        {{ formatNumber(reportData.summary.unsafe_buildings) }}
                      </td>
                    </tr>
                    <tr>
                      <td>{{ tr("ui.text_018") }}</td>
                      <td>
                        {{ formatNumber(reportData.summary.high_service_risk) }}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <h3>{{ tr("ui.text_019") }}</h3>
                <table>
                  <tbody>
                    <tr>
                      <td>P1</td>
                      <td>
                        {{ formatNumber(reportData.priority_distribution.P1) }}
                      </td>
                    </tr>
                    <tr>
                      <td>P2</td>
                      <td>
                        {{ formatNumber(reportData.priority_distribution.P2) }}
                      </td>
                    </tr>
                    <tr>
                      <td>P3</td>
                      <td>
                        {{ formatNumber(reportData.priority_distribution.P3) }}
                      </td>
                    </tr>
                    <tr>
                      <td>P4</td>
                      <td>
                        {{ formatNumber(reportData.priority_distribution.P4) }}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <h3>{{ tr("ui.text_020") }}</h3>
                <ul>
                  <li v-for="gap in reportData.critical_gaps" :key="gap.title">
                    {{ textValue(gap.title) }}
                    <span>— {{ formatPercent(gap.percentage) }}</span>
                  </li>
                </ul>

                <h3>{{ tr("ui.text_021") }}</h3>
                <table>
                  <thead>
                    <tr>
                      <td>{{ tr("ui.text_022") }}</td>
                      <td>{{ tr("ui.text_023") }}</td>
                      <td>{{ tr("ui.text_0244") }}</td>
                      <td>{{ tr("ui.text_024") }}</td>
                    </tr>
                  </thead>

                  <tbody>
                    <tr
                      v-for="facility in reportData.top_priority_facilities"
                      :key="facility.facility_name"
                    >
                      <td>{{ textValue(facility.facility_name) }}</td>
                      <td>{{ facilityTypeAr(facility.facility_type) }}</td>
                      <td>{{ formatScore(facility.fis) }}</td>
                      <td>{{ priorityLabel(facility.priority) }}</td>
                    </tr>
                  </tbody>
                </table>

                <h3>{{ tr("ui.text_025") }}</h3>
                <ul>
                  <li v-for="r in reportData.recommendations" :key="r">
                    {{ textValue(r) }}
                  </li>
                </ul>

                <h3>{{ tr("ui.text_026") }}</h3>
                <p>{{ textValue(reportData.methodology_note) }}</p>
              </div>
            </div>

            <!-- R02 -->
            <div v-else-if="selectedReport?.id === 'R02'">
              <div v-if="loadingReport" class="loading-box">
                {{ tr("ui.text_009") }}
              </div>

              <div v-else-if="reportData">
                <h3>{{ tr("ui.text_010") }}</h3>
                <p>{{ textValue(reportData.executive_summary) }}</p>

                <h3>{{ tr("ui.text_011") }}</h3>
                <table>
                  <tbody>
                    <tr>
                      <td>{{ tr("ui.text_012") }}</td>
                      <td>
                        {{ formatNumber(reportData.summary.total_facilities) }}
                      </td>
                    </tr>
                    <tr>
                      <td>{{ tr("ui.text_027") }}</td>
                      <td>{{ formatScore(reportData.summary.average_fis) }}</td>
                    </tr>
                    <tr>
                      <td>{{ tr("ui.text_028") }}</td>
                      <td>{{ formatScore(reportData.summary.highest_fis) }}</td>
                    </tr>
                    <tr>
                      <td>{{ tr("ui.text_029") }}</td>
                      <td>{{ formatScore(reportData.summary.lowest_fis) }}</td>
                    </tr>
                  </tbody>
                </table>

                <h3>{{ tr("ui.text_019") }}</h3>
                <table>
                  <tbody>
                    <tr>
                      <td>P1</td>
                      <td>
                        {{ formatNumber(reportData.priority_distribution.P1) }}
                      </td>
                    </tr>
                    <tr>
                      <td>P2</td>
                      <td>
                        {{ formatNumber(reportData.priority_distribution.P2) }}
                      </td>
                    </tr>
                    <tr>
                      <td>P3</td>
                      <td>
                        {{ formatNumber(reportData.priority_distribution.P3) }}
                      </td>
                    </tr>
                    <tr>
                      <td>P4</td>
                      <td>
                        {{ formatNumber(reportData.priority_distribution.P4) }}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <h3>{{ tr("ui.text_030") }}</h3>
                <table>
                  <thead>
                    <tr>
                      <td>#</td>
                      <td>{{ tr("ui.text_022") }}</td>
                      <td>{{ tr("ui.text_023") }}</td>
                      <td>{{ tr("ui.text_0244") }}</td>
                      <td>{{ tr("ui.text_024") }}</td>
                      <td>{{ tr("ui.text_031") }}</td>
                      <td>{{ tr("ui.text_032") }}</td>
                    </tr>
                  </thead>

                  <tbody>
                    <tr
                      v-for="facility in reportData.facilities"
                      :key="facility.facility_name"
                    >
                      <td>{{ formatNumber(facility.rank) }}</td>
                      <td>
                        {{
                          facility.facility_name_ar ||
                          facility.facility_name ||
                          facility.facility_name_en ||
                          unknownText()
                        }}
                      </td>
                      <td>
                        {{
                          facilityTypeAr(
                            facility.facility_type_ar ||
                              facility.facility_type ||
                              facility.facility_type_en
                          )
                        }}
                      </td>
                      <td>{{ formatScore(facility.fis) }}</td>
                      <td>{{ priorityLabel(facility.priority) }}</td>
                      <td>{{ textValue(facility.risk_level) }}</td>
                      <td>{{ textValue(facility.timeframe) }}</td>
                    </tr>
                  </tbody>
                </table>

                <h3>{{ tr("ui.text_033") }}</h3>
                <table>
                  <tbody>
                    <tr
                      v-for="risk in reportData.risk_analysis"
                      :key="risk.priority"
                    >
                      <td>
                        {{ textValue(risk.priority) }} -
                        {{ textValue(risk.label) }}
                      </td>
                      <td>{{ textValue(risk.description) }}</td>
                      <td>{{ textValue(risk.timeframe) }}</td>
                    </tr>
                  </tbody>
                </table>

                <h3>{{ tr("ui.text_026") }}</h3>
                <p>{{ textValue(reportData.methodology_note) }}</p>
              </div>
            </div>

            <div
              v-else-if="selectedReport?.id === 'R03'"
              class="facility-profile-report"
            >
              <!-- اختيار المرفق -->
              <div class="no-print facility-selector">
                <label for="facilitySelect">{{ tr("ui.text_034") }}</label>

                <select
                  id="facilitySelect"
                  v-model="selectedFacilityId"
                  class="facility-select"
                  :disabled="loadingFacilities || facilities.length === 0"
                  @change="loadFacilityProfile"
                >
                  <option value="" disabled>
                    {{
                      loadingFacilities
                        ? tr("common.loadingFacilities")
                        : facilities.length === 0
                        ? tr("common.noFacilitiesAvailable")
                        : tr("common.selectHealthFacility")
                    }}
                  </option>

                  <option
                    v-for="facility in facilities"
                    :key="facility.id"
                    :value="facility.id"
                  >
                    {{ facility.name }}
                  </option>
                </select>
              </div>

              <div v-if="loadingReport" class="loading-box">
                {{ tr("ui.text_035") }}
              </div>

              <div v-else-if="reportError" class="error-box">
                {{ reportError }}
              </div>

              <div v-else-if="reportData">
                <!-- رأس التقرير -->
                <div class="facility-report-header">
                  <h1>
                    {{
                      tr("mixed.facilityProfileTitle", {
                        name: facilityInfo.name,
                      })
                    }}
                  </h1>

                  <p class="facility-subtitle">
                    {{ facilityTypeAr(facilityInfo.type) }}
                    <span v-if="facilityInfo.district">
                      {{
                        tr("mixed.districtName", {
                          name: facilityInfo.district,
                        })
                      }}
                    </span>
                    <span v-if="facilityInfo.governorate">
                      — {{ facilityInfo.governorate }}
                    </span>
                  </p>

                  <p>
                    {{ tr("ui.text_036") }}
                  </p>

                  <p class="report-source">
                    {{ tr("ui.text_037") }}
                    <span v-if="facilityInfo.district">
                      {{
                        tr("mixed.districtDash", {
                          name: facilityInfo.district,
                        })
                      }}
                    </span>
                    <span v-if="facilityInfo.governorate">
                      {{
                        tr("mixed.governorateName", {
                          name: facilityInfo.governorate,
                        })
                      }}
                    </span>
                    {{ tr("mixed.creationDate", { date: reportDate }) }}
                  </p>
                </div>

                <!-- 1) البطاقة التعريفية -->
                <h3>{{ tr("ui.text_038") }}</h3>

                <div class="identity-grid">
                  <div class="identity-card">
                    <span class="identity-label">{{ tr("ui.text_039") }}</span>
                    <strong>{{ facilityTypeAr(facilityInfo.type) }}</strong>
                  </div>

                  <div class="identity-card">
                    <span class="identity-label">{{ tr("ui.text_040") }}</span>
                    <strong>{{
                      operationalStatusAr(facilityInfo.operational_status)
                    }}</strong>
                  </div>

                  <div class="identity-card">
                    <span class="identity-label">{{ tr("ui.text_041") }}</span>
                    <strong>{{
                      formatNumber(facilityInfo.total_staff)
                    }}</strong>

                    <small>
                      {{
                        tr("mixed.femaleRatio", {
                          value: formatPercent(facilityInfo.female_staff_ratio),
                        })
                      }}
                    </small>
                  </div>

                  <div class="identity-card">
                    <span class="identity-label">{{ tr("ui.text_042") }}</span>
                    <strong>{{
                      formatNumber(facilityInfo.beneficiaries_month)
                    }}</strong>
                  </div>

                  <div class="identity-card">
                    <span class="identity-label">{{ tr("ui.text_043") }}</span>
                    <strong>{{
                      tr("mixed.population", {
                        value: formatNumber(facilityInfo.target_population),
                      })
                    }}</strong>
                  </div>

                  <div class="identity-card">
                    <span class="identity-label">{{ tr("ui.text_044") }}</span>
                    <strong>
                      {{ textValue(facilityCoordinates) }}
                    </strong>
                  </div>

                  <div class="identity-card fis-card">
                    <span class="identity-label">{{ tr("ui.text_045") }}</span>
                    <strong>{{ formatScore(profileSummary.fis) }}</strong>

                    <small>
                      {{
                        tr("mixed.rank", {
                          value: formatNumber(profileSummary.rank),
                        })
                      }}
                      <span>
                        / {{ formatNumber(profileSummary.facilities_count) }}
                      </span>
                    </small>
                  </div>

                  <div class="identity-card priority-card">
                    <span class="identity-label">{{ tr("ui.text_024") }}</span>

                    <strong>
                      {{ priorityLabel(profileSummary.priority) }}
                    </strong>

                    <small>
                      {{ textValue(profileSummary.risk_level) }}
                      <span v-if="profileSummary.timeframe">
                        — {{ profileSummary.timeframe }}
                      </span>
                    </small>
                  </div>
                </div>
                <!-- 3) جدول جميع المؤشرات -->
                <h3>{{ tr("ui.text_046") }}</h3>

                <div class="table-scroll">
                  <table class="indicators-table">
                    <thead>
                      <tr>
                        <th>{{ tr("ui.text_047") }}</th>
                        <th>{{ tr("ui.text_048") }}</th>
                        <th>{{ tr("ui.text_049") }}</th>
                        <th>{{ tr("ui.text_051") }}</th>
                        <th>{{ tr("ui.text_052") }}</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr
                        v-for="indicator in facilityIndicators"
                        :key="indicator.code"
                      >
                        <td class="indicator-code">
                          {{ textValue(indicator.code) }}
                        </td>

                        <td>
                          {{ axisLabel(indicator.axis || indicator.code) }}
                        </td>

                        <td>
                          {{ textValue(indicator.name || indicator.title) }}
                        </td>

                        <td class="indicator-value">
                          {{ displayIndicatorValue(indicator) }}
                        </td>

                        <td>
                          <span class="rag-badge" :class="ragClass(indicator)">
                            {{ ragLabel(indicator) }}
                          </span>
                        </td>
                      </tr>

                      <tr v-if="!facilityIndicators.length">
                        <td colspan="6" class="empty-table">
                          {{ tr("ui.text_053") }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- 4) ملخص المحاور -->
                <h3>{{ tr("ui.text_054") }}</h3>

                <div class="axis-summary-grid">
                  <article
                    v-for="axis in axisSummaries"
                    :key="axis.code"
                    class="axis-summary-card"
                  >
                    <div class="axis-summary-head">
                      <div>
                        <span class="axis-code">
                          {{ textValue(axis.code) }}
                        </span>
                        <h4>{{ textValue(axis.name) }}</h4>
                      </div>

                      <div
                        class="axis-score"
                        :class="scoreColorClass(axis.score)"
                      >
                        {{ formatScore(axis.score) }}
                      </div>
                    </div>

                    <div class="axis-progress">
                      <span
                        :style="{ width: `${normalizePercent(axis.score)}%` }"
                      ></span>
                    </div>

                    <div v-if="axis.indicators.length" class="axis-indicators">
                      <div
                        v-for="indicator in axis.indicators"
                        :key="indicator.code"
                        class="axis-indicator-row"
                      >
                        <span>
                          {{ textValue(indicator.code) }} —
                          {{ textValue(indicator.name || indicator.title) }}
                        </span>

                        <strong>
                          {{ displayIndicatorValue(indicator) }}
                        </strong>
                      </div>
                    </div>

                    <div v-if="axis.highlights.length" class="axis-highlights">
                      <h5>{{ tr("ui.text_055") }}</h5>

                      <ul>
                        <li
                          v-for="(item, index) in axis.highlights"
                          :key="index"
                        >
                          {{ item }}
                        </li>
                      </ul>
                    </div>

                    <div
                      v-if="axis.recommendations.length"
                      class="axis-recommendations"
                    >
                      <h5>{{ tr("ui.text_056") }}</h5>

                      <ul>
                        <li
                          v-for="(item, index) in axis.recommendations"
                          :key="index"
                        >
                          {{ item }}
                        </li>
                      </ul>
                    </div>
                  </article>
                </div>

                <!-- 5) حزمة التدخل -->
                <h3>{{ tr("ui.text_057") }}</h3>

                <div class="intervention-grid">
                  <article
                    v-for="(group, index) in interventionGroups"
                    :key="`${group.period}-${index}`"
                    class="intervention-period-card"
                  >
                    <div class="intervention-period-title">
                      {{ textValue(group.period) }}
                    </div>

                    <ul>
                      <li
                        v-for="(item, itemIndex) in group.items"
                        :key="item.id || item.title || itemIndex"
                      >
                        <strong>
                          {{ textValue(item.title || item.name) }}
                        </strong>

                        <p v-if="item.description">
                          {{ item.description }}
                        </p>

                        <div class="intervention-meta">
                          <span v-if="item.priority">
                            {{ tr("mixed.priority", { value: item.priority }) }}
                          </span>

                          <span>
                            {{
                              tr("mixed.cost", {
                                value: formatCostCurrency(item.estimated_cost),
                              })
                            }}
                          </span>
                        </div>
                      </li>
                    </ul>
                  </article>

                  <div v-if="!interventionGroups.length" class="empty-section">
                    {{ tr("ui.text_058") }}
                  </div>
                </div>

                <!-- 6) ملخص التكلفة -->
                <h3>{{ tr("ui.text_059") }}</h3>

                <div class="cost-summary-grid">
                  <article
                    v-for="category in costCategories"
                    :key="category.code || category.name"
                    class="cost-summary-card"
                  >
                    <span>{{ textValue(category.name) }}</span>

                    <strong>
                      {{ formatCostCurrency(category.total) }}
                    </strong>
                  </article>

                  <article class="cost-summary-card total-cost-card">
                    <span>{{ tr("ui.text_060") }}</span>
                    <strong>{{
                      formatCostCurrency(totalEstimatedCost)
                    }}</strong>
                  </article>
                </div>

                <!-- 7) تفاصيل التكاليف -->
                <h3>{{ tr("ui.text_061") }}</h3>

                <div
                  v-for="category in detailedCostCategories"
                  :key="category.code || category.name"
                  class="cost-category-section"
                >
                  <div
                    class="cost-category-title no-print"
                    @click="toggleCostCategory(category.code || category.name)"
                  >
                    <div>
                      <strong>{{ textValue(category.name) }}</strong>
                      <span>{{ formatCostCurrency(category.total) }}</span>
                    </div>

                    <button type="button">
                      {{
                        expandedCostCategories.includes(
                          category.code || category.name
                        )
                          ? tr("common.hideDetails")
                          : tr("common.showDetails")
                      }}
                    </button>
                  </div>

                  <div
                    v-show="
                      expandedCostCategories.includes(
                        category.code || category.name
                      )
                    "
                    class="table-scroll cost-details-table"
                  >
                    <table>
                      <thead>
                        <tr>
                          <th>{{ tr("ui.text_062") }}</th>
                          <th>{{ tr("ui.text_063") }}</th>
                          <th>{{ tr("ui.text_064") }}</th>
                          <th>{{ tr("ui.text_065") }}</th>
                          <th>{{ tr("ui.text_066") }}</th>
                          <th>{{ tr("ui.text_067") }}</th>
                          <th>{{ tr("ui.text_068") }}</th>
                          <th>{{ tr("ui.text_069") }}</th>
                        </tr>
                      </thead>

                      <tbody>
                        <tr
                          v-for="(item, index) in category.items"
                          :key="item.id || index"
                        >
                          <td>{{ textValue(item.name || item.title) }}</td>
                          <td>{{ formatNumber(item.quantity) }}</td>
                          <td>{{ textValue(item.unit) }}</td>
                          <td>{{ formatMoneyNumber(item.unit_price) }}</td>
                          <td>{{ formatMoneyNumber(item.immediate) }}</td>
                          <td>{{ formatMoneyNumber(item.short_term) }}</td>
                          <td>{{ formatMoneyNumber(item.medium_term) }}</td>
                          <td>
                            <strong>
                              {{ formatMoneyNumber(item.total) }}
                            </strong>
                          </td>
                        </tr>

                        <tr class="subtotal-row">
                          <td colspan="4">{{ tr("ui.text_070") }}</td>
                          <td>{{ formatMoneyNumber(category.immediate) }}</td>
                          <td>{{ formatMoneyNumber(category.short_term) }}</td>
                          <td>{{ formatMoneyNumber(category.medium_term) }}</td>
                          <td>{{ formatMoneyNumber(category.total) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <p v-if="costMethodologyNote" class="methodology-note">
                  {{ costMethodologyNote }}
                </p>

                <!-- 8) السيناريوهات -->
                <h3>{{ tr("ui.text_071") }}</h3>

                <div class="scenarios-list">
                  <article
                    v-for="(scenario, index) in facilityScenarios"
                    :key="scenario.code || index"
                    class="scenario-card"
                  >
                    <div class="scenario-code">
                      {{ scenario.code || `S${index + 1}` }}
                    </div>

                    <div>
                      <h4>{{ textValue(scenario.title || scenario.name) }}</h4>

                      <p v-if="scenario.description">
                        {{ scenario.description }}
                      </p>

                      <div v-if="scenario.action" class="scenario-action">
                        <strong>{{ tr("ui.text_072") }}</strong>
                        {{ scenario.action }}
                      </div>
                    </div>
                  </article>

                  <div v-if="!facilityScenarios.length" class="empty-section">
                    {{ tr("ui.text_073") }}
                  </div>
                </div>

                <!-- المنهجية -->
                <h3>{{ tr("ui.text_074") }}</h3>

                <p class="methodology-note">
                  {{
                    reportData.methodology_note ||
                    tr("common.facilityMethodology")
                  }}
                </p>
              </div>

              <div v-else class="empty-section">
                {{ tr("ui.text_075") }}
              </div>
            </div>
            <!-- R06: تقرير المحور المتخصص من API -->
            <div v-else-if="selectedReport?.id === 'R06'">
              <div
                v-if="!usesAdministrativeLevelFilter"
                class="no-print report-filter-box"
              >
                <label for="axisSelect">{{ tr("ui.text_076") }}</label>

                <select id="axisSelect" v-model="selectedAxis">
                  <option
                    v-for="axis in axesOptions"
                    :key="axis.code"
                    :value="axis.code"
                  >
                    {{ axis.code }} — {{ axis.name }}
                  </option>
                </select>

                <button
                  class="btn"
                  type="button"
                  :disabled="loadingReport || !selectedAxis"
                  @click="loadSelectedReport"
                >
                  {{
                    loadingReport
                      ? tr("common.loading")
                      : tr("common.viewReport")
                  }}
                </button>
              </div>

              <div v-if="loadingReport" class="loading-box">
                {{ tr("ui.text_077") }}
              </div>

              <div v-else-if="reportError" class="error-box">
                {{ reportError }}
              </div>

              <div v-else-if="reportData">
                <h3>{{ tr("ui.text_078") }}</h3>
                <p>
                  {{
                    reportData.executive_summary ||
                    tr("common.noExecutiveSummary")
                  }}
                </p>

                <h3>{{ tr("ui.text_079") }}</h3>
                <div class="axis-overview-card">
                  <div>
                    <span>{{ tr("ui.text_048") }}</span>
                    <strong>{{ reportData.axis || selectedAxis }}</strong>
                  </div>

                  <div>
                    <span>{{ tr("ui.text_080") }}</span>
                    <strong>
                      {{
                        reportData.axis_name ||
                        axisLabel(reportData.axis || selectedAxis)
                      }}
                    </strong>
                  </div>

                  <div>
                    <span>{{ tr("ui.text_081") }}</span>
                    <strong>{{ formatScore(reportData.score) }}</strong>
                  </div>

                  <div>
                    <span>{{ tr("ui.text_082") }}</span>
                    <strong>{{ specializedAxisResultsCount }}</strong>
                  </div>

                  <div>
                    <span>{{ tr("ui.text_083") }}</span>
                    <strong>{{ specializedAxisFacilitiesCount }}</strong>
                  </div>
                </div>

                <h3>{{ tr("ui.text_084") }}</h3>
                <div class="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>{{ tr("ui.text_047") }}</th>
                        <th>{{ tr("ui.text_049") }}</th>
                        <th>{{ tr("ui.text_085") }}</th>
                        <th>{{ tr("ui.text_050") }}</th>
                        <th>{{ tr("ui.text_083") }}</th>
                        <th>{{ tr("ui.text_052") }}</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr
                        v-for="indicator in specializedIndicatorSummary"
                        :key="indicator.code"
                      >
                        <td class="indicator-code">
                          {{ textValue(indicator.code) }}
                        </td>
                        <td>
                          {{
                            textValue(
                              indicator.name || indicator.indicator_name
                            )
                          }}
                        </td>
                        <td>
                          {{ formatScore(indicator.average) }}
                          <span v-if="indicator.unit">{{
                            indicator.unit
                          }}</span>
                        </td>
                        <td>
                          {{ formatReportValue(indicator.target, "number") }}
                        </td>
                        <td>{{ formatNumber(indicator.facilities_count) }}</td>
                        <td>
                          <span
                            class="rag-badge"
                            :class="apiRagClass(indicator)"
                          >
                            {{ apiStatusLabel(indicator) }}
                          </span>
                        </td>
                      </tr>

                      <tr v-if="!specializedIndicatorSummary.length">
                        <td colspan="6" class="empty-table">
                          {{ tr("ui.text_086") }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3>{{ tr("ui.text_087") }}</h3>
                <div class="table-scroll">
                  <table class="specialized-axis-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{{ tr("ui.text_022") }}</th>
                        <th>{{ tr("ui.text_088") }}</th>
                        <th>{{ tr("ui.text_049") }}</th>
                        <th>{{ tr("ui.text_051") }}</th>
                        <th>{{ tr("ui.text_050") }}</th>
                        <th>{{ tr("ui.text_089") }}</th>
                        <th>{{ tr("ui.text_052") }}</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr
                        v-for="(item, index) in specializedAxisRows"
                        :key="`${item.facility_id || index}-${
                          item.code || item.indicator_code
                        }`"
                      >
                        <td>{{ index + 1 }}</td>
                        <td>
                          {{
                            textValue(
                              item.facility_name_ar || item.facility_name
                            )
                          }}
                        </td>
                        <td>
                          {{
                            textValue(
                              item.district_name_ar || item.district_name
                            )
                          }}
                        </td>
                        <td>
                          <strong>{{
                            textValue(item.code || item.indicator_code)
                          }}</strong>
                          — {{ textValue(item.indicator_name || item.name) }}
                        </td>
                        <td>
                          {{
                            formatReportValue(
                              item.indicator_value ?? item.value,
                              "number"
                            )
                          }}
                          <span v-if="item.unit">{{ item.unit }}</span>
                        </td>
                        <td>
                          {{
                            formatReportValue(
                              item.target_value ?? item.target,
                              "number"
                            )
                          }}
                        </td>
                        <td>{{ textValue(item.classification) }}</td>
                        <td>
                          <span class="rag-badge" :class="apiRagClass(item)">
                            {{ apiStatusLabel(item) }}
                          </span>
                        </td>
                      </tr>

                      <tr v-if="!specializedAxisRows.length">
                        <td colspan="8" class="empty-table">
                          {{ tr("ui.text_090") }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3>{{ tr("ui.text_091") }}</h3>
                <div class="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{{ tr("ui.text_022") }}</th>
                        <th>{{ tr("ui.text_088") }}</th>
                        <th>{{ tr("ui.text_092") }}</th>
                        <th>{{ tr("ui.text_093") }}</th>
                        <th>{{ tr("ui.text_052") }}</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr
                        v-for="(facility, index) in weakestAxisFacilities"
                        :key="facility.facility_id || index"
                      >
                        <td>{{ index + 1 }}</td>
                        <td>
                          {{
                            textValue(
                              facility.facility_name_ar ||
                                facility.facility_name
                            )
                          }}
                        </td>
                        <td>
                          {{
                            textValue(
                              facility.district_name_ar ||
                                facility.district_name
                            )
                          }}
                        </td>
                        <td>{{ formatScore(facility.score) }}</td>
                        <td>{{ formatNumber(facility.indicators_count) }}</td>
                        <td>
                          <span
                            class="rag-badge"
                            :class="apiRagClass(facility)"
                          >
                            {{ apiStatusLabel(facility) }}
                          </span>
                        </td>
                      </tr>

                      <tr v-if="!weakestAxisFacilities.length">
                        <td colspan="6" class="empty-table">
                          {{ tr("ui.text_094") }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3>{{ tr("ui.text_025") }}</h3>
                <ul v-if="specializedRecommendations.length">
                  <li
                    v-for="(
                      recommendation, index
                    ) in specializedRecommendations"
                    :key="`${recommendation}-${index}`"
                  >
                    {{ recommendation }}
                  </li>
                </ul>
                <div v-else class="empty-section">{{ tr("ui.text_095") }}</div>

                <h3>{{ tr("ui.text_026") }}</h3>
                <p class="methodology-note">
                  {{
                    reportData.methodology_note ||
                    tr("common.noMethodologyNotes")
                  }}
                </p>
              </div>

              <div v-else class="empty-section">
                {{ tr("ui.text_096") }}
              </div>
            </div>
            <!-- R13 / R14 / R15: API-provided totals or facility records -->
            <div
              v-else-if="isInventoryReport"
              class="inventory-report"
              :class="{
                'medical-equipment-report': selectedReport?.id === 'R13',
                'resource-report': selectedReport?.id !== 'R13',
              }"
            >
              <div v-if="loadingReport" class="loading-box">
                {{ tr("common.loading") }}
              </div>
              <div v-else-if="reportError" class="error-box">
                {{ reportError }}
              </div>
              <div v-else-if="reportData">
                <div class="equipment-facility-header inventory-scope-header">
                  <h2>
                    {{ inventoryReportTitle }} — {{ inventoryScopeLabel }}
                  </h2>
                  <p
                    v-if="
                      !inventoryIsAggregated && inventoryFacilityDescription
                    "
                  >
                    {{ inventoryFacilityDescription }}
                  </p>
                  <p class="inventory-mode">
                    <span>{{
                      inventoryText(
                        inventoryIsAggregated ? "totals" : "details"
                      )
                    }}</span>
                    <span>
                      {{ inventoryText("scopeFacilities") }}:
                      {{ inventoryNumber(inventoryScope.facilities_count) }}
                    </span>
                  </p>
                </div>

                <h3>
                  {{
                    inventoryText(
                      selectedReport?.id === "R13"
                        ? "recordCounters"
                        : "summary"
                    )
                  }}
                </h3>
                <div class="equipment-summary-grid inventory-summary-grid">
                  <article
                    v-for="card in inventorySummaryCards"
                    :key="card.key"
                    class="equipment-summary-card"
                    :class="card.tone"
                  >
                    <span>{{ card.label }}</span>
                    <strong>{{ inventoryNumber(card.value) }}</strong>
                    <small v-if="'percentage' in card">
                      {{ inventoryNumber(card.percentage) }}%
                    </small>
                    <small v-if="card.note">{{ card.note }}</small>
                  </article>
                </div>
                <p class="inventory-summary-note">
                  {{ inventoryText("summaryNote") }}
                </p>

                <div
                  v-if="selectedReport?.id === 'R15'"
                  class="key-findings-box"
                >
                  <p>
                    <strong>{{ tr("ui.text_145") }}</strong>
                    {{ inventoryLocalized(medicineStatistics.general_status) }}
                  </p>
                </div>

                <section
                  v-if="selectedReport?.id === 'R15'"
                  class="medicine-facility-summary"
                >
                  <h3>{{ inventoryText("medicineFacilitySummary") }}</h3>
                  <p class="inventory-summary-note">
                    {{ inventoryText("medicineFacilitySummaryNote") }}
                  </p>

                  <div class="table-scroll inventory-table-scroll">
                    <table
                      class="inventory-table medicine-availability-table"
                      :aria-label="inventoryText('medicineFacilitySummary')"
                    >
                      <thead>
                        <tr>
                          <th scope="col" class="inventory-index">#</th>
                          <th scope="col">{{ inventoryText("facility") }}</th>
                          <th scope="col">
                            {{ inventoryText("availabilityStatus") }}
                          </th>
                          <th scope="col">{{ inventoryText("reasons") }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(facility, index) in medicineFacilities"
                          :key="
                            facility.survey_id ||
                            facility.facility_id ||
                            facility.summary_record_id ||
                            index
                          "
                        >
                          <td class="inventory-index" data-label="#">
                            {{ index + 1 }}
                          </td>
                          <td :data-label="inventoryText('facility')">
                            <strong>{{
                              inventoryName(
                                facility,
                                "facility_name_ar",
                                "facility_name_en"
                              ) ||
                              facility.facility_name ||
                              inventoryText("unknown")
                            }}</strong>
                          </td>
                          <td :data-label="inventoryText('availabilityStatus')">
                            <span
                              class="inventory-status-badge"
                              :class="`inventory-status-${medicineFacilityStatusClass(
                                facility
                              )}`"
                            >
                              {{ medicineFacilityAvailabilityLabel(facility) }}
                            </span>
                          </td>
                          <td :data-label="inventoryText('reasons')">
                            <template
                              v-if="medicineFacilityNeedsReason(facility)"
                            >
                              {{ medicineFacilityReasons(facility) }}
                            </template>
                            <span v-else>{{ inventoryText("na") }}</span>
                          </td>
                        </tr>
                        <tr v-if="!medicineFacilities.length">
                          <td colspan="4" class="empty-cell">
                            {{ inventoryText("emptyFacilityAvailability") }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </section>

                <h3>
                  {{
                    inventoryText(inventoryIsAggregated ? "totals" : "details")
                  }}
                </h3>
                <p class="inventory-summary-note">
                  {{
                    inventoryText(
                      inventoryIsAggregated ? "totalsNote" : "detailsNote"
                    )
                  }}
                </p>
                <div class="table-scroll inventory-table-scroll">
                  <table
                    class="inventory-table"
                    :class="{
                      'inventory-detail-table':
                        !inventoryIsAggregated && selectedReport?.id === 'R13',
                    }"
                    :aria-label="inventoryReportTitle"
                  >
                    <thead>
                      <tr>
                        <th scope="col" class="inventory-index">#</th>
                        <th
                          v-for="column in inventoryTableColumns"
                          :key="column.key"
                          scope="col"
                        >
                          {{ column.label }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(row, index) in inventoryRows"
                        :key="inventoryRowKey(row, index)"
                      >
                        <td class="inventory-index" data-label="#">
                          {{ index + 1 }}
                        </td>
                        <td
                          v-for="column in inventoryTableColumns"
                          :key="column.key"
                          :data-column="column.key"
                          :data-label="column.label"
                          :class="{
                            'inventory-numeric': [
                              'number',
                              'quantity',
                            ].includes(column.type),
                          }"
                        >
                          <template v-if="column.type === 'quantity'">
                            <strong>{{
                              inventoryNumber(row[column.key])
                            }}</strong>
                            <small
                              v-if="inventoryQuantityNote(row, column.key)"
                              class="inventory-cell-note"
                              >{{
                                inventoryQuantityNote(row, column.key)
                              }}</small
                            >
                          </template>
                          <template v-else-if="column.type === 'number'">
                            <strong>{{
                              inventoryNumber(row[column.key])
                            }}</strong>
                            <small
                              v-if="
                                column.key.endsWith('_quantity') &&
                                inventoryQuantityNote(row, column.key)
                              "
                              class="inventory-cell-note"
                              >{{
                                inventoryQuantityNote(row, column.key)
                              }}</small
                            >
                            <small
                              v-if="column.countKey"
                              class="inventory-cell-note"
                            >
                              {{ inventoryText("facilityCount") }}:
                              {{ inventoryNumber(row[column.countKey]) }}
                            </small>
                          </template>
                          <template v-else-if="column.type === 'equipment'">
                            <strong>
                              {{ textValue(row.equipment_name_ar) }}
                            </strong>
                            <small v-if="row.umdns" class="inventory-cell-note"
                              >UMDNS: {{ row.umdns }}</small
                            >
                            <small
                              v-if="!inventoryIsAggregated && row.model"
                              class="inventory-cell-note"
                              >{{ inventoryText("model") }}:
                              {{ row.model }}</small
                            >
                            <small
                              v-if="!inventoryIsAggregated && row.status_code"
                              class="inventory-cell-note"
                              >{{ inventoryText(row.status_code) }}</small
                            >
                          </template>
                          <template v-else-if="column.type === 'medicine'">
                            <strong>{{
                              textValue(row.supply_name_ar || row.supply_code)
                            }}</strong>
                            <small
                              v-if="row.supply_code"
                              class="inventory-cell-note"
                              >{{ row.supply_code }}</small
                            >
                          </template>
                          <span
                            v-else-if="column.type === 'status'"
                            class="inventory-status-badge"
                            :class="`inventory-status-${inventoryStatusCode(
                              row,
                              column
                            )}`"
                          >
                            {{ inventoryStatusLabel(row, column) }}
                          </span>
                          <bdi v-else-if="column.type === 'installation-date'">
                            {{ textValue(row.date_installation) }}
                          </bdi>
                          <span v-else-if="column.type === 'equipment-age'">
                            {{ inventoryEquipmentAgeLabel(row) }}
                          </span>
                          <template v-else>{{
                            inventoryLocalized(row[column.key])
                          }}</template>
                        </td>
                      </tr>
                      <tr v-if="!inventoryRows.length">
                        <td
                          :colspan="inventoryTableColumns.length + 1"
                          class="empty-table"
                        >
                          {{ inventoryText("empty") }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <h3>{{ inventoryText("methodology") }}</h3>
                <p class="methodology-note">{{ inventoryMethodology }}</p>
              </div>
              <div v-else class="empty-section">
                {{ tr("common.viewReport") }}
              </div>
            </div>
            <!-- R04 - R12 -->
            <div
              v-else-if="genericReportIds.includes(selectedReport?.id)"
              class="generic-api-report"
            >
              <div v-if="loadingReport" class="loading-box">
                {{ tr("ui.text_009") }}
              </div>

              <div v-else-if="reportError" class="error-box">
                {{ reportError }}
              </div>

              <div v-else-if="reportData">
                <h3>{{ tr("ui.text_010") }}</h3>
                <p>
                  {{
                    selectedReport?.id === "R07"
                      ? needsCostsExecutiveSummary
                      : reportData.executive_summary ||
                        reportData.summary_text ||
                        selectedReport?.summary ||
                        tr("common.noExecutiveSummary")
                  }}
                </p>

                <h3 v-if="genericSummaryRows.length">
                  {{ tr("ui.text_152") }}
                </h3>
                <table v-if="genericSummaryRows.length">
                  <tbody>
                    <tr v-for="row in genericSummaryRows" :key="row.key">
                      <td>{{ reportFieldLabel(row.key) }}</td>
                      <td>{{ formatSummaryValue(row.key, row.value) }}</td>
                    </tr>
                  </tbody>
                </table>

                <template v-if="genericPriorityRows.length">
                  <h3>{{ tr("ui.text_019") }}</h3>
                  <table>
                    <thead>
                      <tr>
                        <th>{{ tr("ui.text_024") }}</th>
                        <th>{{ tr("ui.text_153") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="row in genericPriorityRows"
                        :key="row.priority"
                      >
                        <td>{{ textValue(row.priority) }}</td>
                        <td>{{ formatNumber(row.value) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </template>

                <template v-if="genericReportAxes.length">
                  <h3>{{ tr("ui.text_154") }}</h3>
                  <div class="table-scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>{{ tr("ui.text_048") }}</th>
                          <th>{{ tr("ui.text_155") }}</th>
                          <th>{{ tr("ui.text_093") }}</th>
                          <th>{{ tr("ui.text_052") }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="axis in genericReportAxes" :key="axis.code">
                          <td>{{ axisLabel(axis.code) }}</td>
                          <td>{{ formatScore(axis.score) }}</td>
                          <td>{{ formatNumber(axis.indicators_count) }}</td>
                          <td>
                            <span
                              class="rag-badge"
                              :class="`rag-${statusFromScore(axis.score)}`"
                            >
                              {{ statusLabel(statusFromScore(axis.score)) }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </template>

                <!-- R07: الاحتياجات والتكاليف والتوزيع المرحلي -->
                <template
                  v-if="selectedReport?.id === 'R07' && r07Facilities.length"
                >
                  <h3>ملخص الميزانية حسب المرفق الصحي</h3>

                  <div class="r07-table-wrap">
                    <table class="r07-budget-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>المرفق الصحي</th>
                          <th>نوع الاحتياج</th>
                          <th>الميزانية الحالية</th>
                          <th>الميزانية المطلوبة</th>
                          <th>فجوة الميزانية</th>
                          <th>نسبة الفجوة</th>
                          <th>تكلفة التأهيل</th>
                          <th>إجمالي المطلوب</th>
                        </tr>
                      </thead>

                      <tbody>
                        <tr
                          v-for="(facility, index) in r07Facilities"
                          :key="
                            facility.survey_id || facility.facility_id || index
                          "
                        >
                          <td class="numeric-cell">{{ index + 1 }}</td>
                          <td>
                            <strong>
                              {{ textValue(facility.facility_name) }}
                            </strong>
                            <small
                              v-if="facility.facility_name_en"
                              class="r07-facility-name-en"
                            >
                              {{ facility.facility_name_en }}
                            </small>
                          </td>
                          <td>{{ formatR07Needs(facility) }}</td>
                          <td class="numeric-cell r07-money-cell">
                            <strong>
                              {{ formatR07Currency(facility.current_budget) }}
                            </strong>
                            <small>{{
                              formatR07Yer(facility.current_budget)
                            }}</small>
                          </td>
                          <td class="numeric-cell r07-money-cell">
                            <strong>
                              {{ formatR07Currency(facility.required_budget) }}
                            </strong>
                            <small>{{
                              formatR07Yer(facility.required_budget)
                            }}</small>
                          </td>
                          <td class="numeric-cell r07-money-cell">
                            <strong>
                              {{ formatR07Currency(facility.budget_gap) }}
                            </strong>
                            <small>{{
                              formatR07Yer(facility.budget_gap)
                            }}</small>
                          </td>
                          <td class="numeric-cell">
                            {{ formatNumber(facility.gap_rate) }}%
                          </td>
                          <td class="numeric-cell r07-money-cell">
                            <strong>
                              {{
                                formatR07Currency(facility.estimated_rehab_cost)
                              }}
                            </strong>
                            <small>
                              {{ formatR07Yer(facility.estimated_rehab_cost) }}
                            </small>
                          </td>
                          <td class="numeric-cell r07-money-cell">
                            <strong>
                              {{
                                formatR07Currency(facility.total_required_cost)
                              }}
                            </strong>
                            <small>
                              {{ formatR07Yer(facility.total_required_cost) }}
                            </small>
                          </td>
                        </tr>

                        <tr class="subtotal-row">
                          <td colspan="3"><strong>الإجمالي</strong></td>
                          <td class="numeric-cell r07-money-cell">
                            <strong>
                              {{
                                formatR07Currency(
                                  r07FacilityTotals.current_budget
                                )
                              }}
                            </strong>
                            <small>
                              {{
                                formatR07Yer(r07FacilityTotals.current_budget)
                              }}
                            </small>
                          </td>
                          <td class="numeric-cell r07-money-cell">
                            <strong>
                              {{
                                formatR07Currency(
                                  r07FacilityTotals.required_budget
                                )
                              }}
                            </strong>
                            <small>
                              {{
                                formatR07Yer(r07FacilityTotals.required_budget)
                              }}
                            </small>
                          </td>
                          <td class="numeric-cell r07-money-cell">
                            <strong>
                              {{
                                formatR07Currency(r07FacilityTotals.budget_gap)
                              }}
                            </strong>
                            <small>
                              {{ formatR07Yer(r07FacilityTotals.budget_gap) }}
                            </small>
                          </td>
                          <td class="numeric-cell">
                            {{ formatNumber(r07FacilityTotals.gap_rate) }}%
                          </td>
                          <td class="numeric-cell r07-money-cell">
                            <strong>
                              {{
                                formatR07Currency(
                                  r07FacilityTotals.estimated_rehab_cost
                                )
                              }}
                            </strong>
                            <small>
                              {{
                                formatR07Yer(
                                  r07FacilityTotals.estimated_rehab_cost
                                )
                              }}
                            </small>
                          </td>
                          <td class="numeric-cell r07-money-cell">
                            <strong>
                              {{
                                formatR07Currency(
                                  r07FacilityTotals.total_required_cost
                                )
                              }}
                            </strong>
                            <small>
                              {{
                                formatR07Yer(
                                  r07FacilityTotals.total_required_cost
                                )
                              }}
                            </small>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <h3>التوزيع المرحلي حسب المرفق الصحي</h3>

                  <div class="r07-table-wrap">
                    <table class="r07-phasing-table">
                      <thead>
                        <tr>
                          <th>المرفق / الجهة</th>
                          <th>فوري (0–3 أشهر)</th>
                          <th>قصير الأجل (3–12 شهراً)</th>
                          <th>متوسط الأجل (1–3 سنوات)</th>
                          <th>الإجمالي</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="facility in r07Facilities"
                          :key="`phase-${
                            facility.survey_id || facility.facility_id
                          }`"
                        >
                          <td>
                            <strong>
                              {{ textValue(facility.facility_name) }}
                            </strong>
                          </td>
                          <td class="numeric-cell">
                            {{
                              formatR07Currency(
                                facility.cost_items_summary.immediate_usd
                              )
                            }}
                          </td>
                          <td class="numeric-cell">
                            {{
                              formatR07Currency(
                                facility.cost_items_summary.short_term_usd
                              )
                            }}
                          </td>
                          <td class="numeric-cell">
                            {{
                              formatR07Currency(
                                facility.cost_items_summary.medium_term_usd
                              )
                            }}
                          </td>
                          <td class="numeric-cell">
                            <strong>
                              {{
                                formatR07Currency(
                                  facility.cost_items_summary.total_usd
                                )
                              }}
                            </strong>
                          </td>
                        </tr>
                        <tr class="subtotal-row">
                          <td><strong>الإجمالي</strong></td>
                          <td class="numeric-cell">
                            {{
                              formatR07Currency(r07PhasedTotals.immediate_usd)
                            }}
                          </td>
                          <td class="numeric-cell">
                            {{
                              formatR07Currency(r07PhasedTotals.short_term_usd)
                            }}
                          </td>
                          <td class="numeric-cell">
                            {{
                              formatR07Currency(r07PhasedTotals.medium_term_usd)
                            }}
                          </td>
                          <td class="numeric-cell">
                            <strong>
                              {{ formatR07Currency(r07PhasedTotals.total_usd) }}
                            </strong>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <h3 class="r07-details-heading">
                    تفاصيل التكاليف حسب المرفق الصحي
                  </h3>

                  <section
                    v-for="(facility, facilityIndex) in r07Facilities"
                    :key="`details-${
                      facility.survey_id ||
                      facility.facility_id ||
                      facilityIndex
                    }`"
                    class="r07-facility-section"
                  >
                    <header class="r07-facility-title">
                      <div>
                        <span>المرفق الصحي رقم {{ facilityIndex + 1 }}</span>
                        <h4>{{ textValue(facility.facility_name) }}</h4>
                        <small
                          v-if="facility.facility_name_en"
                          class="r07-facility-name-en"
                        >
                          {{ facility.facility_name_en }}
                        </small>
                      </div>

                      <div class="r07-facility-grand-total">
                        <span>إجمالي المطلوب</span>
                        <strong>
                          {{ formatR07Currency(facility.total_required_cost) }}
                        </strong>
                      </div>
                    </header>

                    <div class="r07-facility-meta">
                      <span>
                        نوع الاحتياج
                        <strong>{{ formatR07Needs(facility) }}</strong>
                      </span>
                      <span>
                        فجوة الميزانية
                        <strong>
                          {{ formatR07Currency(facility.budget_gap) }}
                        </strong>
                      </span>
                      <span>
                        نسبة الفجوة
                        <strong>{{ formatNumber(facility.gap_rate) }}%</strong>
                      </span>
                      <span>
                        تكلفة التأهيل
                        <strong>
                          {{ formatR07Currency(facility.estimated_rehab_cost) }}
                        </strong>
                      </span>
                    </div>

                    <div class="r07-facility-phase-grid">
                      <div>
                        <span>فوري (0–3 أشهر)</span>
                        <strong>
                          {{
                            formatR07Currency(
                              facility.cost_items_summary.immediate_usd
                            )
                          }}
                        </strong>
                      </div>
                      <div>
                        <span>قصير الأجل (3–12 شهراً)</span>
                        <strong>
                          {{
                            formatR07Currency(
                              facility.cost_items_summary.short_term_usd
                            )
                          }}
                        </strong>
                      </div>
                      <div>
                        <span>متوسط الأجل (1–3 سنوات)</span>
                        <strong>
                          {{
                            formatR07Currency(
                              facility.cost_items_summary.medium_term_usd
                            )
                          }}
                        </strong>
                      </div>
                      <div class="r07-phase-total">
                        <span>إجمالي بنود التأهيل</span>
                        <strong>
                          {{
                            formatR07Currency(
                              facility.cost_items_summary.total_usd
                            )
                          }}
                        </strong>
                      </div>
                    </div>

                    <p
                      v-if="!facility.cost_groups.length"
                      class="empty-section r07-empty-costs"
                    >
                      لا توجد بنود تكلفة تفصيلية لهذا المرفق.
                    </p>

                    <div
                      v-for="group in facility.cost_groups"
                      :key="`${
                        facility.survey_id ||
                        facility.facility_id ||
                        facilityIndex
                      }-${group.code}`"
                      class="r07-cost-group"
                    >
                      <div class="r07-cost-group-title">
                        <strong>{{ textValue(group.code) }}</strong>
                        <div>
                          <h4>{{ textValue(group.name) }}</h4>
                          <span>{{ group.items.length }} بند تكلفة</span>
                        </div>
                      </div>

                      <div class="r07-table-wrap">
                        <table class="r07-items-table">
                          <thead>
                            <tr>
                              <th>بند التكلفة</th>
                              <th>الكمية</th>
                              <th>الوحدة</th>
                              <th>سعر الوحدة ($)</th>
                              <th>فوري</th>
                              <th>قصير الأجل</th>
                              <th>متوسط الأجل</th>
                              <th>إجمالي 3 سنوات</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="item in group.items"
                              :key="`${group.code}-${item.line_no}-${item.name}`"
                            >
                              <td>{{ textValue(item.name) }}</td>
                              <td class="numeric-cell">
                                {{ formatNumber(item.quantity) }}
                              </td>
                              <td>{{ textValue(item.unit) }}</td>
                              <td class="numeric-cell">
                                {{ formatR07Currency(item.unit_rate_usd) }}
                              </td>
                              <td class="numeric-cell">
                                {{ formatR07Currency(item.immediate_usd) }}
                              </td>
                              <td class="numeric-cell">
                                {{ formatR07Currency(item.short_term_usd) }}
                              </td>
                              <td class="numeric-cell">
                                {{ formatR07Currency(item.medium_term_usd) }}
                              </td>
                              <td class="numeric-cell">
                                <strong>
                                  {{ formatR07Currency(item.total_usd) }}
                                </strong>
                              </td>
                            </tr>

                            <tr class="subtotal-row">
                              <td colspan="4">
                                <strong>
                                  إجمالي {{ textValue(group.name) }}
                                </strong>
                              </td>
                              <td class="numeric-cell">
                                {{ formatR07Currency(group.immediate_usd) }}
                              </td>
                              <td class="numeric-cell">
                                {{ formatR07Currency(group.short_term_usd) }}
                              </td>
                              <td class="numeric-cell">
                                {{ formatR07Currency(group.medium_term_usd) }}
                              </td>
                              <td class="numeric-cell">
                                <strong>
                                  {{ formatR07Currency(group.total_usd) }}
                                </strong>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </section>
                </template>

                <template
                  v-if="
                    selectedReport?.id !== 'R07' && genericFacilities.length
                  "
                >
                  <h3>{{ tr("ui.text_156") }}</h3>
                  <div class="table-scroll">
                    <table class="generic-facilities-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>{{ tr("ui.text_022") }}</th>
                          <th>{{ tr("ui.text_088") }}</th>
                          <th>{{ tr("ui.text_157") }}</th>
                          <th>{{ tr("ui.text_024") }}</th>
                          <th>{{ tr("ui.text_031") }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(facility, index) in genericFacilities"
                          :key="facility.facility_id || facility.id || index"
                        >
                          <td>{{ facility.rank ?? index + 1 }}</td>
                          <td>
                            {{
                              textValue(
                                facility.facility_name_ar ||
                                  facility.facility_name ||
                                  facility.name
                              )
                            }}
                          </td>
                          <td>
                            {{
                              textValue(
                                facility.district_name_ar || facility.district
                              )
                            }}
                          </td>
                          <td>
                            {{
                              formatScore(
                                facility.fis ??
                                  facility.overall_score ??
                                  facility.normalized_score ??
                                  facility.score
                              )
                            }}
                          </td>
                          <td>{{ textValue(facility.priority) }}</td>
                          <td>
                            {{
                              textValue(facility.risk_level || facility.status)
                            }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </template>

                <template v-if="genericIndicators.length">
                  <h3>{{ tr("ui.text_158") }}</h3>
                  <div class="table-scroll">
                    <table class="indicators-table">
                      <thead>
                        <tr>
                          <th>{{ tr("ui.text_047") }}</th>
                          <th>{{ tr("ui.text_048") }}</th>
                          <th>{{ tr("ui.text_049") }}</th>
                          <th>{{ tr("ui.text_051") }}</th>
                          <th>{{ tr("ui.text_052") }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="indicator in genericIndicators"
                          :key="indicator.code"
                        >
                          <td>{{ textValue(indicator.code) }}</td>
                          <td>
                            {{ axisLabel(indicator.axis || indicator.code) }}
                          </td>
                          <td>{{ textValue(indicator.name) }}</td>
                          <td>{{ displayIndicatorValue(indicator) }}</td>
                          <td>
                            <span
                              class="rag-badge"
                              :class="ragClass(indicator)"
                            >
                              {{ ragLabel(indicator) }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </template>

                <template v-if="genericCostCategories.length">
                  <h3>{{ tr("ui.text_159") }}</h3>
                  <div class="cost-summary-grid">
                    <article
                      v-for="category in genericCostCategories"
                      :key="category.code || category.name"
                      class="cost-summary-card"
                    >
                      <span>{{ textValue(category.name) }}</span>
                      <strong>{{ formatCurrency(category.total) }}</strong>
                    </article>
                  </div>
                </template>

                <template v-if="genericInterventions.length">
                  <h3>{{ tr("ui.text_160") }}</h3>
                  <div class="generic-cards">
                    <article
                      v-for="(item, index) in genericInterventions"
                      :key="item.id || item.code || index"
                      class="generic-card"
                    >
                      <h4>
                        {{
                          item.title ||
                          item.name ||
                          item.intervention ||
                          tr("common.interventionNumber", { number: index + 1 })
                        }}
                      </h4>
                      <p v-if="item.description">{{ item.description }}</p>
                      <div class="generic-card-meta">
                        <span v-if="item.priority">{{
                          tr("mixed.priority", { value: item.priority })
                        }}</span>
                        <span v-if="item.timeframe || item.period">
                          {{
                            tr("mixed.duration", {
                              value: item.timeframe || item.period,
                            })
                          }}
                        </span>
                        <span v-if="'estimated_cost' in item || 'cost' in item">
                          {{
                            tr("mixed.cost", {
                              value: formatCurrency(
                                item.estimated_cost ?? item.cost
                              ),
                            })
                          }}
                        </span>
                      </div>
                    </article>
                  </div>
                </template>

                <template v-if="genericScenarios.length">
                  <h3>{{ tr("ui.text_161") }}</h3>
                  <div class="scenarios-list">
                    <article
                      v-for="(scenario, index) in genericScenarios"
                      :key="scenario.code || index"
                      class="scenario-card"
                    >
                      <div class="scenario-code">
                        {{ scenario.code || `S${index + 1}` }}
                      </div>
                      <div>
                        <h4>
                          {{ textValue(scenario.title || scenario.name) }}
                        </h4>
                        <p v-if="scenario.description">
                          {{ scenario.description }}
                        </p>
                        <div v-if="scenario.action" class="scenario-action">
                          <strong>{{ tr("ui.text_072") }}</strong>
                          {{ scenario.action }}
                        </div>
                      </div>
                    </article>
                  </div>
                </template>

                <template v-if="genericNeeds.length">
                  <h3>{{ tr("ui.text_162") }}</h3>
                  <ul>
                    <li v-for="(item, index) in genericNeeds" :key="index">
                      {{ item }}
                    </li>
                  </ul>
                </template>

                <template v-if="selectedReport?.outputs?.length">
                  <h3>{{ tr("ui.text_163") }}</h3>
                  <ul>
                    <li v-for="item in selectedReport.outputs" :key="item">
                      {{ item }}
                    </li>
                  </ul>
                </template>

                <h3>{{ tr("ui.text_026") }}</h3>
                <p class="methodology-note">
                  {{
                    selectedReport?.id === "R07"
                      ? needsCostsMethodologyNote ||
                        tr("common.costMethodology")
                      : reportData.methodology_note ||
                        tr("common.genericMethodology")
                  }}
                </p>
              </div>

              <div v-else class="empty-section">
                {{ tr("ui.text_164") }}
              </div>
            </div>

            <div v-else class="empty-section">{{ tr("ui.text_165") }}</div>
          </div>
        </div>

        <div class="m-foot">
          <button
            type="button"
            class="btn export-pdf"
            :disabled="!canExportReport"
            @click="downloadReport('pdf')"
          >
            {{ exportingFormat === "pdf" ? "جارٍ التصدير..." : "تصدير PDF" }}
          </button>
          <button
            type="button"
            class="btn export-excel"
            :disabled="!canExportReport"
            @click="downloadReport('xlsx')"
          >
            {{ exportingFormat === "xlsx" ? "جارٍ التصدير..." : "تصدير Excel" }}
          </button>
          <button class="btn" @click="printReport">
            {{ tr("ui.text_166") }}
          </button>
          <button class="btn ghost" @click="closeReport">
            {{ tr("ui.text_167") }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, ref } from "vue";
import { useI18n } from "vue-i18n";
import api from "@/plugins/api";

const { t, locale } = useI18n();
const tr = (key, params = {}) => t(`reportsPage.${key}`, params);
const pageDirection = computed(() => (locale.value === "ar" ? "rtl" : "ltr"));

// قيم العرض الموحدة للحقول الفارغة:
// - الحقل الرقمي الفارغ يعرض 0.
// - الحقل النصي الفارغ يعرض وصفاً واضحاً حسب لغة الواجهة.
const unknownText = () =>
  String(locale.value).startsWith("ar") ? "غير معروف" : "Unknown";

const textValue = (value, fallback = unknownText()) => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string" && value.trim() === "") return fallback;
  return value;
};

const modalOpen = ref(false);
const selectedReport = ref(null);
const reportData = ref(null);
const loadingReport = ref(false);
const reportError = ref("");
const exportingFormat = ref("");

const facilities = ref([]);
const selectedFacilityId = ref("");
const loadingFacilities = ref(false);
const expandedCostCategories = ref([]);

const governorates = ref([]);
const districts = ref([]);
const loadingFilters = ref(false);
const selectedGovernorateId = ref("");
const selectedDistrictCode = ref("");
const selectedAxis = ref("INF");
const scenarioLevel = ref("facility");
const selectedScenarioFacilityId = ref("");
const selectedScenarioDistrictCode = ref("");
const selectedEquipmentFacilityId = ref("");
const selectedEquipmentAvailability = ref("");
const selectedStaffFacilityId = ref("");
const selectedStaffAvailability = ref("");
const selectedMedicineFacilityId = ref("");
const selectedMedicineStockLevel = ref("");
const selectedReportLevel = ref("national");
const selectedAdministrativeFacilityId = ref("");

const administrativeLevelReportIds = [
  "R04",
  "R06",
  "R07",
  "R10",
  "R13",
  "R14",
  "R15",
];

const usesAdministrativeLevelFilter = computed(() =>
  administrativeLevelReportIds.includes(selectedReport.value?.id)
);

const allAdministrativeLevelOptions = [
  { value: "national", label: "المستوى الوطني / المركزي" },
  { value: "governorate", label: "مستوى المحافظة" },
  { value: "district", label: "مستوى المديرية" },
  { value: "facility", label: "مستوى المرفق الصحي" },
];

const administrativeLevelOptions = computed(() => {
  // تقرير حالة المحافظة يظل نسخة من الموجز الوطني مفلترة بمحافظة واحدة.
  if (selectedReport.value?.id === "R04") {
    return allAdministrativeLevelOptions.filter(
      (option) => option.value === "governorate"
    );
  }

  return allAdministrativeLevelOptions;
});

const administrativeNeedsGovernorate = computed(() =>
  ["governorate", "district", "facility"].includes(selectedReportLevel.value)
);

const administrativeNeedsDistrict = computed(() =>
  ["district", "facility"].includes(selectedReportLevel.value)
);

const administrativeNeedsFacility = computed(
  () => selectedReportLevel.value === "facility"
);

const canLoadAdministrativeReport = computed(() => {
  if (!usesAdministrativeLevelFilter.value) return false;
  if (administrativeNeedsGovernorate.value && !selectedGovernorateId.value) {
    return false;
  }
  if (administrativeNeedsDistrict.value && !selectedDistrictCode.value) {
    return false;
  }
  if (
    administrativeNeedsFacility.value &&
    !selectedAdministrativeFacilityId.value
  ) {
    return false;
  }
  if (selectedReport.value?.id === "R06" && !selectedAxis.value) return false;

  return true;
});

const buildAdministrativeReportParams = () => {
  const params = {
    level: selectedReportLevel.value,
  };

  if (selectedGovernorateId.value) {
    params.governorate_id = String(selectedGovernorateId.value);
  }

  if (selectedDistrictCode.value) {
    params.district_code = String(selectedDistrictCode.value);
  }

  if (selectedAdministrativeFacilityId.value) {
    const facilityId = String(selectedAdministrativeFacilityId.value);
    params.facility_id = facilityId;

    // إبقاء الأسماء القديمة مؤقتاً حتى تعمل الواجهات الحالية أثناء الترحيل.
    params.survey_id = facilityId;
    params.facility = facilityId;
  }

  if (selectedReport.value?.id === "R06" && selectedAxis.value) {
    params.axis = selectedAxis.value;
  }

  return params;
};

const axesOptions = computed(() => [
  { code: "INF", name: tr("axes.INF") },
  { code: "SRV", name: tr("axes.SRV") },
  { code: "HRH", name: tr("axes.HRH") },
  { code: "FIN", name: tr("axes.FIN") },
  { code: "GOV", name: tr("axes.GOV") },
  { code: "EQ", name: tr("axes.EQ") },
]);
const specializedAxisRows = computed(() => {
  const source =
    reportData.value?.data ||
    reportData.value?.results ||
    reportData.value?.rows ||
    [];

  return Array.isArray(source) ? source : [];
});

const specializedIndicatorSummary = computed(() => {
  const source =
    reportData.value?.indicator_summary ||
    reportData.value?.indicators_summary ||
    reportData.value?.summary?.indicators ||
    [];

  return Array.isArray(source) ? source : [];
});

const weakestAxisFacilities = computed(() => {
  const source =
    reportData.value?.weakest_facilities ||
    reportData.value?.low_performance_facilities ||
    [];

  return Array.isArray(source) ? source : [];
});

const specializedRecommendations = computed(() => {
  return asArray(reportData.value?.recommendations || []);
});

const specializedAxisFacilitiesCount = computed(() => {
  return (
    toNumber(reportData.value?.facilities_count) ??
    toNumber(reportData.value?.summary?.facilities_count) ??
    0
  );
});

const specializedAxisResultsCount = computed(() => {
  return (
    toNumber(reportData.value?.results_count) ??
    toNumber(reportData.value?.summary?.results_count) ??
    specializedAxisRows.value.length
  );
});
const medicineStatistics = computed(() => reportData.value?.statistics || {});
const medicineAvailabilitySummary = computed(
  () => reportData.value?.availability_summary || {}
);

// The API owns aggregation and the ten-year equipment rule.
const inventoryReportIds = ["R13", "R14", "R15"];
const inventoryStaffOptions = ref([]);

const staffAvailabilityLabels = {
  ar: {
    fully_available: "متوفر بالكامل",
    partially_available: "متوفر جزئياً",
    sometimes_available: "متوفر أحياناً",
    available: "متوفر",
    not_available: "غير متوفر",
    not_applicable: "لا ينطبق",
    on_call: "متوفر عند الطلب",
    mixed: "حالات متعددة",
    unknown: "غير معروف",
  },
  en: {
    fully_available: "Fully available",
    partially_available: "Partially available",
    sometimes_available: "Sometimes available",
    available: "Available",
    not_available: "Not available",
    not_applicable: "Not applicable",
    on_call: "Available on call",
    mixed: "Mixed availability",
    unknown: "Unknown",
  },
};

const staffAvailabilityAliases = {
  full: "fully_available",
  fully: "fully_available",
  full_available: "fully_available",
  fully_available: "fully_available",
  available_fully: "fully_available",

  partial: "partially_available",
  partially: "partially_available",
  partial_available: "partially_available",
  partially_available: "partially_available",
  available_partially: "partially_available",

  no: "not_available",
  none: "not_available",
  unavailable: "not_available",
  not_available: "not_available",

  na: "not_applicable",
  "n/a": "not_applicable",
  not_applicable: "not_applicable",
};

const staffAvailabilityLabelAliases = {
  "متوفر كلياً": "fully_available",
  "متوفر كليا": "fully_available",
  "متوفر بالكامل": "fully_available",
  "fully available": "fully_available",

  "متوفر بشكل جزئي": "partially_available",
  "متوفر جزئياً": "partially_available",
  "متوفر جزئيا": "partially_available",
  "partially available": "partially_available",

  "غير متوفر": "not_available",
  "not available": "not_available",

  "لا ينطبق": "not_applicable",
  "not applicable": "not_applicable",
};

const normalizeStaffAvailabilityText = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const normalizeStaffAvailabilityCode = (value) => {
  const code = normalizeStaffAvailabilityText(value);
  return staffAvailabilityAliases[code] || code;
};

const normalizeStaffAvailabilityOption = (option) => {
  const code = normalizeStaffAvailabilityCode(option?.code);
  const labelAr = normalizeStaffAvailabilityText(option?.label_ar);
  const labelEn = normalizeStaffAvailabilityText(option?.label_en);

  return (
    staffAvailabilityAliases[code] ||
    staffAvailabilityLabelAliases[labelAr] ||
    staffAvailabilityLabelAliases[labelEn] ||
    code
  );
};

const normalizeInventoryStatusCode = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const cleanStaffAvailabilityLabel = (value, code) => {
  const label = String(value ?? "").trim();
  if (!label) return "";

  // Some API responses return the code itself in the label field.
  return normalizeStaffAvailabilityCode(label) === code ? "" : label;
};

const staffAvailabilityOptions = computed(() => {
  const apiOptions = reportData.value?.availability_options;
  const sourceOptions =
    Array.isArray(apiOptions) && apiOptions.length
      ? apiOptions
      : inventoryStaffOptions.value;
  const language = String(locale.value).startsWith("ar") ? "ar" : "en";
  const uniqueOptions = new Map();

  for (const option of Array.isArray(sourceOptions) ? sourceOptions : []) {
    const code = normalizeStaffAvailabilityOption(option);
    if (!code) continue;

    const labelAr =
      staffAvailabilityLabels.ar[code] ||
      cleanStaffAvailabilityLabel(option?.label_ar, code) ||
      code.replaceAll("_", " ");
    const labelEn =
      staffAvailabilityLabels.en[code] ||
      cleanStaffAvailabilityLabel(option?.label_en, code) ||
      code.replaceAll("_", " ");

    uniqueOptions.set(code, {
      ...option,
      code,
      label_ar: labelAr,
      label_en: labelEn,
      label: language === "ar" ? labelAr : labelEn,
    });
  }

  const availabilityOrder = [
    "fully_available",
    "partially_available",
    "not_available",
    "not_applicable",
    "sometimes_available",
    "available",
    "on_call",
    "unknown",
  ];

  return Array.from(uniqueOptions.values()).sort((a, b) => {
    const aIndex = availabilityOrder.indexOf(a.code);
    const bIndex = availabilityOrder.indexOf(b.code);

    return (
      (aIndex === -1 ? availabilityOrder.length : aIndex) -
      (bIndex === -1 ? availabilityOrder.length : bIndex)
    );
  });
});

const isInventoryReport = computed(() =>
  inventoryReportIds.includes(selectedReport.value?.id)
);
const inventoryMessages = {
  ar: {
    national: "المستوى الوطني",
    governorate: "المحافظة",
    district: "المديرية",
    facility: "المرفق الصحي",
    totals: "إجماليات الأصناف",
    details: "تفاصيل المرفق",
    quantity: "إجمالي الكمية",
    unit: "الوحدة",
    facilities: "عدد المرافق التي يتوفر فيها الصنف",
    availableFacilities: "مرافق يتوفر فيها الصنف",
    scopeFacilities: "المرافق ضمن النطاق",
    facilityCount: "عدد المرافق",
    missing: "0",
    partial: "المجموع المسجل جزئياً",
    missingRecords: "سجلات بكمية غير مسجلة",
    equipment: "الجهاز",
    departments: "الأقسام",
    department: "القسم",
    equipmentTypes: "أنواع الأجهزة",
    totalEquipmentQuantity: "إجمالي كمية الأجهزة",
    availableEquipmentQuantity: "كمية الأجهزة المتوفرة",
    oldQuantity: "كمية الأجهزة القديمة",
    brokenQuantity: "كمية الأجهزة المعطلة",
    generalStatus: "الحالة العامة",
    operationalStatus: "الحالة التشغيلية",
    availability: "التوفر",
    dates: "تاريخ التركيب",
    ageYears: "عدد السنوات",
    notes: "ملاحظات",
    model: "الموديل",
    old: "قديم",
    broken: "معطل",
    excellent: "ممتاز",
    good: "جيد",
    fair: "مقبول",
    unknown: "غير معروف",
    mixed: "حالات متعددة",
    mixedWithOld: "حالات متعددة، تشمل أجهزة قديمة",
    yes: "متوفر",
    no: "غير متوفر",
    na: "لا ينطبق",
    r: "مطلوب",
    c: "مشروط",
    cadre: "الكادر",
    cadreGroup: "المجموعة",
    cadreTypes: "فئات الكوادر",
    male: "ذكور",
    female: "إناث",
    totalStaff: "إجمالي الكوادر",
    dailyAvailability: "التوفر اليومي",
    medicine: "الدواء",
    medicineTypes: "أصناف الأدوية",
    medicineFacilitySummary: "توفر الأدوية الأساسية حسب المرفق",
    medicineFacilitySummaryNote:
      "تظهر حالة كل مرفق بصورة مستقلة، وتظهر أسباب الانقطاع فقط عندما تكون الأدوية متوفرة جزئياً أو غير متوفرة.",
    medicineAvailabilityMethodology:
      "سجل basic_meds هو ملخص للمرفق ولا يدخل ضمن أصناف الأدوية أو كمياتها. السبب يظهر فقط للحالة الجزئية أو غير المتوفرة.",
    availabilityStatus: "حالة التوفر",
    fully: "متوفرة بالكامل",
    partially: "متوفرة جزئياً",
    not_available: "غير متوفرة",
    not_recorded: "غير مسجل",
    reasonNotRecorded: "السبب غير مسجل",
    emptyFacilityAvailability: "لا يوجد ملخص توفر مسجل للمرافق.",
    stock: "مستوى المخزون",
    sufficient: "كافٍ",
    insufficient: "غير كافٍ",
    out_of_stock: "نافد",
    summary: "ملخص النطاق",
    summaryNote:
      "الملخص يشمل كامل النطاق. فلتر التوفر أو المخزون يغيّر صفوف الجدول فقط.",
    empty: "لا توجد بيانات مطابقة للفلتر المحدد.",
    methodology: "منهجية التقرير",
    reasons: "أسباب الانقطاع",
    recordCounters: "ملخص المعدات الطبية",
    totalRecords: "إجمالي سجلات التقييم",
    availableRecords: "السجلات المتوفرة",
    unavailableRecords: "السجلات غير المتوفرة",
    brokenRecords: "سجلات الأجهزة المعطلة",
    oldRecords: "سجلات الأجهزة القديمة",
    naRecords: "سجلات لا ينطبق",
    totalsNote:
      "صف واحد لكل صنف ووحدة، مع مجموع الكميات وعدد المرافق المميزة التي يتوفر فيها. المرفق يُحسب مرة واحدة ولو ورد الصنف في عدة أقسام.",
    detailsNote: "يعرض التقرير سجلات المرفق المختار وأعدادها وتفاصيلها.",
    quantityNote:
      "الكمية غير المسجلة لا تعني صفراً. لا تجمع الكميات بوحدات مختلفة.",
    oldNote:
      "يظهر تاريخ التركيب في عمود مستقل، ويظهر العمر في عمود عدد السنوات. الجهاز قديم عندما يزيد عمره على 10 سنوات.",
  },
  en: {
    national: "National level",
    governorate: "Governorate",
    district: "District",
    facility: "Health facility",
    totals: "Item totals",
    details: "Facility details",
    quantity: "Total quantity",
    unit: "Unit",
    facilities: "Facilities with this item",
    availableFacilities: "Facilities with available items",
    scopeFacilities: "Facilities in scope",
    facilityCount: "Facility count",
    missing: "0",
    partial: "Recorded subtotal",
    missingRecords: "Records without a quantity",
    equipment: "Equipment",
    departments: "Departments",
    department: "Department",
    equipmentTypes: "Equipment types",
    totalEquipmentQuantity: "Total equipment quantity",
    availableEquipmentQuantity: "Available equipment quantity",
    oldQuantity: "Old equipment quantity",
    brokenQuantity: "Broken equipment quantity",
    generalStatus: "General status",
    operationalStatus: "Operational status",
    availability: "Availability",
    dates: "Installation date",
    ageYears: "Number of years",
    notes: "Notes",
    model: "Model",
    old: "Old",
    broken: "Broken",
    excellent: "Excellent",
    good: "Good",
    fair: "Fair",
    unknown: "Unknown",
    mixed: "Mixed statuses",
    mixedWithOld: "Mixed, including old equipment",
    yes: "Available",
    no: "Unavailable",
    na: "N/A",
    r: "Required",
    c: "Conditional",
    cadre: "Cadre",
    cadreGroup: "Group",
    cadreTypes: "Cadre categories",
    male: "Male",
    female: "Female",
    totalStaff: "Total workforce",
    dailyAvailability: "Daily availability",
    medicine: "Medicine",
    medicineTypes: "Medicine types",
    medicineFacilitySummary: "Essential medicines availability by facility",
    medicineFacilitySummaryNote:
      "Each facility is shown separately. Interruption reasons appear only when medicines are partially available or unavailable.",
    medicineAvailabilityMethodology:
      "The basic_meds record is a facility summary and is excluded from medicine items and quantities. Reasons appear only for partial or unavailable status.",
    availabilityStatus: "Availability status",
    fully: "Fully available",
    partially: "Partially available",
    not_available: "Not available",
    not_recorded: "Not recorded",
    reasonNotRecorded: "Reason not recorded",
    emptyFacilityAvailability:
      "No facility availability summary has been recorded.",
    stock: "Stock level",
    sufficient: "Sufficient",
    insufficient: "Insufficient",
    out_of_stock: "Out of stock",
    summary: "Scope summary",
    summaryNote:
      "The summary covers the full scope. Availability and stock filters affect table rows only.",
    empty: "No data matches the selected filter.",
    methodology: "Report methodology",
    reasons: "Interruption reasons",
    recordCounters: "Medical equipment summary",
    totalRecords: "Total assessment records",
    availableRecords: "Available records",
    unavailableRecords: "Unavailable records",
    brokenRecords: "Broken equipment records",
    oldRecords: "Old equipment records",
    naRecords: "N/A records",
    totalsNote:
      "One row per item and unit, with total quantities and distinct facilities where it is available. Each facility is counted once, even across multiple departments.",
    detailsNote:
      "The report shows the selected facility's records, quantities and details.",
    quantityNote:
      "An unrecorded quantity is not zero. Quantities with different units are kept separate.",
    oldNote:
      "The installation date and number of years are shown in separate columns. Equipment is old when its age exceeds 10 years.",
  },
};
const inventoryLanguage = computed(() =>
  String(locale.value).startsWith("ar") ? "ar" : "en"
);
const inventoryText = (key) =>
  inventoryMessages[inventoryLanguage.value][key] ?? key;
const inventoryLocalized = (value) => {
  if (value === null || value === undefined || value === "") {
    return inventoryText("unknown");
  }
  const parts = String(value).split(/\s+\/\s+/);
  return parts.length === 2
    ? parts[inventoryLanguage.value === "ar" ? 0 : 1]
    : String(value);
};
const inventoryName = (row, ar, en) => {
  const preferred = inventoryLanguage.value === "ar" ? row?.[ar] : row?.[en];
  return preferred || row?.[ar] || row?.[en] || "";
};

const medicineFacilities = computed(() => {
  const rows = medicineAvailabilitySummary.value?.facilities;
  return Array.isArray(rows) ? rows : [];
});

const normalizeMedicineFacilityAvailability = (value) => {
  const code = String(value ?? "")
    .trim()
    .toLowerCase();

  const aliases = {
    full: "fully",
    fully_available: "fully",
    available_fully: "fully",
    available: "fully",
    partial: "partially",
    partially_available: "partially",
    available_partially: "partially",
    unavailable: "not_available",
    none: "not_available",
    no: "not_available",
    out_of_stock: "not_available",
    never_existed: "not_available",
  };

  return aliases[code] || code || "not_recorded";
};

const medicineFacilityStatusCode = (facility) =>
  normalizeMedicineFacilityAvailability(
    facility?.availability || facility?.availability_code
  );

const medicineFacilityStatusClass = (facility) => {
  const code = medicineFacilityStatusCode(facility);

  return (
    {
      fully: "fully_available",
      partially: "partially_available",
      not_available: "not_available",
      not_recorded: "unknown",
    }[code] || "unknown"
  );
};

const medicineFacilityAvailabilityLabel = (facility) => {
  return (
    inventoryName(facility, "availability_ar", "availability_en") ||
    inventoryText(medicineFacilityStatusCode(facility))
  );
};

const medicineFacilityNeedsReason = (facility) => {
  if (typeof facility?.requires_reason === "boolean") {
    return facility.requires_reason;
  }

  return ["partially", "not_available"].includes(
    medicineFacilityStatusCode(facility)
  );
};

const medicineFacilityReasons = (facility) => {
  if (!medicineFacilityNeedsReason(facility)) return "";

  const reasonLabels = Array.isArray(facility?.reasons)
    ? facility.reasons
        .map((reason) => inventoryName(reason, "label_ar", "label_en"))
        .filter(Boolean)
    : [];

  if (!reasonLabels.length) {
    const storedReasons =
      inventoryLanguage.value === "ar"
        ? facility?.reasons_ar
        : facility?.reasons_en;

    if (storedReasons) reasonLabels.push(storedReasons);
  }

  const otherReason = inventoryName(
    facility,
    "interruption_other_ar",
    "interruption_other_en"
  );

  if (otherReason) reasonLabels.push(otherReason);

  const uniqueReasons = [...new Set(reasonLabels.map(String))];

  return uniqueReasons.length
    ? uniqueReasons.join(inventoryLanguage.value === "ar" ? "، " : ", ")
    : inventoryText("reasonNotRecorded");
};

const inventoryIsAggregated = computed(() => {
  const mode = reportData.value?.aggregation_mode;
  if (mode === "item_totals") return true;
  if (mode === "facility_details") return false;
  return (
    (reportData.value?.scope?.level ||
      reportData.value?.filters?.level ||
      selectedReportLevel.value) !== "facility"
  );
});
const inventoryRows = computed(() => {
  const key = { R13: "equipment", R14: "staff", R15: "medicines" }[
    selectedReport.value?.id
  ];
  const rows = reportData.value?.[key];
  return Array.isArray(rows) ? rows : [];
});
const inventoryScope = computed(() => reportData.value?.scope || {});
const inventoryLevelLabel = computed(() => {
  const level =
    inventoryScope.value.level ||
    reportData.value?.filters?.level ||
    selectedReportLevel.value;
  return inventoryText(level);
});
const inventoryScopeLabel = computed(() => {
  const scope = inventoryScope.value;
  const level =
    scope.level ||
    reportData.value?.filters?.level ||
    selectedReportLevel.value;
  if (level === "national") return inventoryText("national");
  return (
    inventoryName(scope, "scope_label", "scope_label_en") ||
    inventoryName(scope, "label", "label_en") ||
    inventoryName(reportData.value?.report, "scope_label", "scope_label_en") ||
    inventoryName(
      reportData.value?.facility,
      "facility_name_ar",
      "facility_name_en"
    ) ||
    inventoryLevelLabel.value
  );
});
const inventoryFacilityDescription = computed(() => {
  const row = inventoryRows.value[0] || {};
  const facility = {
    ...row,
    ...inventoryScope.value,
    ...(reportData.value?.facility || {}),
  };
  return [
    inventoryName(facility, "facility_type_ar", "facility_type_en"),
    inventoryName(facility, "district_name_ar", "district_name_en") ||
      inventoryName(facility, "district_name", "district_name_en"),
    inventoryName(facility, "governorate_name_ar", "governorate_name_en") ||
      inventoryName(facility, "governorate_name", "governorate_name_en"),
  ]
    .filter(Boolean)
    .join(" · ");
});
const inventoryReportTitle = computed(() => {
  const report = reportData.value?.report;
  return (
    inventoryName(report, "name", "name_en") ||
    inventoryName(report, "title", "title_en") ||
    selectedReport.value?.name ||
    ""
  );
});
const inventoryNumber = (value) => {
  if (
    value === null ||
    value === undefined ||
    typeof value === "boolean" ||
    String(value).trim() === ""
  )
    return inventoryText("missing");
  const number = Number(value);
  return Number.isFinite(number)
    ? number.toLocaleString("en-US", { maximumFractionDigits: 2 })
    : inventoryText("missing");
};
const inventoryQuantityNote = (row, quantityKey = "quantity") => {
  const prefixes = {
    quantity: "",
    available_quantity: "available_",
    old_quantity: "old_",
    broken_quantity: "broken_",
  };
  const prefix = prefixes[quantityKey] ?? "";
  const missing = row?.[`${prefix}quantity_missing_count`];
  if (!(Number(missing) > 0)) return "";
  const recorded = row?.[`${prefix}recorded_quantity`];
  const partial =
    recorded !== null && recorded !== undefined && Number(recorded) > 0
      ? `${inventoryText("partial")}: ${inventoryNumber(recorded)}. `
      : "";
  return `${partial}${inventoryText("missingRecords")}: ${inventoryNumber(
    missing
  )}`;
};
const inventorySummaryCards = computed(() => {
  const data = reportData.value || {};
  const summary = data.summary || {};
  const card = (key, label, value, tone = "", note = "") => ({
    key,
    label: inventoryText(label),
    value,
    tone,
    note,
  });
  if (selectedReport.value?.id === "R13") {
    const recordNote = (label, count, quantityKey = "quantity") =>
      [
        `${inventoryText(label)}: ${inventoryNumber(count)}`,
        inventoryQuantityNote(summary, quantityKey),
      ]
        .filter(Boolean)
        .join(" · ");
    return [
      card(
        "total",
        "totalEquipmentQuantity",
        summary.quantity,
        "",
        recordNote("totalRecords", summary.total_equipment)
      ),
      card(
        "available",
        "availableEquipmentQuantity",
        summary.available_quantity,
        "available",
        [
          recordNote(
            "availableRecords",
            summary.available_count,
            "available_quantity"
          ),
          `${inventoryText("facilityCount")}: ${inventoryNumber(
            summary.facilities_count
          )}`,
        ].join(" · ")
      ),
      card(
        "unavailable",
        "unavailableRecords",
        summary.unavailable_count,
        "unavailable"
      ),
      card(
        "not-applicable",
        "naRecords",
        summary.not_applicable_count,
        "not-applicable"
      ),
      card(
        "broken",
        "brokenQuantity",
        summary.broken_quantity,
        "broken",
        recordNote("brokenRecords", summary.broken_count, "broken_quantity")
      ),
      card(
        "old",
        "oldQuantity",
        summary.old_quantity,
        "old",
        recordNote("oldRecords", summary.old_count, "old_quantity")
      ),
    ];
  }
  if (selectedReport.value?.id === "R14")
    return [
      card("types", "cadreTypes", summary.cadres_count),
      card(
        "total",
        "totalStaff",
        summary.total_staff,
        "",
        inventoryQuantityNote(summary)
      ),
      card("male", "male", summary.male_count),
      card("female", "female", summary.female_count),
      card(
        "facilities",
        "availableFacilities",
        summary.facilities_count,
        "available"
      ),
    ];
  const stats = data.statistics || {};
  const cards = [
    card("types", "medicineTypes", stats.total_medicines),
    card(
      "facilities",
      "availableFacilities",
      stats.facilities_count,
      "available"
    ),
  ];
  for (const code of [
    "sufficient",
    "insufficient",
    "out_of_stock",
    "mixed",
    "unknown",
  ]) {
    if (
      ["mixed", "unknown"].includes(code) &&
      !(Number(stats[code]?.count) > 0)
    )
      continue;
    cards.push({
      ...card(code, code, stats[code]?.count, code),
      percentage: stats[code]?.percentage,
    });
  }
  return cards;
});
const inventoryTableColumns = computed(() => {
  const column = (key, label, type = "text", extra = {}) => ({
    key,
    label: inventoryText(label),
    type,
    ...extra,
  });
  const quantity = column("quantity", "quantity", "quantity");
  const facilities = column("facilities_count", "facilities", "number");
  if (selectedReport.value?.id === "R13") {
    const columns = [
      column("equipment_name_ar", "equipment", "equipment"),
      column(
        "department_name_ar",
        inventoryIsAggregated.value ? "departments" : "department"
      ),
      quantity,
      column("unit", "unit"),
    ];
    if (inventoryIsAggregated.value)
      return [
        ...columns,
        facilities,
        column("old_quantity", "oldQuantity", "number", {
          countKey: "old_facilities_count",
        }),
        column("broken_quantity", "brokenQuantity", "number", {
          countKey: "broken_facilities_count",
        }),
        column("general_status", "generalStatus", "status", {
          codeKey: "general_status_code",
        }),
      ];
    return [
      ...columns,
      column("general_status", "generalStatus", "status", {
        codeKey: "general_status_code",
      }),
      column("operational_status", "operationalStatus", "status", {
        codeKey: "operational_status_code",
      }),
      column("date_installation", "dates", "installation-date"),
      column("age_years", "ageYears", "equipment-age"),
      column("notes", "notes"),
    ];
  }
  if (selectedReport.value?.id === "R14") {
    const columns = [
      column("cadre_name_ar", "cadre"),
      column("cadre_group_ar", "cadreGroup"),
      column("male_count", "male", "number"),
      column("female_count", "female", "number"),
      column("total_count", "totalStaff", "quantity"),
    ];
    if (inventoryIsAggregated.value) columns.push(facilities);
    return [
      ...columns,
      column("availability_today_ar", "dailyAvailability", "status", {
        codeKey: "availability_code",
      }),
    ];
  }
  const columns = [
    column("supply_name_ar", "medicine", "medicine"),
    quantity,
    column("unit", "unit"),
  ];
  if (inventoryIsAggregated.value) columns.push(facilities);
  return [
    ...columns,
    column("stock_level_ar", "stock", "status", { codeKey: "stock_level" }),
  ];
});
const inventoryStatusCode = (row, column) => {
  if (column.key === "availability_today_ar") {
    return (
      normalizeStaffAvailabilityOption({
        code: row?.availability_code || row?.availability_today_ar,
        label_ar: row?.availability_today_ar,
      }) || "unknown"
    );
  }

  if (
    column.key === "general_status" &&
    (row.is_old === true || row.general_status_code === "old")
  ) {
    return "old";
  }

  return normalizeInventoryStatusCode(row?.[column.codeKey] || "unknown");
};

const inventoryStatusLabel = (row, column) => {
  const code = inventoryStatusCode(row, column);

  if (
    column.key === "general_status" &&
    code === "mixed" &&
    Number(row.old_facilities_count) > 0
  ) {
    return inventoryText("mixedWithOld");
  }

  if (column.key === "availability_today_ar") {
    const rawLabel = String(row?.[column.key] ?? "").trim();
    const option = staffAvailabilityOptions.value.find(
      (item) => normalizeStaffAvailabilityCode(item?.code) === code
    );

    if (option?.label) return option.label;

    const fallback = staffAvailabilityLabels[inventoryLanguage.value]?.[code];
    if (fallback) return fallback;

    if (rawLabel && normalizeStaffAvailabilityCode(rawLabel) !== code) {
      return inventoryLocalized(rawLabel);
    }

    return code.replaceAll("_", " ");
  }

  return (
    inventoryMessages[inventoryLanguage.value][code] ||
    inventoryLocalized(row[column.key])
  );
};
const inventoryEquipmentAgeLabel = (row) => {
  const years = Number(row?.age_years);
  if (
    row?.age_years === null ||
    row?.age_years === undefined ||
    row?.age_years === "" ||
    !Number.isFinite(years)
  )
    return inventoryNumber(0);

  if (years > 10)
    return inventoryLanguage.value === "ar"
      ? `قديم (${inventoryNumber(years)})`
      : `Old (${inventoryNumber(years)})`;

  if (inventoryLanguage.value === "en")
    return `${inventoryNumber(years)} ${years === 1 ? "year" : "years"}`;

  if (years === 0) return "أقل من سنة";
  if (years === 1) return "سنة";
  if (years === 2) return "سنتين";
  return `${inventoryNumber(years)} سنوات`;
};
const inventoryRowKey = (row, index) =>
  JSON.stringify(
    inventoryIsAggregated.value
      ? [
          selectedReport.value?.id,
          row.equipment_id ??
            row.cadre_id ??
            row.supply_code ??
            row.supply_name_ar,
          row.unit ?? "",
        ]
      : [row.survey_id ?? "", row.id ?? index]
  );
const inventoryMethodology = computed(() => {
  if (inventoryLanguage.value === "ar" && reportData.value?.methodology_note)
    return reportData.value.methodology_note;
  return [
    inventoryText(inventoryIsAggregated.value ? "totalsNote" : "detailsNote"),
    inventoryText("quantityNote"),
    selectedReport.value?.id === "R13" ? inventoryText("oldNote") : "",
    selectedReport.value?.id === "R15"
      ? inventoryText("medicineAvailabilityMethodology")
      : "",
  ]
    .filter(Boolean)
    .join(" ");
});

const genericReportIds = [
  "R04",
  "R05",
  "R07",
  "R08",
  "R09",
  "R10",
  "R11",
  "R12",
];

const today = computed(() => new Date().toISOString().slice(0, 10));

const reportDate = computed(
  () =>
    reportData.value?.report?.generated_at ||
    reportData.value?.generated_at ||
    today.value
);

const reports = ref([
  {
    id: "R01",
    name: "موجز القرار التنفيذي الوطني",
    audience: "القيادة الوطنية / وزير الصحة",
    level: "وطني",
    freq: "ربع سنوي",
    isApiReport: true,
    api: "/reports/national-executive-summary",
    requiresGovernorate: true,
    requiresDistrict: true,
    summary: "",
    outputs: [],
  },
  {
    id: "R02",
    name: "تقرير ترتيب أولويات المرافق (FIS)",
    audience: "لجنة الاستثمار الصحي",
    level: "مديرية",
    freq: "بعد كل تقييم",
    isApiReport: true,
    api: "/reports/facility-priority-fis",
    summary: "",
    outputs: [],
  },
  {
    id: "R03",
    name: "ملف المرفق الصحي التفصيلي",
    audience: "إدارة المرفق / مكتب الصحة",
    level: "مرفق",
    freq: "بعد كل تقييم",
    isApiReport: true,
    api: "/reports/facility-profile",
    summary: "",
    outputs: [],
  },
  {
    id: "R04",
    name: "تقرير حالة المحافظة",
    audience: "مكتب الصحة بالمحافظة",
    level: "محافظة",
    freq: "ربع سنوي",
    isApiReport: true,
    api: "/reports/governorate",
    requiresGovernorate: true,
    summary: "",
    outputs: [],
  },
  {
    id: "R05",
    name: "تقرير المديرية الشامل",
    audience: "مدير مكتب صحة المديرية",
    level: "مديرية",
    freq: "بعد كل تقييم",
    isApiReport: true,
    api: "/reports/district",
    requiresDistrict: true,
    summary: "",
    outputs: [],
  },
  {
    id: "R06",
    name: "تقرير المحور المتخصص",
    audience: "الإدارات الفنية المتخصصة",
    level: "متعدد",
    freq: "حسب الطلب",
    isApiReport: true,
    api: "/reports/axis",
    requiresAxis: true,
    summary: "",
    outputs: [],
  },
  {
    id: "R07",
    name: "تقرير الاحتياجات والتكاليف",
    audience: "وزارة المالية / إدارة التخطيط",
    level: "مديرية",
    freq: "سنوي",
    isApiReport: true,
    api: "/reports/needs-costs",
    detailsApi: "/reports/rehab-budget-summary",
    requiresGovernorate: true,
    requiresDistrict: true,
    summary: "",
    outputs: [],
  },
  {
    id: "R10",
    name: "تقرير الإنصاف والنوع الاجتماعي (GESI)",
    audience: "إدارة النوع الاجتماعي / الشركاء",
    level: "مديرية",
    freq: "نصف سنوي",
    isApiReport: true,
    api: "/reports/gesi",
    requiresDistrict: true,
    summary: "",
    outputs: [],
  },
  {
    id: "R13",
    name: "تقرير المعدات الطبية",
    audience: "إدارة المرفق / إدارة التجهيزات الطبية",
    level: "مرفق",
    freq: "بعد كل تقييم",
    isApiReport: true,
    api: "/reports/medical-equipment",
    summary: "",
    outputs: [
      "قائمة المعدات الطبية",
      "حالة توفر المعدات",
      "الحالة الفنية للمعدات",
      "توزيع المعدات حسب الأقسام",
    ],
  },
  {
    id: "R14",
    name: "تقرير الكوادر الصحية",
    audience: "إدارة المرفق / الموارد البشرية الصحية",
    level: "مرفق",
    freq: "بعد كل تقييم",
    isApiReport: true,
    api: "/reports/staff-cadres",
    outputs: ["توزيع الكوادر", "التوزيع حسب الجنس", "التوفر اليومي"],
  },
  {
    id: "R15",
    name: "تقرير توفر الأدوية الأساسية ومستوى المخزون",
    audience: "إدارة المرفق / إدارة الإمداد الدوائي",
    level: "مرفق",
    freq: "بعد كل تقييم",
    isApiReport: true,
    api: "/reports/essential-medicines",
    outputs: ["حالة توفر الأدوية", "مستويات المخزون", "أسباب الانقطاع"],
  },
]);

const reportExportKeys = Object.freeze({
  R01: "national-executive-summary",
  R02: "facility-priority-fis",
  R03: "facility-profile",
  R04: "governorate",
  R05: "district",
  R06: "axis",
  R07: "needs-costs",
  R10: "gesi",
  R13: "medical-equipment",
  R14: "staff-cadres",
  R15: "essential-medicines",
});

const canExportReport = computed(
  () =>
    Boolean(reportData.value) &&
    Boolean(reportExportKeys[selectedReport.value?.id]) &&
    !loadingReport.value &&
    !exportingFormat.value
);

const localizedReports = computed(() =>
  reports.value.map((report) => ({
    ...report,
    name: tr(`reports.${report.id}.name`),
    audience: tr(`reports.${report.id}.audience`),
    level: administrativeLevelReportIds.includes(report.id)
      ? report.id === "R04"
        ? "محافظة"
        : "متعدد المستويات"
      : tr(`reports.${report.id}.level`),
    freq: tr(`reports.${report.id}.frequency`),
  }))
);

const facilityTypeAr = (type) => {
  const values = {
    general_hospital: tr("facilityTypes.general_hospital"),
    specialized_hospital: tr("facilityTypes.specialized_hospital"),
    health_complex: tr("facilityTypes.health_complex"),
    health_center: tr("facilityTypes.health_center"),
    primary_health_center: tr("facilityTypes.primary_health_center"),
    health_unit: tr("facilityTypes.health_unit"),
  };
  return values[type] || textValue(type);
};

const operationalStatusAr = (value) => {
  const values = {
    fully_operational: tr("operationalStatuses.fullyOperational"),
    operational: tr("operationalStatuses.operational"),
    partially_operational: tr("operationalStatuses.partiallyOperational"),
    partial: tr("operationalStatuses.partiallyOperational"),
    non_operational: tr("operationalStatuses.nonOperational"),
    closed: tr("operationalStatuses.closed"),
    temporarily_closed: tr("operationalStatuses.temporarilyClosed"),
  };
  return values[value] || textValue(value);
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(/[$,%]/g, "").trim());
  return Number.isFinite(n) ? n : null;
};

const asArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === "string"
          ? item
          : item.title ||
            item.name ||
            item.description ||
            item.finding ||
            item.action
      )
      .filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(/[؛\n]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const formatNumber = (value) => {
  const n = toNumber(value);
  return (n ?? 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const formatScore = (value) => {
  const n = toNumber(value);
  return (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

const formatPercent = (value) => {
  const n = toNumber(value);
  return `${formatNumber(n ?? 0)}%`;
};

const formatMoneyNumber = (value) => {
  const n = toNumber(value);
  return (n ?? 0).toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const formatCurrency = (value, currency = "USD") => {
  const n = toNumber(value);
  const formatted = (n ?? 0).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  });
  const code = String(currency || "USD")
    .trim()
    .toUpperCase();

  if (["YER", "ريال", "ر.ي"].includes(code)) {
    return `${formatted} ريال`;
  }

  if (["USD", "$", "US$"].includes(code)) {
    return `$${formatted}`;
  }

  return `${formatted} ${code}`;
};

const priorityLabel = (value) => {
  const labels = {
    P1: "الأولوية الأولى — حرجة",
    P2: "الأولوية الثانية — عالية",
    P3: "الأولوية الثالثة — متوسطة",
    P4: "الأولوية الرابعة — منخفضة",
  };
  return labels[String(value || "").toUpperCase()] || textValue(value);
};

const facilityInfo = computed(() => {
  const f =
    reportData.value?.facility || reportData.value?.data?.facility || {};

  return {
    id: f.id ?? f.facility_id ?? selectedFacilityId.value,
    name: f.name ?? f.facility_name ?? f.facility_name_ar ?? unknownText(),
    type: f.type ?? f.facility_type ?? f.facility_type_code ?? f.type_code,
    governorate:
      f.governorate ?? f.governorate_name ?? f.province_name ?? f.province,
    district:
      f.district ?? f.district_name ?? f.district_name_ar ?? f.district_ar,
    operational_status: f.operational_status ?? f.status,
    total_staff: f.total_staff ?? f.staff_count ?? null,
    female_staff_ratio: toNumber(
      f.female_staff_ratio ?? f.female_ratio ?? f.female_staff_percentage
    ),
    beneficiaries_month:
      f.beneficiaries_month ?? f.monthly_beneficiaries ?? null,
    target_population: f.target_population ?? f.population_coverage ?? null,
    latitude: f.latitude ?? f.lat ?? f.gps_latitude,
    longitude: f.longitude ?? f.lng ?? f.lon ?? f.gps_longitude,
  };
});

const profileSummary = computed(() => {
  const s = reportData.value?.summary || reportData.value?.data?.summary || {};

  const f =
    reportData.value?.facility || reportData.value?.data?.facility || {};

  return {
    fis: toNumber(
      s.fis ??
        s.fis_score ??
        s.overall_score ??
        s.FIS ??
        s.facility_fis ??
        f.fis ??
        f.fis_score ??
        reportData.value?.fis
    ),

    priority:
      s.priority ??
      s.periority ??
      f.priority ??
      reportData.value?.priority ??
      null,

    risk_level:
      s.risk_level ??
      s.priority_level ??
      f.risk_level ??
      reportData.value?.risk_level ??
      null,

    timeframe: s.timeframe ?? f.timeframe ?? s.intervention_timeframe ?? null,

    rank: s.rank ?? s.facility_rank ?? null,

    facilities_count: s.facilities_count ?? s.total_facilities ?? null,
  };
});
const facilityCoordinates = computed(() => {
  const { latitude, longitude } = facilityInfo.value;
  if (
    latitude === null ||
    latitude === undefined ||
    longitude === null ||
    longitude === undefined
  )
    return "";
  return `${latitude}, ${longitude}`;
});

const normalizeIndicator = (item) => ({
  ...item,
  code: String(
    item.code || item.indicator || item.indicator_code || ""
  ).toUpperCase(),
  axis: item.axis || item.pillar || item.category || "",
  name:
    item.name || item.title || item.indicator_name || item.label || item.code,
  value:
    item.value ?? item.score ?? item.percentage ?? item.indicator_value ?? null,
  unit: item.unit ?? item.value_unit ?? "",
  standard:
    item.standard ||
    item.benchmark ||
    item.target_description ||
    item.standard_text ||
    "",
  status: item.status || item.rag || item.color || item.classification || "",
});

const facilityIndicators = computed(() => {
  const source =
    reportData.value?.indicators ||
    reportData.value?.rag_indicators ||
    reportData.value?.indicator_values ||
    reportData.value?.data?.indicators ||
    [];

  if (Array.isArray(source)) return source.map(normalizeIndicator);

  if (source && typeof source === "object") {
    return Object.entries(source).map(([code, value]) =>
      normalizeIndicator(
        value && typeof value === "object"
          ? { code, ...value }
          : { code, name: code, value }
      )
    );
  }

  return [];
});
let inventoryRequestVersion = 0;
const invalidateInventoryRequest = () => {
  inventoryRequestVersion += 1;
  if (isInventoryReport.value) loadingReport.value = false;
};

const loadMedicalEquipmentReport = async () => {
  const requestVersion = ++inventoryRequestVersion;
  reportData.value = null;
  reportError.value = "";

  if (usesAdministrativeLevelFilter.value && !canLoadAdministrativeReport.value)
    return;

  if (
    !usesAdministrativeLevelFilter.value &&
    !selectedEquipmentFacilityId.value
  )
    return;

  loadingReport.value = true;

  try {
    const params = usesAdministrativeLevelFilter.value
      ? buildAdministrativeReportParams()
      : { survey_id: selectedEquipmentFacilityId.value };

    if (selectedEquipmentAvailability.value) {
      params.availability = selectedEquipmentAvailability.value;
    }

    const response = await api.get("/reports/medical-equipment", {
      params,
    });

    if (requestVersion !== inventoryRequestVersion) return;
    reportData.value = response.data?.data ?? response.data;
  } catch (error) {
    if (requestVersion !== inventoryRequestVersion) return;
    console.error("Medical equipment report error:", error);

    reportError.value =
      error.response?.data?.message || tr("common.medicalEquipmentLoadError");
  } finally {
    if (requestVersion === inventoryRequestVersion) loadingReport.value = false;
  }
};
const loadStaffCadreReport = async () => {
  const requestVersion = ++inventoryRequestVersion;
  if (
    usesAdministrativeLevelFilter.value &&
    !canLoadAdministrativeReport.value
  ) {
    reportData.value = null;
    return;
  }

  if (!usesAdministrativeLevelFilter.value && !selectedStaffFacilityId.value) {
    reportData.value = null;
    reportError.value = tr("common.facilityRequired");
    return;
  }

  try {
    loadingReport.value = true;
    reportError.value = "";
    reportData.value = null;

    const params = usesAdministrativeLevelFilter.value
      ? buildAdministrativeReportParams()
      : { survey_id: selectedStaffFacilityId.value };

    if (selectedStaffAvailability.value) {
      params.availability = selectedStaffAvailability.value;
    }

    const response = await api.get("/reports/staff-cadres", {
      params,
    });

    if (requestVersion !== inventoryRequestVersion) return;
    const result = response.data?.data ?? response.data;
    if (result?.report?.code !== "R14") {
      throw new Error(
        `استجابة غير صحيحة من API، رمز التقرير: ${
          result?.report?.code || "غير موجود"
        }`
      );
    }

    if (Array.isArray(result.availability_options))
      inventoryStaffOptions.value = result.availability_options;
    reportData.value = result;
  } catch (error) {
    if (requestVersion !== inventoryRequestVersion) return;
    console.error("Staff cadre report error:", error);

    reportData.value = null;

    reportError.value =
      error.response?.data?.errors?.survey_id?.[0] ||
      error.response?.data?.message ||
      error.message ||
      tr("common.staffLoadError");
  } finally {
    if (requestVersion === inventoryRequestVersion) loadingReport.value = false;
  }
};

const loadEssentialMedicinesReport = async () => {
  const requestVersion = ++inventoryRequestVersion;
  reportData.value = null;
  reportError.value = "";

  if (usesAdministrativeLevelFilter.value && !canLoadAdministrativeReport.value)
    return;

  if (!usesAdministrativeLevelFilter.value && !selectedMedicineFacilityId.value)
    return;

  loadingReport.value = true;
  try {
    const params = usesAdministrativeLevelFilter.value
      ? buildAdministrativeReportParams()
      : { facility: String(selectedMedicineFacilityId.value) };
    if (selectedMedicineStockLevel.value) {
      params.stock_level = selectedMedicineStockLevel.value;
    }

    const { data } = await api.get("/reports/essential-medicines", { params });
    if (requestVersion !== inventoryRequestVersion) return;
    reportData.value = data?.data ?? data;
  } catch (error) {
    if (requestVersion !== inventoryRequestVersion) return;
    console.error("Essential medicines report error:", error);
    reportError.value =
      error.response?.data?.message || tr("common.medicinesLoadError");
  } finally {
    if (requestVersion === inventoryRequestVersion) loadingReport.value = false;
  }
};

const getAxisCode = (item) => {
  const explicit = String(item.axis || "").toUpperCase();
  for (const code of ["INF", "SRV", "HRH", "FIN", "GOV", "EQ"]) {
    if (explicit.includes(code)) return code;
  }
  return String(item.code || "")
    .split("-")[0]
    .toUpperCase();
};

const axisLabel = (value) => {
  const code =
    typeof value === "object"
      ? getAxisCode(value)
      : String(value || "")
          .split("-")[0]
          .toUpperCase();

  return (
    {
      INF: "البنية التحتية",
      SRV: "الخدمات",
      HRH: "الموارد البشرية",
      FIN: "التمويل",
      GOV: "الحوكمة",
      EQ: "الإنصاف وإمكانية الوصول",
    }[code] || textValue(value)
  );
};

const indicatorValue = (item) =>
  toNumber(item.value ?? item.score ?? item.percentage);

const displayIndicatorValue = (item) => {
  const n = indicatorValue(item);
  if (n === null) return textValue(item.value);
  const v = formatNumber(n);
  return item.unit === "%" || item.unit === "percent"
    ? `${v}%`
    : item.unit
    ? `${v} ${item.unit}`
    : v;
};

const ragStatus = (item) => {
  const direct = String(item.status || "").toLowerCase();

  if (["green", "good", "excellent", "met", "جيد", "ممتاز"].includes(direct))
    return "green";
  if (["yellow", "fair", "جزئي", "مقبول"].includes(direct)) return "yellow";
  if (["orange", "warning", "moderate", "متوسط", "محدود"].includes(direct))
    return "orange";
  if (["red", "critical", "poor", "bad", "حرج", "ضعيف"].includes(direct))
    return "red";

  const n = indicatorValue(item);
  if (n === null) return "gray";

  if (item.code === "INF-01") {
    if (n >= 3) return "red";
    if (n >= 2) return "orange";
    if (n >= 1) return "yellow";
    return "green";
  }

  if (item.code === "FIN-02") {
    if (n >= 50) return "red";
    if (n >= 30) return "orange";
    if (n >= 20) return "yellow";
    return "green";
  }

  if (n >= 80) return "green";
  if (n >= 60) return "yellow";
  if (n >= 40) return "orange";
  return "red";
};

const ragClass = (item) => `rag-${ragStatus(item)}`;

const ragLabel = (item) =>
  ({
    green: "جيد",
    yellow: "مقبول",
    orange: "يحتاج تحسين",
    red: "حرج",
    gray: unknownText(),
  }[ragStatus(item)]);

const normalizePercent = (value) => {
  const n = toNumber(value);
  return n === null ? 0 : Math.min(100, Math.max(0, n));
};

const scoreColorClass = (value) => {
  const n = toNumber(value);
  if (n === null) return "score-gray";
  if (n >= 80) return "score-green";
  if (n >= 60) return "score-yellow";
  if (n >= 40) return "score-orange";
  return "score-red";
};

const axisSummaries = computed(() => {
  const configs = [
    { code: "INF", name: "البنية التحتية" },
    { code: "SRV", name: "الخدمات الصحية" },
    { code: "HRH", name: "القوى العاملة الصحية" },
    { code: "FIN", name: "التمويل والمرونة المالية" },
    { code: "GOV", name: "الحوكمة ونظم المعلومات" },
  ];

  const backend =
    reportData.value?.axes ||
    reportData.value?.axis_summaries ||
    reportData.value?.pillars ||
    {};

  return configs.map((config) => {
    let axis = {};

    if (Array.isArray(backend)) {
      axis =
        backend.find(
          (item) =>
            String(
              item.code || item.axis || item.pillar || ""
            ).toUpperCase() === config.code
        ) || {};
    } else {
      axis = backend[config.code] || backend[config.code.toLowerCase()] || {};
    }

    const indicators = facilityIndicators.value.filter(
      (item) => getAxisCode(item) === config.code
    );

    const values = indicators
      .map(indicatorValue)
      .filter((value) => value !== null);

    const average = values.length
      ? values.reduce((sum, value) => sum + value, 0) / values.length
      : null;

    return {
      code: config.code,
      name: axis.name || config.name,
      score: toNumber(axis.score ?? axis.value ?? axis.average) ?? average,
      indicators,
      highlights: asArray(
        axis.highlights || axis.findings || axis.key_findings
      ),
      recommendations: asArray(
        axis.recommendations || axis.interventions || axis.actions
      ),
    };
  });
});

const rawInterventions = computed(
  () =>
    reportData.value?.interventions ||
    reportData.value?.recommended_interventions ||
    reportData.value?.intervention_package ||
    []
);

const interventionGroups = computed(() => {
  if (!Array.isArray(rawInterventions.value)) return [];

  const labels = {
    immediate: "فوري",
    urgent: "فوري",
    short_term: "قصير الأجل",
    short: "قصير الأجل",
    medium_term: "متوسط الأجل",
    medium: "متوسط الأجل",
    long_term: "طويل الأجل",
    long: "طويل الأجل",
  };

  const groups = {};

  rawInterventions.value.forEach((raw) => {
    const item = typeof raw === "string" ? { title: raw } : raw;

    const rawPeriod =
      item.period || item.timeframe || item.phase || unknownText();

    const key = String(rawPeriod).trim().toLowerCase().replace(/\s+/g, "_");

    const period = labels[key] || rawPeriod;

    if (!groups[period]) groups[period] = [];

    groups[period].push({
      ...item,
      title: item.title || item.name || item.intervention || "تدخل مقترح",
      estimated_cost: item.estimated_cost ?? item.cost ?? null,
    });
  });

  return Object.entries(groups).map(([period, items]) => ({
    period,
    items,
  }));
});

const normalizeCostItem = (item) => ({
  ...item,
  name:
    item.name || item.title || item.cost_item || item.item_name || "بند تكلفة",
  quantity: item.quantity ?? item.qty ?? null,
  unit: item.unit ?? item.measurement_unit ?? unknownText(),
  unit_price: item.unit_price ?? item.price ?? null,
  immediate: item.immediate ?? item.immediate_cost ?? item.urgent ?? null,
  short_term: item.short_term ?? item.short_term_cost ?? null,
  medium_term: item.medium_term ?? item.medium_term_cost ?? null,
  total: item.total ?? item.total_cost ?? item.three_year_total ?? null,
});

const costNameMap = {
  rehabilitation: "إعادة التأهيل",
  rehab: "إعادة التأهيل",
  maintenance: "الصيانة",
  infrastructure: "البنية التحتية والإصلاح الإنشائي",
  inf: "البنية التحتية والإصلاح الإنشائي",
  non_structural: "الأعمال غير الإنشائية",
  operational_services: "الخدمات التشغيلية",
  operational_services_er: "الخدمات التشغيلية والطوارئ",
  wash: "المياه والإصحاح والنظافة",
  electrical: "الأعمال الكهربائية والطاقة",
  equipment: "المعدات الطبية والمستلزمات",
  medical_equipment: "المعدات الطبية والمستلزمات",
  services: "الخدمات الصحية",
  staff: "القوى العاملة الصحية",
  human_resources: "القوى العاملة الصحية",
  hrh: "القوى العاملة الصحية",
  workforce: "القوى العاملة الصحية",
  medicine: "الأدوية والمستلزمات",
  medicines: "الأدوية والمستلزمات",
  finance: "التمويل",
  governance: "الحوكمة ونظم المعلومات",
  gov: "الحوكمة ونظم المعلومات",
  gesi: "البنية المستجيبة للنوع الاجتماعي والإعاقة",
  gender: "البنية المستجيبة للنوع الاجتماعي والإعاقة",
  emergency: "التأهب للطوارئ والقدرة الاستيعابية",
  other: "احتياجات أخرى",
};

const splitCostCodes = (value) =>
  String(value || "")
    .split(/[,،;|]+/)
    .map((item) => item.trim())
    .filter(Boolean);

const costName = (value) => {
  const codes = splitCostCodes(value);

  if (!codes.length) return "فئة تكلفة";

  const names = codes.map((code) => {
    const normalizedCode = code.toLowerCase();
    return costNameMap[normalizedCode] || code.replaceAll("_", " ");
  });

  return names.length > 1 ? `حزمة متعددة: ${names.join("، ")}` : names[0];
};

const normalizeCostCategory = (category, index = 0) => {
  const rawItems =
    category.items || category.details || category.cost_items || [];

  const items = Array.isArray(rawItems) ? rawItems.map(normalizeCostItem) : [];

  const categoryCode =
    category.code || category.category_code || `category-${index}`;

  const rawCategoryName =
    category.name ||
    category.category_name ||
    category.category ||
    category.title;

  // الـ API قد يعيد name بنفس رموز الاختيارات المفصولة بفواصل.
  // عندها نستخدم الاسم العربي المترجم بدل عرض الرموز الخام.
  const categoryNameLooksLikeCode =
    !rawCategoryName ||
    rawCategoryName === categoryCode ||
    /^[a-z0-9_\s,;|.-]+$/i.test(String(rawCategoryName));

  const sum = (field) =>
    items.reduce((total, item) => total + (toNumber(item[field]) || 0), 0);

  return {
    ...category,
    code: categoryCode,
    name: categoryNameLooksLikeCode
      ? costName(categoryCode || rawCategoryName)
      : rawCategoryName,
    items,
    immediate:
      category.immediate ?? category.immediate_total ?? sum("immediate"),
    short_term:
      category.short_term ?? category.short_term_total ?? sum("short_term"),
    medium_term:
      category.medium_term ?? category.medium_term_total ?? sum("medium_term"),
    total: category.total ?? category.total_cost ?? sum("total"),
  };
};

const rawCostCategories = computed(() => {
  const costSummary = reportData.value?.cost_summary;

  return (
    reportData.value?.cost_categories ??
    costSummary?.categories ??
    reportData.value?.costs?.categories ??
    reportData.value?.estimated_costs ??
    []
  );
});

const normalizeCostSource = (source) => {
  if (Array.isArray(source)) return source.map(normalizeCostCategory);

  if (source && typeof source === "object") {
    // بعض استجابات الـ API تضع الفئات داخل cost_summary.categories،
    // بينما بقية خصائص cost_summary هي مجاميع وبيانات وصفية وليست فئات.
    if (source.categories !== undefined) {
      return normalizeCostSource(source.categories);
    }

    const summaryMetaKeys = new Set([
      "total",
      "total_cost",
      "total_estimated_cost",
      "immediate",
      "immediate_total",
      "short_term",
      "short_term_total",
      "medium_term",
      "medium_term_total",
      "currency",
    ]);

    return Object.entries(source)
      .filter(([code]) => !summaryMetaKeys.has(code))
      .map(([code, value], index) =>
        normalizeCostCategory(
          value && typeof value === "object"
            ? { code, ...value }
            : { code, name: costName(code), total: value },
          index
        )
      );
  }

  return [];
};

const costCategories = computed(() =>
  normalizeCostSource(rawCostCategories.value)
);

const costCurrency = computed(
  () =>
    reportData.value?.cost_summary?.currency ||
    reportData.value?.costs?.currency ||
    reportData.value?.currency ||
    "USD"
);

const formatCostCurrency = (value) => formatCurrency(value, costCurrency.value);

const detailedCostCategories = computed(() => {
  const source =
    reportData.value?.cost_details ||
    reportData.value?.detailed_costs ||
    reportData.value?.costs?.details ||
    rawCostCategories.value;

  return normalizeCostSource(source).filter(
    (category) => category.items.length
  );
});

const totalEstimatedCost = computed(() => {
  const supplied = toNumber(
    reportData.value?.total_estimated_cost ??
      reportData.value?.cost_summary?.total_estimated_cost ??
      reportData.value?.costs?.total ??
      reportData.value?.cost_summary?.total
  );

  if (supplied !== null) return supplied;

  return costCategories.value.reduce(
    (sum, category) => sum + (toNumber(category.total) || 0),
    0
  );
});

const costMethodologyNote = computed(
  () =>
    reportData.value?.cost_methodology_note || reportData.value?.cost_note || ""
);

const facilityScenarios = computed(() => {
  const source =
    reportData.value?.scenarios ||
    reportData.value?.activated_scenarios ||
    reportData.value?.decision_scenarios ||
    [];

  if (!Array.isArray(source)) return [];

  return source.map((scenario, index) => {
    if (typeof scenario !== "string") return scenario;

    const pos = scenario.indexOf(":");
    return pos > -1
      ? {
          code: scenario.slice(0, pos).trim(),
          title: scenario.slice(pos + 1).trim(),
        }
      : {
          code: `S${index + 1}`,
          title: scenario,
        };
  });
});

const filteredDistricts = computed(() => {
  if (!selectedGovernorateId.value) return districts.value;

  return districts.value.filter((district) => {
    const governorateId =
      district.governorate_id ??
      district.province_id ??
      district.governorate_code ??
      district.province_code;

    return String(governorateId ?? "") === String(selectedGovernorateId.value);
  });
});

const resetAdministrativeResult = () => {
  if (isInventoryReport.value) invalidateInventoryRequest();
  reportData.value = null;
  reportError.value = "";
};

const handleAdministrativeLevelChange = async () => {
  selectedGovernorateId.value = "";
  selectedDistrictCode.value = "";
  selectedAdministrativeFacilityId.value = "";
  facilities.value = [];
  resetAdministrativeResult();

  if (selectedReportLevel.value === "national") return;

  if (!governorates.value.length || !districts.value.length) {
    await loadReportFilters();
  }
};

const handleAdministrativeGovernorateChange = () => {
  selectedDistrictCode.value = "";
  selectedAdministrativeFacilityId.value = "";
  facilities.value = [];
  resetAdministrativeResult();
};

const handleAdministrativeDistrictChange = async () => {
  selectedAdministrativeFacilityId.value = "";
  facilities.value = [];
  resetAdministrativeResult();

  if (selectedReportLevel.value === "facility" && selectedDistrictCode.value) {
    await loadFacilities({
      loadProfile: false,
      governorateId: selectedGovernorateId.value,
      districtCode: selectedDistrictCode.value,
    });
  }
};

const loadAdministrativeReport = async () => {
  if (!canLoadAdministrativeReport.value || !selectedReport.value) return;

  switch (selectedReport.value.id) {
    case "R13":
      await loadMedicalEquipmentReport();
      return;
    case "R14":
      await loadStaffCadreReport();
      return;
    case "R15":
      await loadEssentialMedicinesReport();
      return;
    default:
      await loadSelectedReport();
  }
};

const r07BudgetPayload = computed(() => {
  if (selectedReport.value?.id !== "R07") return {};

  return (
    reportData.value?.rehab_budget_details ||
    reportData.value?.rehab_budget ||
    reportData.value ||
    {}
  );
});

const genericSummaryRows = computed(() => {
  const summary =
    selectedReport.value?.id === "R07"
      ? reportData.value?.package_summary || r07BudgetPayload.value?.summary
      : reportData.value?.summary;

  if (!summary || Array.isArray(summary) || typeof summary !== "object") {
    return [];
  }

  return Object.entries(summary)
    .filter(
      ([key, value]) =>
        key !== "title" &&
        (value === null ||
          ["string", "number", "boolean"].includes(typeof value))
    )
    .map(([key, value]) => ({ key, value }));
});

const genericPriorityRows = computed(() => {
  const source = reportData.value?.priority_distribution || {};
  if (!source || typeof source !== "object" || Array.isArray(source)) return [];
  return Object.entries(source).map(([priority, value]) => ({
    priority,
    value,
  }));
});

const genericReportAxes = computed(() => {
  const source =
    reportData.value?.axes ||
    reportData.value?.axis_summary ||
    reportData.value?.axis_summaries ||
    [];

  if (Array.isArray(source)) {
    return source.map((axis) => ({
      ...axis,
      code: String(
        axis.code || axis.axis || axis.axis_code || ""
      ).toUpperCase(),
      score: toNumber(axis.score ?? axis.value ?? axis.average),
    }));
  }

  if (source && typeof source === "object") {
    return Object.entries(source).map(([code, axis]) => ({
      code: String(code).toUpperCase(),
      ...(axis && typeof axis === "object" ? axis : { score: axis }),
      score: toNumber(
        axis && typeof axis === "object"
          ? axis.score ?? axis.value ?? axis.average
          : axis
      ),
    }));
  }

  return [];
});

const genericFacilities = computed(() => {
  const source =
    reportData.value?.facilities ||
    reportData.value?.top_priority_facilities ||
    reportData.value?.affected_facilities ||
    reportData.value?.data?.facilities ||
    [];
  return Array.isArray(source) ? source : [];
});

const sumR07Values = (rows, field) =>
  rows.reduce((total, row) => total + (toNumber(row?.[field]) || 0), 0);

const normalizeR07CostItem = (item) => ({
  ...item,
  name:
    item.item_name_ar ||
    item.item_name_en ||
    item.name ||
    item.item_name ||
    "بند تكلفة",
  quantity: item.qty ?? item.quantity ?? null,
  unit: item.unit_ar || item.unit_en || item.unit || unknownText(),
  unit_rate_usd: item.unit_rate_usd ?? item.unit_price ?? item.price ?? 0,
  immediate_usd: item.immediate_usd ?? item.immediate ?? 0,
  short_term_usd: item.short_term_usd ?? item.short_term ?? 0,
  medium_term_usd: item.medium_term_usd ?? item.medium_term ?? 0,
  total_usd: item.total_usd ?? item.total ?? item.total_cost ?? 0,
});

const buildR07CostGroups = (costItems) => {
  if (!Array.isArray(costItems)) return [];

  const groups = new Map();

  costItems.forEach((rawItem, index) => {
    const item = normalizeR07CostItem(rawItem || {});
    const code = String(item.group_code || "OTHER").trim() || "OTHER";

    if (!groups.has(code)) {
      groups.set(code, {
        code,
        name:
          item.group_name_ar ||
          item.group_name_en ||
          costName(code) ||
          "احتياجات أخرى",
        items: [],
      });
    }

    groups.get(code).items.push({
      ...item,
      line_no: item.line_no ?? index + 1,
    });
  });

  return [...groups.values()].map((group) => {
    const items = [...group.items].sort(
      (a, b) => (toNumber(a.line_no) || 0) - (toNumber(b.line_no) || 0)
    );

    return {
      ...group,
      items,
      immediate_usd: sumR07Values(items, "immediate_usd"),
      short_term_usd: sumR07Values(items, "short_term_usd"),
      medium_term_usd: sumR07Values(items, "medium_term_usd"),
      total_usd: sumR07Values(items, "total_usd"),
    };
  });
};

const r07Facilities = computed(() => {
  if (selectedReport.value?.id !== "R07") return [];

  const rows =
    r07BudgetPayload.value?.facilities ||
    r07BudgetPayload.value?.data?.facilities ||
    reportData.value?.facilities ||
    reportData.value?.facility_distribution?.rows ||
    [];

  if (!Array.isArray(rows)) return [];

  return rows
    .map((facility, index) => {
      const costItems = Array.isArray(facility?.cost_items)
        ? facility.cost_items.map(normalizeR07CostItem)
        : [];

      const costGroups = buildR07CostGroups(facility?.cost_items || []);
      const suppliedCostSummary = facility?.cost_items_summary || {};

      const calculatedCostSummary = {
        items_count: costItems.length,
        immediate_usd: sumR07Values(costItems, "immediate_usd"),
        short_term_usd: sumR07Values(costItems, "short_term_usd"),
        medium_term_usd: sumR07Values(costItems, "medium_term_usd"),
        total_usd: sumR07Values(costItems, "total_usd"),
      };

      const costItemsSummary = {
        items_count:
          toNumber(suppliedCostSummary.items_count) ??
          calculatedCostSummary.items_count,
        immediate_usd:
          toNumber(suppliedCostSummary.immediate_usd) ??
          calculatedCostSummary.immediate_usd,
        short_term_usd:
          toNumber(suppliedCostSummary.short_term_usd) ??
          calculatedCostSummary.short_term_usd,
        medium_term_usd:
          toNumber(suppliedCostSummary.medium_term_usd) ??
          calculatedCostSummary.medium_term_usd,
        total_usd:
          toNumber(suppliedCostSummary.total_usd) ??
          calculatedCostSummary.total_usd,
      };

      const estimatedRehabCost =
        toNumber(facility?.estimated_rehab_cost) ??
        toNumber(facility?.rehab_cost_usd) ??
        costItemsSummary.total_usd;

      const budgetGap =
        toNumber(facility?.budget_gap) ??
        toNumber(facility?.monthly_gap_usd) ??
        toNumber(facility?.monthly_gap_yer) ??
        0;

      return {
        ...facility,
        facility_id:
          facility?.health_facility_id ??
          facility?.ref_facility_id ??
          index + 1,
        facility_name:
          facility?.facility_name_ar ||
          facility?.facility_name ||
          facility?.facility_name_en ||
          `مرفق رقم ${index + 1}`,
        required_budget:
          toNumber(facility?.required_budget) ??
          toNumber(facility?.required_budget_yer) ??
          0,
        current_budget:
          toNumber(facility?.current_budget) ??
          toNumber(facility?.current_budget_yer) ??
          0,
        budget_gap: budgetGap,
        gap_rate: toNumber(facility?.gap_rate) ?? 0,
        estimated_rehab_cost: estimatedRehabCost,
        total_required_cost:
          toNumber(facility?.total_required_cost) ??
          estimatedRehabCost + budgetGap,
        cost_items: costItems,
        cost_items_summary: costItemsSummary,
        cost_groups: costGroups,
      };
    })
    .sort(
      (a, b) =>
        (toNumber(b.estimated_rehab_cost) || 0) -
        (toNumber(a.estimated_rehab_cost) || 0)
    );
});

const r07FacilityTotals = computed(() => {
  if (selectedReport.value?.id !== "R07") return {};

  const summary = r07BudgetPayload.value?.summary || {};
  const legacyTotals = reportData.value?.facility_distribution?.totals || {};
  const rows = r07Facilities.value;

  const requiredBudget =
    toNumber(summary.required_budget) ??
    toNumber(legacyTotals.required_budget) ??
    toNumber(legacyTotals.required_budget_yer) ??
    sumR07Values(rows, "required_budget");

  const currentBudget =
    toNumber(summary.current_budget) ??
    toNumber(legacyTotals.current_budget) ??
    toNumber(legacyTotals.current_budget_yer) ??
    sumR07Values(rows, "current_budget");

  const budgetGap =
    toNumber(summary.budget_gap) ??
    toNumber(legacyTotals.budget_gap) ??
    toNumber(legacyTotals.monthly_gap_yer) ??
    sumR07Values(rows, "budget_gap");

  return {
    required_budget: requiredBudget,
    current_budget: currentBudget,
    budget_gap: budgetGap,
    gap_rate:
      toNumber(summary.gap_rate) ??
      (requiredBudget > 0 ? (budgetGap / requiredBudget) * 100 : 0),
    estimated_rehab_cost:
      toNumber(summary.estimated_rehab_cost) ??
      toNumber(legacyTotals.estimated_rehab_cost) ??
      toNumber(legacyTotals.rehab_cost_usd) ??
      sumR07Values(rows, "estimated_rehab_cost"),
    total_required_cost: sumR07Values(rows, "total_required_cost"),
  };
});

const r07PhasedTotals = computed(() => ({
  immediate_usd: r07Facilities.value.reduce(
    (total, facility) =>
      total + (toNumber(facility.cost_items_summary?.immediate_usd) || 0),
    0
  ),
  short_term_usd: r07Facilities.value.reduce(
    (total, facility) =>
      total + (toNumber(facility.cost_items_summary?.short_term_usd) || 0),
    0
  ),
  medium_term_usd: r07Facilities.value.reduce(
    (total, facility) =>
      total + (toNumber(facility.cost_items_summary?.medium_term_usd) || 0),
    0
  ),
  total_usd: r07Facilities.value.reduce(
    (total, facility) =>
      total + (toNumber(facility.cost_items_summary?.total_usd) || 0),
    0
  ),
}));

const r07Currency = computed(
  () =>
    r07BudgetPayload.value?.summary?.currency_symbol ||
    r07Facilities.value[0]?.currency_symbol ||
    "USD"
);

const formatR07Currency = (value) => formatCurrency(value, r07Currency.value);

const r07CurrencyRate = computed(
  () =>
    toNumber(r07BudgetPayload.value?.summary?.currency_rate) ??
    toNumber(r07Facilities.value[0]?.currency_rate) ??
    1
);

const formatR07Yer = (value) => {
  const amount = toNumber(value);
  return formatCurrency((amount ?? 0) * r07CurrencyRate.value, "YER");
};

const r07NeedLabels = {
  structural: "إنشائي",
  non_structural: "غير إنشائي",
  operational_services_er: "خدمات تشغيلية وطوارئ",
  operational_services: "خدمات تشغيلية",
  wash: "مياه وصرف صحي",
  medical_equipment: "معدات طبية",
  safety_security: "أمن وسلامة",
};

const formatR07Needs = (facility) => {
  const source = facility?.rehab_need_code || facility?.rehab_need_type || "";
  const values = String(source)
    .split(/[,،;|]+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .map(
      (item) => r07NeedLabels[item.toLowerCase()] || item.replaceAll("_", " ")
    );

  return values.length ? values.join("، ") : unknownText();
};

const genericIndicators = computed(() => {
  const source =
    reportData.value?.indicators ||
    reportData.value?.indicator_values ||
    reportData.value?.data?.indicators ||
    [];

  if (Array.isArray(source)) return source.map(normalizeIndicator);
  if (source && typeof source === "object") {
    return Object.entries(source).map(([code, value]) =>
      normalizeIndicator(
        value && typeof value === "object"
          ? { code, ...value }
          : { code, value }
      )
    );
  }
  return [];
});

const genericCostCategories = computed(() => {
  const source =
    reportData.value?.cost_categories ??
    reportData.value?.cost_summary?.categories ??
    reportData.value?.costs?.categories ??
    reportData.value?.investment_by_category_and_phase?.rows ??
    [];

  return normalizeCostSource(source);
});

const genericInterventions = computed(() => {
  const source =
    reportData.value?.interventions ||
    reportData.value?.recommended_interventions ||
    reportData.value?.intervention_plan ||
    [];
  return Array.isArray(source) ? source : [];
});

const genericScenarios = computed(() => {
  const source =
    reportData.value?.scenarios ||
    reportData.value?.decision_scenarios ||
    reportData.value?.activated_scenarios ||
    [];
  return Array.isArray(source) ? source : [];
});

const genericNeeds = computed(() =>
  asArray(
    reportData.value?.needs ||
      reportData.value?.recommendations ||
      reportData.value?.critical_gaps
  )
);

const needsCostsExecutiveSummary = computed(() => {
  if (selectedReport.value?.id !== "R07" || !reportData.value) {
    return "";
  }

  const budgetSummary = r07BudgetPayload.value?.summary || {};

  if (
    !reportData.value?.package_summary &&
    (budgetSummary.current_budget !== undefined ||
      budgetSummary.required_budget !== undefined ||
      Array.isArray(r07BudgetPayload.value?.facilities))
  ) {
    const districtName = selectedDistrictCode.value || "المديرية المحددة";
    const facilitiesCount = r07Facilities.value.length;
    const currentBudget = r07FacilityTotals.value.current_budget || 0;
    const requiredBudget = r07FacilityTotals.value.required_budget || 0;
    const budgetGap = r07FacilityTotals.value.budget_gap || 0;
    const gapRate = r07FacilityTotals.value.gap_rate || 0;
    const rehabCost = r07FacilityTotals.value.estimated_rehab_cost || 0;

    return (
      `يعرض هذا التقرير احتياجات وتكاليف ${formatNumber(
        facilitiesCount
      )} مرفقاً صحياً في ${districtName}. ` +
      `بلغت الميزانية الحالية ${formatR07Currency(currentBudget)} مقابل ` +
      `ميزانية مطلوبة قدرها ${formatR07Currency(requiredBudget)}، ` +
      `بفجوة تمويلية تبلغ ${formatR07Currency(budgetGap)} ` +
      `وبنسبة ${formatNumber(gapRate)}%. ` +
      `كما بلغت التكلفة التقديرية لإعادة التأهيل ${formatR07Currency(
        rehabCost
      )}، موزعة على التدخلات الفورية وقصيرة ومتوسطة الأجل.`
    );
  }

  const packageSummary = reportData.value?.package_summary || {};
  const facilities = reportData.value?.facility_distribution?.rows || [];
  const categories =
    reportData.value?.investment_by_category_and_phase?.rows || [];

  const subtitle = reportData.value?.report?.subtitle;
  const districtName =
    subtitle || selectedDistrictCode.value || "المديرية المحددة";

  const facilitiesCount = Array.isArray(facilities) ? facilities.length : 0;
  const directProgram = toNumber(packageSummary.direct_program) ?? 0;
  const operatingGap = toNumber(packageSummary.operating_gap_3_years) ?? 0;
  const emergencyReserve = toNumber(packageSummary.emergency_reserve) ?? 0;
  const management =
    toNumber(packageSummary.management_monitoring_evaluation) ?? 0;
  const grandTotal = toNumber(packageSummary.grand_total) ?? 0;

  const highestCategory = Array.isArray(categories)
    ? [...categories]
        .filter((item) => toNumber(item?.total) !== null)
        .sort(
          (a, b) => (toNumber(b?.total) ?? 0) - (toNumber(a?.total) ?? 0)
        )[0]
    : null;

  let summary =
    `يعرض هذا التقرير الاحتياجات الاستثمارية والتشغيلية لـ${districtName}. ` +
    `يشمل التقرير ${formatNumber(facilitiesCount)} مرفقاً صحياً، ` +
    `وبلغت التكلفة المباشرة للبرنامج ${formatCurrency(directProgram)}، ` +
    `بينما بلغت فجوة التشغيل المقدرة لثلاث سنوات ${formatCurrency(
      operatingGap
    )}. `;

  if (highestCategory) {
    summary += `تمثل فئة «${
      highestCategory.category_name ||
      highestCategory.name ||
      highestCategory.category_code ||
      "غير محددة"
    }» أكبر بند استثماري بقيمة ${formatCurrency(highestCategory.total)}. `;
  }

  summary +=
    `بلغ احتياطي الطوارئ ${formatCurrency(emergencyReserve)}، ` +
    `وتكلفة الإدارة والمتابعة والتقييم ${formatCurrency(management)}، ` +
    `ليصل إجمالي الحزمة المقدرة إلى ${formatCurrency(grandTotal)}.`;

  return summary;
});

const needsCostsMethodologyNote = computed(() => {
  if (selectedReport.value?.id !== "R07" || !reportData.value) {
    return "";
  }

  const assumptions = reportData.value?.assumptions?.rows || [];

  if (!Array.isArray(assumptions) || assumptions.length === 0) {
    return r07Facilities.value.some((facility) => facility.cost_items.length)
      ? "تم تجميع بنود التكلفة لكل مرفق حسب فئة التدخل، ثم توزيعها على ثلاث مراحل زمنية: فورية من صفر إلى ثلاثة أشهر، وقصيرة الأجل من ثلاثة إلى اثني عشر شهراً، ومتوسطة الأجل من سنة إلى ثلاث سنوات. جميع تكاليف التأهيل والتوزيع المرحلي معروضة بالدولار الأمريكي وفق بيانات الاستجابة المرسلة من واجهة API."
      : "";
  }

  return assumptions
    .map((item) => {
      if (!item || typeof item !== "object") return "";

      const title = item.category ? `${item.category}: ` : "";
      return `${title}${item.description || ""}`.trim();
    })
    .filter(Boolean)
    .join(" ");
});

const reportFieldLabel = (key) => {
  const summaryLabels = {
    facilities_count: { ar: "عدد المرافق", en: "Number of facilities" },
    assessed_facilities: {
      ar: "المرافق المُقيَّمة",
      en: "Assessed facilities",
    },
    gesi_score: {
      ar: "درجة الإنصاف والنوع الاجتماعي (GESI)",
      en: "GESI score",
    },
    high_risk_facilities: {
      ar: "المرافق عالية المخاطر",
      en: "High-risk facilities",
    },
    indicators_count: { ar: "عدد المؤشرات", en: "Number of indicators" },
  };
  const language = String(locale.value).startsWith("ar") ? "ar" : "en";
  if (summaryLabels[key]) return summaryLabels[key][language];

  const labels = {
    total_facilities: "إجمالي المرافق",
    assessment_coverage: "نسبة تغطية التقييم",
    national_fis: "متوسط النتيجة المتكاملة الوطني",
    average_fis: "متوسط النتيجة المتكاملة",
    highest_fis: "أعلى النتيجة المتكاملة",
    lowest_fis: "أقل النتيجة المتكاملة",
    overall_score: "النتيجة الإجمالية",
    available_axes: "المحاور المتاحة",
    districts_count: "عدد المديريات",
    governorates_count: "عدد المحافظات",
    critical_facilities: "المرافق الحرجة",
    unsafe_buildings: "المباني غير الآمنة",
    high_service_risk: "مخاطر تشغيلية عالية",
    current_budget: "الميزانية الحالية",
    required_budget: "الميزانية المطلوبة",
    budget_gap: "فجوة الميزانية",
    gap_rate: "نسبة الفجوة",
    estimated_rehab_cost: "تكلفة إعادة التأهيل التقديرية",
    total_required_cost: "إجمالي التكلفة المطلوبة",
    currency_name: "اسم العملة",
    currency_symbol: "رمز العملة",
    currency_rate: "سعر الصرف",
    total_cost: "إجمالي التكلفة",
    funding_gap: "فجوة التمويل",
    direct_program: "التكلفة المباشرة للبرنامج",
    operating_gap_3_years: "فجوة التشغيل لثلاث سنوات",
    emergency_reserve_rate: "نسبة احتياطي الطوارئ",
    emergency_reserve: "احتياطي الطوارئ",
    table_3_total: "إجمالي الجدول الثالث",
    management_monitoring_evaluation: "الإدارة والمتابعة والتقييم",
    grand_total: "الإجمالي الكلي للحزمة",
    completeness_rate: "معدل اكتمال البيانات",
    missing_values: "القيم المفقودة",
  };

  return labels[key] || String(key).replaceAll("_", " ");
};

const formatReportValue = (value, type = "text") => {
  if (value === null || value === undefined || value === "") {
    return type === "number" ? formatNumber(0) : unknownText();
  }
  if (typeof value === "boolean")
    return value ? tr("common.yes") : tr("common.no");
  if (typeof value === "number") return formatNumber(value);
  if (Array.isArray(value)) {
    return value.length ? value.join("، ") : unknownText();
  }
  if (typeof value === "object") return JSON.stringify(value);
  return value;
};

const isNumericReportField = (key, value) => {
  if (typeof value === "number") return true;

  return /(^|_)(count|total|score|rate|percentage|percent|quantity|amount|cost|budget|gap|rank|fis|population|staff|coverage|average|highest|lowest|years|age|value)(_|$)/i.test(
    String(key || "")
  );
};

const formatSummaryValue = (key, value) => {
  if (selectedReport.value?.id === "R07") {
    if (["gap_rate", "emergency_reserve_rate"].includes(key)) {
      return `${formatNumber(value)}%`;
    }

    const currencyFields = [
      "current_budget",
      "required_budget",
      "budget_gap",
      "estimated_rehab_cost",
      "total_required_cost",
      "direct_program",
      "operating_gap_3_years",
      "emergency_reserve",
      "table_3_total",
      "management_monitoring_evaluation",
      "grand_total",
    ];

    if (currencyFields.includes(key)) {
      return formatR07Currency(value);
    }
  }

  return isNumericReportField(key, value)
    ? formatReportValue(value, "number")
    : formatReportValue(value, "text");
};

const statusFromScore = (value) => {
  const n = toNumber(value);
  if (n === null) return "gray";
  if (n >= 80) return "green";
  if (n >= 60) return "yellow";
  if (n >= 40) return "orange";
  return "red";
};

const statusLabel = (status) =>
  ({
    green: "جيد",
    yellow: "مقبول",
    orange: "يحتاج تحسين",
    red: "حرج",
    gray: unknownText(),
  }[status] || status);

const normalizeApiStatus = (value) => {
  const status = String(value || "")
    .trim()
    .toLowerCase();

  const aliases = {
    green: "green",
    good: "green",
    excellent: "green",
    met: "green",
    جيد: "green",
    ممتاز: "green",

    yellow: "yellow",
    fair: "yellow",
    acceptable: "yellow",
    مقبول: "yellow",
    جزئي: "yellow",

    orange: "orange",
    warning: "orange",
    moderate: "orange",
    متوسط: "orange",
    "يحتاج تحسين": "orange",

    red: "red",
    critical: "red",
    poor: "red",
    bad: "red",
    حرج: "red",
    ضعيف: "red",

    gray: "gray",
    grey: "gray",
    unavailable: "gray",
    // tr("common.notAvailable"): "gray",
  };

  return aliases[status] || "gray";
};

const apiRagClass = (item) => {
  const status = normalizeApiStatus(
    item?.status ?? item?.rag ?? item?.color ?? item?.status_code
  );

  return `rag-${status}`;
};

const apiStatusLabel = (item) => {
  if (item?.status_label) return item.status_label;

  return statusLabel(
    normalizeApiStatus(item?.status ?? item?.rag ?? item?.color)
  );
};

const toggleCostCategory = (key) => {
  const value = String(key);

  if (expandedCostCategories.value.includes(value)) {
    expandedCostCategories.value = expandedCostCategories.value.filter(
      (item) => item !== value
    );
  } else {
    expandedCostCategories.value.push(value);
  }
};

const extractList = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;

  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
    if (Array.isArray(payload?.data?.[key])) return payload.data[key];
  }

  return [];
};

const loadReportFilters = async () => {
  try {
    loadingFilters.value = true;
    reportError.value = "";

    const [governoratesResponse, districtsResponse] = await Promise.all([
      api.get("/ref-governorates"),
      api.get("/ref-districts"),
    ]);

    governorates.value = extractList(governoratesResponse.data, [
      "governorates",
    ]);
    districts.value = extractList(districtsResponse.data, ["districts"]);
  } catch (error) {
    console.error("Load report filters error:", error);
    reportError.value =
      error.response?.data?.message || tr("common.filtersLoadError");
  } finally {
    loadingFilters.value = false;
  }
};

const handleGovernorateChange = () => {
  selectedDistrictCode.value = "";
  selectedScenarioDistrictCode.value = "";
  reportData.value = null;
  reportError.value = "";

  // R01 و R04 يعرضان نتيجة المحافظة مباشرة، ثم يمكن تضييق R01 بالمديرية.
  // أما R07 وبقية تقارير المديريات فتنتظر اختيار المديرية.
  if (["R01", "R04"].includes(selectedReport.value?.id)) {
    loadSelectedReport();
  }
};

const buildReportUrl = (report) => {
  if (!report?.api) return null;

  if (administrativeLevelReportIds.includes(report.id)) {
    const params = new URLSearchParams(buildAdministrativeReportParams());
    const query = params.toString();

    // توافق انتقالي مع المسارات القديمة إلى أن يتم اعتماد query params بالكامل.
    if (report.id === "R04" && selectedGovernorateId.value) {
      return `${report.api}/${encodeURIComponent(
        selectedGovernorateId.value
      )}?${query}`;
    }

    if (report.id === "R06" && selectedAxis.value) {
      return `${report.api}/${encodeURIComponent(selectedAxis.value)}?${query}`;
    }

    if (["R07", "R10"].includes(report.id) && selectedDistrictCode.value) {
      return `${report.api}/${encodeURIComponent(
        selectedDistrictCode.value
      )}?${query}`;
    }

    return `${report.api}?${query}`;
  }

  switch (report.id) {
    case "R01": {
      if (!selectedGovernorateId.value) return null;

      const params = new URLSearchParams({
        governorate_id: String(selectedGovernorateId.value),
      });

      if (selectedDistrictCode.value) {
        params.set("district_code", String(selectedDistrictCode.value));
      }

      return `${report.api}?${params.toString()}`;
    }
    case "R04":
      return selectedGovernorateId.value
        ? `${report.api}/${encodeURIComponent(selectedGovernorateId.value)}`
        : null;
    case "R05":
    case "R07":
    case "R08":
    case "R10":
      return selectedDistrictCode.value
        ? `${report.api}/${encodeURIComponent(selectedDistrictCode.value)}`
        : null;
    case "R06":
      return selectedAxis.value
        ? `${report.api}/${encodeURIComponent(selectedAxis.value)}`
        : null;
    case "R09":
      if (scenarioLevel.value === "facility") {
        return selectedScenarioFacilityId.value
          ? `${report.api}/facility/${encodeURIComponent(
              selectedScenarioFacilityId.value
            )}`
          : null;
      }

      return selectedScenarioDistrictCode.value
        ? `${report.api}/district/${encodeURIComponent(
            selectedScenarioDistrictCode.value
          )}`
        : null;
    default:
      return report.api;
  }
};

const unwrapReportPayload = (responseData) => {
  const hasTopLevelReportFields =
    responseData &&
    typeof responseData === "object" &&
    !Array.isArray(responseData) &&
    (responseData.axis !== undefined ||
      responseData.axis_name !== undefined ||
      responseData.score !== undefined ||
      responseData.executive_summary !== undefined ||
      responseData.indicator_summary !== undefined ||
      responseData.weakest_facilities !== undefined ||
      responseData.recommendations !== undefined ||
      responseData.methodology_note !== undefined ||
      responseData.package_summary !== undefined ||
      responseData.facility_distribution !== undefined);

  if (hasTopLevelReportFields) return responseData;

  return responseData?.data && !Array.isArray(responseData.data)
    ? responseData.data
    : responseData;
};

const loadSelectedReport = async () => {
  if (!selectedReport.value) return;

  const url = buildReportUrl(selectedReport.value);
  if (!url) {
    reportData.value = null;
    return;
  }

  try {
    loadingReport.value = true;
    reportError.value = "";
    reportData.value = null;

    if (selectedReport.value.id === "R07" && selectedReport.value.detailsApi) {
      const administrativeParams = buildAdministrativeReportParams();
      const [packageResponse, detailsResponse] = await Promise.all([
        api.get(url),
        api.get(selectedReport.value.detailsApi, {
          params: administrativeParams,
        }),
      ]);

      const packagePayload = unwrapReportPayload(packageResponse.data) || {};
      const detailsPayload = unwrapReportPayload(detailsResponse.data) || {};

      reportData.value = {
        ...(typeof packagePayload === "object" ? packagePayload : {}),
        rehab_budget_details: detailsPayload,
      };

      return;
    }

    const { data } = await api.get(url);
    reportData.value = unwrapReportPayload(data);
  } catch (error) {
    console.error("Load selected report error:", error);
    reportData.value = null;
    reportError.value =
      error.response?.data?.message || tr("common.reportLoadError");
  } finally {
    loadingReport.value = false;
  }
};

const resetScenarioSelection = () => {
  selectedScenarioFacilityId.value = "";
  selectedScenarioDistrictCode.value = "";
  reportData.value = null;
};

const loadFacilities = async (options = {}) => {
  try {
    loadingFacilities.value = true;
    reportError.value = "";
    facilities.value = [];

    const params = {};

    if (options.governorateId) {
      params.governorate_id = String(options.governorateId);
    }

    if (options.districtCode) {
      params.district_code = String(options.districtCode);
    }

    const response = await api.get("/health-facilities", { params });

    const payload = response.data;

    let source = [];

    if (Array.isArray(payload)) {
      source = payload;
    } else if (Array.isArray(payload?.data)) {
      source = payload.data;
    } else if (Array.isArray(payload?.data?.data)) {
      source = payload.data.data;
    } else if (Array.isArray(payload?.facilities)) {
      source = payload.facilities;
    } else if (Array.isArray(payload?.data?.facilities)) {
      source = payload.data.facilities;
    }

    facilities.value = source
      .map((facility) => {
        const id =
          facility.id ??
          facility.facility_name ??
          facility.health_facility_id ??
          facility.survey_id ??
          facility.hf_code;

        const name =
          facility.facility_name_ar ??
          facility.facility_name ??
          facility.name_ar ??
          facility.name ??
          facility.hf_name_ar ??
          `مرفق رقم ${id}`;

        return {
          ...facility,
          id,
          name,
        };
      })
      .filter((facility) => {
        return (
          facility.id !== null &&
          facility.id !== undefined &&
          facility.id !== ""
        );
      });

    if (facilities.value.length > 0 && options.loadProfile !== false) {
      selectedFacilityId.value = facilities.value[0].id;
      await loadFacilityProfile();
    } else if (facilities.value.length > 0) {
      selectedFacilityId.value = "";
    } else {
      selectedFacilityId.value = "";
      reportError.value = tr("common.noFacilitiesInApi");
    }
  } catch (error) {
    console.error("Load facilities error:", error);

    facilities.value = [];
    selectedFacilityId.value = "";

    reportError.value =
      error.response?.data?.message ||
      error.message ||
      tr("common.facilitiesLoadError");
  } finally {
    loadingFacilities.value = false;
  }
};

const loadFacilityProfile = async () => {
  if (
    selectedFacilityId.value === null ||
    selectedFacilityId.value === undefined ||
    selectedFacilityId.value === ""
  ) {
    reportData.value = null;
    return;
  }

  try {
    loadingReport.value = true;
    reportError.value = "";
    reportData.value = null;

    const { data } = await api.get(
      `/reports/facility-profile/${selectedFacilityId.value}`
    );

    reportData.value = data?.data || data;
  } catch (error) {
    console.error("Load facility profile error:", error);

    reportData.value = null;

    reportError.value =
      error.response?.data?.message || tr("common.facilityProfileLoadError");
  } finally {
    loadingReport.value = false;
  }
};

const openReport = async (report) => {
  invalidateInventoryRequest();
  selectedReport.value = report;
  modalOpen.value = true;
  reportData.value = null;
  reportError.value = "";
  selectedFacilityId.value = "";
  expandedCostCategories.value = [];

  selectedGovernorateId.value = "";
  selectedDistrictCode.value = "";
  selectedAxis.value = "INF";
  selectedReportLevel.value = report.id === "R04" ? "governorate" : "national";
  selectedAdministrativeFacilityId.value = "";
  scenarioLevel.value = "facility";
  selectedScenarioFacilityId.value = "";
  selectedScenarioDistrictCode.value = "";

  if (!report.isApiReport) return;

  if (report.id === "R03") {
    await loadFacilities();
    return;
  }

  if (administrativeLevelReportIds.includes(report.id)) {
    selectedEquipmentAvailability.value = "";
    selectedStaffAvailability.value = "";
    selectedMedicineStockLevel.value = "";
    selectedEquipmentFacilityId.value = "";
    selectedStaffFacilityId.value = "";
    selectedMedicineFacilityId.value = "";
    facilities.value = [];

    await loadReportFilters();
    return;
  }

  if (report.id === "R13") {
    reportData.value = null;
    reportError.value = "";
    selectedEquipmentAvailability.value = "";
    selectedEquipmentFacilityId.value = "";

    // تحميل المرافق أولاً
    await loadFacilities();

    // اختيار أول مرفق
    if (facilities.value.length > 0) {
      selectedEquipmentFacilityId.value = facilities.value[0].id;
      reportError.value = "";

      await loadMedicalEquipmentReport();
    }

    return;
  }
  if (report.id === "R14") {
    selectedStaffAvailability.value = "";
    selectedStaffFacilityId.value = "";
    await loadFacilities();

    if (facilities.value.length > 0) {
      selectedStaffFacilityId.value = facilities.value[0].id;
      await loadStaffCadreReport();
    }
    return;
  }

  if (report.id === "R15") {
    selectedMedicineStockLevel.value = "";
    selectedMedicineFacilityId.value = "";
    await loadFacilities();

    if (facilities.value.length > 0) {
      selectedMedicineFacilityId.value = facilities.value[0].id;
      await loadEssentialMedicinesReport();
    }
    return;
  }

  if (
    report.requiresGovernorate ||
    report.requiresDistrict ||
    report.requiresScenarioLevel
  ) {
    await loadReportFilters();
  }

  if (report.requiresScenarioLevel) {
    await loadFacilities();
    selectedFacilityId.value = "";
    reportData.value = null;
    return;
  }

  if (report.requiresGovernorate || report.requiresDistrict) return;

  await loadSelectedReport();
};

const closeReport = () => {
  invalidateInventoryRequest();
  modalOpen.value = false;
  selectedReport.value = null;
  reportData.value = null;
  reportError.value = "";
  selectedFacilityId.value = "";
  expandedCostCategories.value = [];
  selectedGovernorateId.value = "";
  selectedDistrictCode.value = "";
  selectedAxis.value = "INF";
  selectedReportLevel.value = "national";
  selectedAdministrativeFacilityId.value = "";
  scenarioLevel.value = "facility";
  selectedScenarioFacilityId.value = "";
  selectedScenarioDistrictCode.value = "";
  exportingFormat.value = "";
};

const buildExportParams = (report) => {
  const params = administrativeLevelReportIds.includes(report.id)
    ? { ...buildAdministrativeReportParams() }
    : {};

  if (report.id === "R01") {
    if (selectedGovernorateId.value) {
      params.governorate_id = String(selectedGovernorateId.value);
    }

    if (selectedDistrictCode.value) {
      params.district_code = String(selectedDistrictCode.value);
    }
  }

  if (report.id === "R03" && selectedFacilityId.value) {
    params.facility = String(selectedFacilityId.value);
  }

  if (["R05", "R07", "R10"].includes(report.id) && selectedDistrictCode.value) {
    // يدعم اسم المعامل الجديد والقديم أثناء انتقال مسارات التقارير.
    params.district = String(selectedDistrictCode.value);
    params.district_code = String(selectedDistrictCode.value);
  }

  if (report.id === "R06" && selectedAxis.value) {
    params.axis = String(selectedAxis.value);
  }

  if (report.id === "R13" && selectedEquipmentAvailability.value) {
    params.availability = String(selectedEquipmentAvailability.value);
  }

  if (report.id === "R14" && selectedStaffAvailability.value) {
    params.availability = String(selectedStaffAvailability.value);
  }

  if (report.id === "R15" && selectedMedicineStockLevel.value) {
    params.stock_level = String(selectedMedicineStockLevel.value);
  }

  return params;
};

const exportErrorMessage = async (error) => {
  const payload = error?.response?.data;

  if (payload instanceof Blob) {
    try {
      const text = await payload.text();
      const json = JSON.parse(text);
      return json?.message || text;
    } catch {
      return "تعذر تصدير التقرير.";
    }
  }

  return payload?.message || error?.message || "تعذر تصدير التقرير.";
};

const downloadReport = async (format) => {
  const report = selectedReport.value;
  const exportKey = reportExportKeys[report?.id];

  if (!report || !exportKey || !reportData.value || exportingFormat.value) {
    return;
  }

  try {
    exportingFormat.value = format;
    reportError.value = "";

    const response = await api.get(`/reports/export/${exportKey}/${format}`, {
      params: buildExportParams(report),
      responseType: "blob",
    });

    const contentType =
      response.headers?.["content-type"] || "application/octet-stream";
    const blobUrl = URL.createObjectURL(
      new Blob([response.data], { type: contentType })
    );
    const link = document.createElement("a");

    link.href = blobUrl;
    link.download = `${report.id}-${new Date()
      .toISOString()
      .slice(0, 10)}.${format}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Report export error:", error);
    reportError.value = await exportErrorMessage(error);
  } finally {
    exportingFormat.value = "";
  }
};

const PRINT_STYLE_ID = "reports-page-global-print-style";
const PRINT_MODE_CLASS = "reports-page-print-mode";
const PRINT_ANCESTOR_CLASS = "reports-page-print-ancestor";
const PRINT_TARGET_CLASS = "reports-page-print-target";

const cleanupPrintLayout = () => {
  document.getElementById(PRINT_STYLE_ID)?.remove();
  document.body.classList.remove(PRINT_MODE_CLASS);

  document
    .querySelectorAll(`.${PRINT_ANCESTOR_CLASS}`)
    .forEach((element) => element.classList.remove(PRINT_ANCESTOR_CLASS));

  document
    .querySelectorAll(`.${PRINT_TARGET_CLASS}`)
    .forEach((element) => element.classList.remove(PRINT_TARGET_CLASS));
};

const preparePrintLayout = () => {
  cleanupPrintLayout();

  const reportElement = document.querySelector(".print-report");
  if (!reportElement) return false;

  const style = document.createElement("style");
  style.id = PRINT_STYLE_ID;
  style.textContent = `
    @page {
      size: 297mm 210mm;
      margin: 9mm 8mm 11mm;
    }

    @media print {
      html,
      body {
        width: 100% !important;
        min-width: 0 !important;
        max-width: none !important;
        height: auto !important;
        min-height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: visible !important;
        background: #ffffff !important;
      }

      body.${PRINT_MODE_CLASS} > :not(.${PRINT_ANCESTOR_CLASS}) {
        display: none !important;
      }

      body.${PRINT_MODE_CLASS} .${PRINT_ANCESTOR_CLASS} {
        position: static !important;
        inset: auto !important;
        display: block !important;
        visibility: visible !important;
        width: 100% !important;
        min-width: 0 !important;
        max-width: none !important;
        height: auto !important;
        min-height: 0 !important;
        max-height: none !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: visible !important;
        transform: none !important;
      }

      body.${PRINT_MODE_CLASS}
        .${PRINT_ANCESTOR_CLASS}
        > :not(.${PRINT_ANCESTOR_CLASS}):not(.${PRINT_TARGET_CLASS}) {
        display: none !important;
      }

      body.${PRINT_MODE_CLASS} .${PRINT_TARGET_CLASS} {
        position: static !important;
        inset: auto !important;
        display: block !important;
        visibility: visible !important;
        width: 281mm !important;
        min-width: 281mm !important;
        max-width: 281mm !important;
        height: auto !important;
        min-height: 0 !important;
        max-height: none !important;
        margin: 0 auto !important;
        padding: 0 !important;
        overflow: visible !important;
        transform: none !important;
        box-sizing: border-box !important;
      }

      body.${PRINT_MODE_CLASS} .${PRINT_TARGET_CLASS},
      body.${PRINT_MODE_CLASS} .${PRINT_TARGET_CLASS} * {
        visibility: visible !important;
      }
    }
  `;

  document.head.appendChild(style);
  document.body.classList.add(PRINT_MODE_CLASS);
  reportElement.classList.add(PRINT_TARGET_CLASS);

  let ancestor = reportElement.parentElement;

  while (ancestor && ancestor !== document.body) {
    ancestor.classList.add(PRINT_ANCESTOR_CLASS);
    ancestor = ancestor.parentElement;
  }

  return true;
};

const printReport = async () => {
  expandedCostCategories.value = detailedCostCategories.value.map((category) =>
    String(category.code || category.name)
  );

  await nextTick();

  if (!preparePrintLayout()) return;

  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  await new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });

  window.addEventListener("afterprint", cleanupPrintLayout, { once: true });

  try {
    window.print();
  } catch (error) {
    cleanupPrintLayout();
    throw error;
  }
};
</script>

<style scoped>
/* =========================================
   الرأس الرسمي المشترك لجميع التقارير
========================================= */

.official-report-header {
  width: 100%;
  margin: 0 0 28px;
  padding: 0;
  background: #ffffff;
  direction: rtl;
  break-inside: avoid;
  page-break-inside: avoid;
}

.report-logos {
  min-height: 105px;
  display: grid;
  grid-template-columns: minmax(150px, 1fr) minmax(280px, 2fr) minmax(
      150px,
      1fr
    );
  align-items: center;
  gap: 22px;
  padding: 14px 8px 12px;
}

.report-logo-side,
.report-logo-center {
  min-width: 0;
  display: flex;
  align-items: center;
}

.report-logo-side:first-child {
  justify-content: flex-start;
}

.report-logo-side:last-child {
  justify-content: flex-end;
}

.report-logo-center {
  justify-content: center;
}

.report-logos img {
  display: block;
  max-width: 100%;
  object-fit: contain;
}

.yemen-logo {
  width: 230px;
  max-height: 78px;
}

.unops-logo {
  width: 320px;
  max-height: 88px;
}

.afcar-logo {
  width: 145px;
  max-height: 76px;
}

.official-header-line {
  width: 100%;
  height: 3px;
  margin: 0;
  background: linear-gradient(to left, #123f74 0%, #009ad8 50%, #123f74 100%);
}

.official-report-title {
  padding: 20px 18px 16px;
  text-align: center;
  border-bottom: 1px solid #d8e1eb;
}

.report-system-name {
  display: block;
  margin-bottom: 8px;
  color: #52647a;
  font-size: 14px;
  font-weight: 700;
}

.official-report-title h1 {
  margin: 0;
  color: #102f54;
  font-size: 25px;
  line-height: 1.5;
  font-weight: 900;
}

.report-reference {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 28px;
  flex-wrap: wrap;
  margin-top: 12px;
  color: #64748b;
  font-size: 12px;
}

.report-reference span {
  position: relative;
}

.report-reference span + span::before {
  content: "";
  position: absolute;
  right: -15px;
  top: 3px;
  width: 1px;
  height: 14px;
  background: #cbd5e1;
}

/* عنوان المشروع الموجود أسفل الرأس */
.pr-head {
  margin-bottom: 24px;
  padding: 12px 16px;
  text-align: center;
  background: #f5f8fc;
  border-right: 4px solid #123f74;
  border-radius: 4px;
}

.pr-head .sub {
  color: #40566f;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.7;
}

/* =========================================
   تنسيق جسم التقرير
========================================= */

.print-report {
  width: 100%;
  max-width: 1150px;
  min-height: 100%;
  margin: 0 auto;
  padding: 30px 38px 42px;
  box-sizing: border-box;
  background: #ffffff;
  color: #1e293b;
  direction: rtl;
  font-family: "Cairo";
  line-height: 1.8;
}

.print-report > h3,
.print-report h3 {
  margin: 30px 0 14px;
  padding: 9px 13px;
  color: #123f74;
  font-size: 17px;
  font-weight: 900;
  line-height: 1.5;
  background: #f3f7fb;
  border-right: 4px solid #009ad8;
  border-bottom: 1px solid #dbe5ef;
  break-after: avoid;
  page-break-after: avoid;
}

.print-report h4 {
  color: #173f69;
  font-size: 15px;
  font-weight: 800;
}

.print-report p {
  margin: 8px 0 14px;
  color: #334155;
  font-size: 14px;
  line-height: 2;
  text-align: justify;
}

.print-report ul {
  margin: 8px 0 18px;
  padding-right: 25px;
}

.print-report li {
  margin-bottom: 7px;
  color: #334155;
  font-size: 14px;
  line-height: 1.8;
}

.print-report table {
  width: 100%;
  margin: 12px 0 24px;
  border-collapse: collapse;
  border-spacing: 0;
  background: #ffffff;
  font-size: 13px;
  overflow: hidden;
}

.print-report thead {
  display: table-header-group;
}

.print-report thead th,
.print-report thead td {
  padding: 11px 10px;
  color: #ffffff;
  font-weight: 800;
  text-align: right;
  background: #123f74;
  border: 1px solid #31557d;
}

.print-report tbody td {
  padding: 10px;
  color: #334155;
  vertical-align: middle;
  border: 1px solid #d9e2ec;
}

.print-report tbody tr:nth-child(even) {
  background: #f8fafc;
}

.print-report tbody tr:hover {
  background: #eef6fb;
}

.print-report tbody tr td:first-child {
  font-weight: 700;
}

.table-scroll {
  width: 100%;
  overflow-x: visible;
}

.methodology-note {
  padding: 14px 16px;
  color: #475569 !important;
  background: #f8fafc;
  border: 1px solid #dbe3ed;
  border-right: 4px solid #64748b;
  border-radius: 5px;
}

.empty-section,
.empty-table {
  padding: 24px !important;
  color: #64748b;
  text-align: center;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
}

.reports-page {
  min-height: 100vh;
  background: #f4f6f9;
  color: #1a2433;
  padding: 28px;
  font-family: "Cairo";
}

.section-head {
  margin-bottom: 18px;
}

.section-head h2 {
  font-size: 24px;
  font-weight: 900;
  color: #1a2433;
  margin-bottom: 6px;
}

.section-head .desc {
  font-size: 13px;
  color: #4c5b6e;
  max-width: 900px;
}

.report-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
  gap: 14px;
}

.report-card {
  background: #fff;
  border: 1px solid #dde4ec;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 1px 2px rgba(13, 34, 64, 0.06);
  transition: 0.2s ease;
}

.report-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(13, 34, 64, 0.12);
}

.rc-top {
  display: flex;
  align-items: center;
  gap: 9px;
}

.rc-num {
  font-family: monospace;
  font-size: 13px;
  font-weight: 900;
  background: #15355c;
  color: #fff;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}

.report-card h4 {
  font-size: 15px;
  font-weight: 900;
  flex: 1;
  color: #0d2240;
}

.rc-goal {
  font-size: 12px;
  color: #4c5b6e;
  flex: 1;
}

.rc-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 11px;
  color: #7a8799;
}

.rc-meta span {
  background: #f8fafc;
  border: 1px solid #dde4ec;
  border-radius: 999px;
  padding: 3px 10px;
}

.btn {
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 800;
  background: #15355c;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 9px 16px;
  cursor: pointer;
  align-self: flex-start;
  margin-top: 4px;
}

.btn:hover {
  opacity: 0.9;
}

.btn.ghost {
  background: #fff;
  color: #15355c;
  border: 1px solid #c3cedb;
}

.btn.export-pdf {
  background: #b42318;
}

.btn.export-excel {
  background: #067647;
}

.m-foot .btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: rgba(10, 20, 38, 0.55);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  overflow-y: auto;
}

.modal {
  background: #fff;
  border-radius: 10px;
  width: 100%;
  max-width: 960px;
  box-shadow: 0 8px 24px rgba(13, 34, 64, 0.18);
  animation: fadeUp 0.2s ease;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(6px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

.m-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #dde4ec;
}

.m-head h3 {
  font-size: 17px;
  font-weight: 900;
  flex: 1;
  color: #0d2240;
}

.close-x {
  background: none;
  border: none;
  cursor: pointer;
  color: #7a8799;
  font-size: 20px;
  line-height: 1;
  padding: 4px;
}

.m-body {
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.m-foot {
  padding: 14px 20px;
  border-top: 1px solid #dde4ec;
  display: flex;
  gap: 10px;
  justify-content: flex-start;
}

.print-report {
  font-size: 13px;
}

.pr-head {
  text-align: center;
  border-bottom: 3px double #15355c;
  padding-bottom: 12px;
  margin-bottom: 16px;
}

.pr-head h2 {
  font-size: 20px;
  color: #15355c;
  margin-bottom: 4px;
}

.pr-head .sub {
  font-size: 12px;
  color: #4c5b6e;
}

.print-report h3 {
  font-size: 15px;
  color: #15355c;
  margin: 16px 0 8px;
  border-right: 4px solid #b8923e;
  padding-right: 9px;
}

.print-report table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin-bottom: 12px;
}

.print-report td {
  border: 1px solid #dde4ec;
  padding: 10px;
}

.print-report td:first-child {
  font-weight: 900;
  width: 180px;
}

.print-report p {
  line-height: 2;
  margin-bottom: 8px;
}

.print-report ul {
  padding-right: 22px;
  line-height: 2;
}

@media (max-width: 700px) {
  .reports-page {
    padding: 18px 12px;
  }

  .report-grid {
    grid-template-columns: 1fr;
  }

  .m-foot {
    flex-direction: column;
  }
}

/* ================= فلاتر وتقارير عامة ================= */
.report-filter-box {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  margin: 14px 0;
  background: #f8fafc;
  border: 1px solid #dde4ec;
  border-radius: 10px;
}

.report-filter-box label {
  min-width: 125px;
  color: #15355c;
  font-weight: 900;
}

.report-filter-box select {
  flex: 1;
  min-height: 42px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  font-family: inherit;
}

.report-filter-column {
  flex-direction: column;
  align-items: stretch;
}

.report-filter-column label {
  min-width: 0;
}

.administrative-level-filter {
  margin: 18px 0 22px;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #d7e1ec;
  border-right: 5px solid #15355c;
  border-radius: 10px;
}

.administrative-filter-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.administrative-filter-head strong,
.administrative-filter-head span {
  display: block;
}

.administrative-filter-head strong {
  color: #15355c;
  font-size: 14px;
}

.administrative-filter-head span {
  margin-top: 3px;
  color: #64748b;
  font-size: 11px;
}

.administrative-filter-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  align-items: end;
}

.administrative-filter-field {
  min-width: 0;
}

.administrative-filter-field label {
  display: block;
  margin-bottom: 6px;
  color: #334155;
  font-size: 12px;
  font-weight: 800;
}

.administrative-filter-field select {
  width: 100%;
  min-width: 0;
  min-height: 43px;
  padding: 8px 10px;
  color: #1e293b;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  font-family: inherit;
}

.administrative-filter-action .btn {
  width: 100%;
  min-height: 43px;
  margin: 0;
}

.administrative-filter-action .btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

@media screen and (max-width: 1100px) {
  .administrative-filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media screen and (max-width: 650px) {
  .administrative-filter-grid {
    grid-template-columns: 1fr;
  }
}

.generic-facilities-table {
  min-width: 850px;
}

.generic-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.generic-card {
  padding: 14px;
  background: #fff;
  border: 1px solid #dde4ec;
  border-radius: 9px;
}

.generic-card h4 {
  margin: 0 0 8px;
  color: #15355c;
}

.generic-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  color: #64748b;
  font-size: 11px;
}

/* ================= R03 ================= */
.facility-selector {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  margin-bottom: 18px;
  background: #f8fafc;
  border: 1px solid #dde4ec;
  border-radius: 10px;
}

.facility-selector label {
  font-weight: 900;
  color: #15355c;
  white-space: nowrap;
}

.facility-selector select {
  width: 100%;
  min-height: 42px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  font-family: inherit;
}

.loading-box,
.error-box,
.empty-section {
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  font-weight: 800;
}

.loading-box {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
}

.error-box {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
}

.empty-section {
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  color: #64748b;
}

.facility-report-header {
  text-align: center;
  padding-bottom: 16px;
  margin-bottom: 18px;
  border-bottom: 3px double #15355c;
}

.facility-report-header h1 {
  margin: 0 0 7px;
  color: #15355c;
  font-size: 22px;
  font-weight: 900;
}

.facility-subtitle {
  color: #334155;
  font-size: 14px;
  font-weight: 800;
}

.report-source {
  color: #64748b;
  font-size: 11px;
}

.identity-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.identity-card {
  min-height: 96px;
  padding: 14px;
  background: #fff;
  border: 1px solid #dde4ec;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.identity-label {
  margin-bottom: 6px;
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
}

.identity-card strong {
  color: #0f2747;
  font-size: 16px;
}

.fis-card {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.priority-card {
  background: #fff7ed;
  border-color: #fed7aa;
}

.key-findings-box {
  padding: 16px 18px;
  background: #fffaf0;
  border: 1px solid #f5dca6;
  border-right: 5px solid #b8923e;
  border-radius: 9px;
}

.table-scroll {
  width: 100%;
  overflow-x: visible;
}

.indicators-table {
  min-width: 900px;
}

.indicators-table th,
.cost-details-table th {
  padding: 10px;
  background: #15355c;
  color: #fff;
  border: 1px solid #365779;
  text-align: right;
}

.indicator-code {
  direction: ltr;
  text-align: center;
  font-family: monospace;
  font-weight: 900;
}

.standard-cell {
  min-width: 280px;
}

.indicator-value {
  font-weight: 900;
  text-align: center;
}

.rag-badge {
  display: inline-flex;
  min-width: 86px;
  justify-content: center;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 900;
}

.rag-green {
  background: #dcfce7;
  color: #166534;
}

.rag-yellow {
  background: #fef9c3;
  color: #854d0e;
}

.rag-orange {
  background: #ffedd5;
  color: #9a3412;
}

.rag-red {
  background: #fee2e2;
  color: #991b1b;
}

.rag-gray {
  background: #f1f5f9;
  color: #64748b;
}

.axis-summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.axis-summary-card {
  padding: 16px;
  background: #fff;
  border: 1px solid #dde4ec;
  border-radius: 10px;
}

.axis-summary-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.axis-score {
  min-width: 58px;
  height: 58px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
}

.score-green {
  background: #dcfce7;
  color: #166534;
}

.score-yellow {
  background: #fef9c3;
  color: #854d0e;
}

.score-orange {
  background: #ffedd5;
  color: #9a3412;
}

.score-red {
  background: #fee2e2;
  color: #991b1b;
}

.score-gray {
  background: #f1f5f9;
  color: #64748b;
}

.axis-progress {
  height: 7px;
  margin: 12px 0;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}

.axis-progress span {
  display: block;
  height: 100%;
  background: #15355c;
}

.axis-indicator-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px dashed #e2e8f0;
}

.axis-highlights,
.axis-recommendations {
  margin-top: 12px;
  padding: 10px;
  border-radius: 7px;
}

.axis-highlights {
  background: #f8fafc;
}

.axis-recommendations {
  background: #eff6ff;
}

.intervention-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.intervention-period-card {
  background: #fff;
  border: 1px solid #dde4ec;
  border-radius: 10px;
  overflow: hidden;
}

.intervention-period-title {
  padding: 10px 14px;
  background: #15355c;
  color: #fff;
  font-weight: 900;
}

.cost-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.cost-summary-card {
  min-height: 82px;
  padding: 14px;
  background: #fff;
  border: 1px solid #dde4ec;
  border-radius: 9px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.cost-summary-card span {
  line-height: 1.8;
  overflow-wrap: anywhere;
}

.total-cost-card {
  background: #15355c;
}

.total-cost-card span,
.total-cost-card strong {
  color: #fff;
}

.cost-category-section {
  margin-bottom: 13px;
}

.cost-category-title {
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #dde4ec;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
}

.cost-category-title > div {
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.cost-category-title strong {
  line-height: 1.8;
  overflow-wrap: anywhere;
}

.cost-category-title span {
  flex: 0 0 auto;
  white-space: nowrap;
}

.cost-details-table table {
  min-width: 1100px;
  margin-top: 8px;
}

.subtotal-row td {
  background: #edf3f8 !important;
  font-weight: 900;
}

.scenarios-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.scenario-card {
  padding: 14px;
  background: #fff;
  border: 1px solid #dde4ec;
  border-right: 5px solid #15355c;
  border-radius: 8px;
  display: grid;
  grid-template-columns: 52px 1fr;
  gap: 12px;
}

.scenario-code {
  height: 42px;
  background: #15355c;
  color: #fff;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
}

.scenario-action,
.methodology-note {
  margin-top: 7px;
  padding: 10px;
  background: #eff6ff;
  border-radius: 6px;
}

@media (max-width: 900px) {
  .identity-grid,
  .cost-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .axis-summary-grid,
  .intervention-grid,
  .generic-cards {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .identity-grid,
  .cost-summary-grid {
    grid-template-columns: 1fr;
  }

  .facility-selector {
    flex-direction: column;
    align-items: stretch;
  }
}

/* استجابة رأس التقرير للشاشات الصغيرة */
@media (max-width: 800px) {
  .report-logos {
    grid-template-columns: minmax(80px, 1fr) minmax(150px, 1.6fr) minmax(
        80px,
        1fr
      );
    gap: 10px;
    min-height: 76px;
    padding: 10px 4px;
  }

  .yemen-logo {
    width: 145px;
    max-height: 54px;
  }

  .unops-logo {
    width: 210px;
    max-height: 60px;
  }

  .afcar-logo {
    width: 90px;
    max-height: 50px;
  }

  .official-report-title {
    padding: 15px 8px 12px;
  }

  .official-report-title h1 {
    font-size: 19px;
  }

  .report-system-name {
    font-size: 12px;
  }

  .report-reference {
    gap: 16px;
    font-size: 11px;
  }

  .print-report {
    padding: 20px 14px 32px;
  }
}

/* إصلاح الطباعة: يطبع التقرير فقط بتنسيق رسمي */
@media print {
  @page {
    size: A4 portrait;
    margin: 12mm 10mm 14mm;
  }

  :global(html),
  :global(body) {
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #ffffff !important;
  }

  :global(body *) {
    visibility: hidden !important;
  }

  .modal-backdrop,
  .modal-backdrop *,
  .modal,
  .modal *,
  .m-body,
  .m-body *,
  .print-report,
  .print-report * {
    visibility: visible !important;
  }

  .modal-backdrop {
    position: absolute !important;
    inset: 0 !important;
    display: block !important;
    width: 100% !important;
    min-height: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
    background: #ffffff !important;
    backdrop-filter: none !important;
  }

  .modal {
    position: static !important;
    width: 100% !important;
    max-width: none !important;
    max-height: none !important;
    margin: 0 !important;
    overflow: visible !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: #ffffff !important;
    box-shadow: none !important;
  }

  .m-body {
    max-height: none !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
    background: #ffffff !important;
  }

  .print-report {
    width: 100% !important;
    max-width: none !important;
    min-height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #1e293b !important;
    background: #ffffff !important;
    box-shadow: none !important;
    font-size: 10px !important;
    line-height: 1.65 !important;
    print-color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
  }

  .official-report-header {
    margin: 0 0 16px !important;
    padding: 0 !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  .report-logos {
    min-height: 70px !important;
    grid-template-columns: minmax(120px, 1fr) minmax(220px, 2fr) minmax(
        100px,
        1fr
      ) !important;
    gap: 12px !important;
    padding: 4px 4px 8px !important;
  }

  .yemen-logo {
    width: 175px !important;
    max-height: 58px !important;
  }

  .unops-logo {
    width: 250px !important;
    max-height: 64px !important;
  }

  .afcar-logo {
    width: 105px !important;
    max-height: 55px !important;
  }

  .official-header-line {
    height: 2px !important;
    background: #123f74 !important;
    print-color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
  }

  .official-report-title {
    padding: 11px 8px 9px !important;
  }

  .official-report-title h1 {
    margin: 0 !important;
    color: #102f54 !important;
    font-size: 18px !important;
    line-height: 1.45 !important;
  }

  .report-system-name {
    margin-bottom: 3px !important;
    font-size: 10px !important;
  }

  .report-reference {
    gap: 22px !important;
    margin-top: 5px !important;
    font-size: 8.5px !important;
  }

  .pr-head {
    margin: 0 0 12px !important;
    padding: 6px 9px !important;
    background: #f5f8fc !important;
    border-right: 3px solid #123f74 !important;
    print-color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
  }

  .pr-head .sub {
    font-size: 9px !important;
  }

  .print-report h1,
  .print-report h2,
  .print-report h3,
  .print-report h4,
  .print-report p {
    orphans: 3;
    widows: 3;
  }

  .print-report h3 {
    margin: 15px 0 7px !important;
    padding: 5px 8px !important;
    color: #123f74 !important;
    background: #f3f7fb !important;
    border-right: 3px solid #009ad8 !important;
    border-bottom: 1px solid #dbe5ef !important;
    font-size: 12px !important;
    line-height: 1.45 !important;
    break-after: avoid !important;
    page-break-after: avoid !important;
    print-color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
  }

  .print-report h4 {
    font-size: 10.5px !important;
  }

  .print-report p,
  .print-report li {
    font-size: 9px !important;
    line-height: 1.65 !important;
  }

  .print-report table {
    width: 100% !important;
    min-width: 0 !important;
    margin: 6px 0 12px !important;
    border-collapse: collapse !important;
    font-size: 8px !important;
  }

  .print-report thead {
    display: table-header-group !important;
  }

  .print-report thead th,
  .print-report thead td {
    padding: 5px 4px !important;
    color: #ffffff !important;
    background: #123f74 !important;
    border: 1px solid #31557d !important;
    print-color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
  }

  .print-report tbody td {
    padding: 4px !important;
    border: 1px solid #d9e2ec !important;
  }

  .print-report tbody tr:nth-child(even) {
    background: #f8fafc !important;
    print-color-adjust: exact !important;
    -webkit-print-color-adjust: exact !important;
  }

  .table-scroll,
  .cost-details-table {
    display: block !important;
    width: 100% !important;
    overflow: visible !important;
  }

  .cost-details-table table,
  .indicators-table,
  .specialized-axis-table,
  .generic-facilities-table {
    min-width: 0 !important;
  }

  .identity-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
    gap: 6px !important;
  }

  .axis-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 7px !important;
  }

  .cost-summary-grid,
  .intervention-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 6px !important;
  }

  .identity-card,
  .axis-summary-card,
  .intervention-period-card,
  .cost-summary-card,
  .scenario-card,
  .generic-card,
  .cost-category-section,
  .key-findings-box,
  .methodology-note {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  tr {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  .no-print,
  .m-head,
  .m-foot,
  .close-x,
  .btn,
  .facility-selector,
  .report-filter-box,
  .cost-category-title button {
    display: none !important;
  }
}

/* Card */
.equipment-report-filters {
  background: #fff;
  border-radius: 22px;

  padding: 28px;

  margin-bottom: 35px;

  border: 1px solid #e5e7eb;

  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08);
}

/* Header */

.filter-header {
  display: flex;
  align-items: center;
  gap: 12px;

  margin-bottom: 24px;

  font-size: 20px;
  font-weight: 700;
  color: #1e3a8a;
}

.filter-header i {
  width: 42px;
  height: 42px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;

  background: #eff6ff;
  color: #2563eb;
}

/* Grid */

.filter-grid {
  display: grid;

  grid-template-columns: 2fr 1fr auto;

  gap: 22px;

  align-items: end;
}

.equipment-filter-field {
  display: flex;
  flex-direction: column;
}

.equipment-filter-field label {
  font-weight: 700;
  margin-bottom: 10px;

  color: #334155;

  display: flex;
  align-items: center;
  gap: 8px;
}

.equipment-filter-field label i {
  color: #2563eb;
}

.equipment-filter-field select {
  height: 52px;

  border: 1px solid #cbd5e1;

  border-radius: 14px;

  padding: 0 18px;

  font-size: 15px;

  transition: 0.25s;

  background: #fff;
}

.equipment-filter-field select:focus {
  border-color: #2563eb;

  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12);

  outline: none;
}

/* Button */

.equipment-filter-action {
  display: flex;
}

.equipment-btn {
  height: 52px;

  padding: 0 30px;

  border: none;

  border-radius: 14px;

  background: #1d4ed8;

  color: #fff;

  font-weight: 700;

  cursor: pointer;

  transition: 0.25s;

  display: flex;

  align-items: center;

  gap: 10px;
}

.equipment-btn:hover {
  background: #1e40af;

  transform: translateY(-2px);
}

.equipment-btn:disabled {
  opacity: 0.6;

  cursor: not-allowed;

  transform: none;
}

/* Responsive */

@media (max-width: 900px) {
  .filter-grid {
    grid-template-columns: 1fr;
  }

  .equipment-btn {
    width: 100%;
    justify-content: center;
  }

  .equipment-facility-header {
    margin-bottom: 22px;
    padding: 18px;
    text-align: center;
    background: #f7fafc;
    border: 1px solid #dbe5ef;
    border-right: 5px solid #123f74;
    border-radius: 7px;
  }

  .equipment-facility-header h2 {
    margin: 0 0 8px;
    color: #123f74;
    font-size: 21px;
  }

  .equipment-facility-header p {
    margin: 0;
    text-align: center;
  }

  .equipment-summary-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(130px, 1fr));
    gap: 12px;
    margin-bottom: 24px;
  }

  .equipment-summary-card {
    padding: 17px 12px;
    text-align: center;
    background: #f8fafc;
    border: 1px solid #dbe3ed;
    border-top: 4px solid #64748b;
    border-radius: 7px;
  }

  .equipment-summary-card span {
    display: block;
    margin-bottom: 7px;
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
  }

  .equipment-summary-card strong {
    color: #0f2340;
    font-size: 24px;
  }

  .equipment-summary-card.available {
    border-top-color: #15803d;
  }

  .equipment-summary-card.unavailable,
  .equipment-summary-card.broken {
    border-top-color: #b91c1c;
  }

  .equipment-summary-card.not-applicable {
    border-top-color: #64748b;
  }

  .medical-equipment-table {
    min-width: 1250px;
  }

  .medical-equipment-table th,
  .medical-equipment-table td {
    text-align: right;
    vertical-align: middle;
  }

  .equipment-notes {
    min-width: 180px;
    white-space: normal;
  }

  .availability-badge,
  .general-status-badge,
  .equipment-status-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 65px;
    padding: 4px 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 800;
  }

  .availability-yes {
    color: #166534;
    background: #dcfce7;
  }

  .availability-no {
    color: #991b1b;
    background: #fee2e2;
  }

  .availability-na {
    color: #475569;
    background: #e2e8f0;
  }

  .general-excellent {
    color: #166534;
    background: #dcfce7;
  }

  .general-good {
    color: #166534;
    background: #ecfdf5;
  }

  .general-fair {
    color: #92400e;
    background: #fef3c7;
  }

  .general-broken {
    color: #991b1b;
    background: #fee2e2;
  }

  .general-unknown {
    color: #475569;
    background: #e2e8f0;
  }

  .equipment-status-badge.required {
    color: #991b1b;
    background: #fee2e2;
  }

  .equipment-status-badge.conditional {
    color: #92400e;
    background: #fef3c7;
  }

  @media (max-width: 900px) {
    .equipment-summary-grid {
      grid-template-columns: repeat(2, minmax(130px, 1fr));
    }

    .equipment-filter-field {
      min-width: 100%;
    }
  }

  @media print {
    .equipment-summary-grid {
      grid-template-columns: repeat(5, 1fr) !important;
      gap: 6px !important;
    }

    .equipment-summary-card {
      padding: 8px 5px !important;
      break-inside: avoid;
      page-break-inside: avoid;
    }

    .equipment-summary-card span {
      font-size: 8px !important;
    }

    .equipment-summary-card strong {
      font-size: 15px !important;
    }

    .medical-equipment-table {
      min-width: 0 !important;
      width: 100% !important;
      table-layout: fixed;
    }

    .medical-equipment-table th,
    .medical-equipment-table td {
      padding: 4px 3px !important;
      font-size: 7px !important;
      word-break: break-word;
    }

    .availability-badge,
    .general-status-badge,
    .equipment-status-badge {
      min-width: auto !important;
      padding: 2px 4px !important;
      font-size: 6.5px !important;
    }
  }
}

.equipment-facility-header {
  margin-bottom: 22px;
  padding: 18px;
  text-align: center;
  background: #f7fafc;
  border: 1px solid #dbe5ef;
  border-right: 5px solid #123f74;
  border-radius: 7px;
}

.equipment-facility-header h2 {
  margin: 0 0 8px;
  color: #123f74;
  font-size: 21px;
}

.equipment-facility-header p {
  margin: 0;
  text-align: center;
}

.equipment-summary-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(130px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
}

.equipment-summary-card {
  padding: 17px 12px;
  text-align: center;
  background: #f8fafc;
  border: 1px solid #dbe3ed;
  border-top: 4px solid #64748b;
  border-radius: 7px;
}

.equipment-summary-card span {
  display: block;
  margin-bottom: 7px;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
}

.equipment-summary-card strong {
  color: #0f2340;
  font-size: 24px;
}

.equipment-summary-card.available {
  border-top-color: #15803d;
}

.equipment-summary-card.unavailable,
.equipment-summary-card.broken {
  border-top-color: #b91c1c;
}

.equipment-summary-card.not-applicable {
  border-top-color: #64748b;
}

.medical-equipment-table {
  min-width: 1250px;
}

.medical-equipment-table th,
.medical-equipment-table td {
  text-align: right;
  vertical-align: middle;
}

.equipment-notes {
  min-width: 180px;
  white-space: normal;
}

.resource-report .equipment-summary-card small {
  display: block;
  margin-top: 4px;
  color: #64748b;
  font-weight: 700;
}

.stock-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 92px;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 800;
  font-size: 0.84rem;
  background: #e2e8f0;
  color: #334155;
}

.stock-sufficient {
  background: #dcfce7;
  color: #166534;
}

.stock-insufficient {
  background: #fef3c7;
  color: #92400e;
}

.stock-out_of_stock {
  background: #fee2e2;
  color: #991b1b;
}

/* =========================================================
   Bilingual direction and mobile improvements
========================================================= */
.reports-page {
  font-family: "Inter", "Cairo", sans-serif;
  overflow-x: hidden;
}

.reports-page[dir="rtl"] {
  font-family: "Cairo", "Inter", sans-serif;
}

.reports-page[dir="rtl"] .section-head,
.reports-page[dir="rtl"] .modal,
.reports-page[dir="rtl"] .print-report {
  text-align: right;
}

.reports-page[dir="ltr"] .section-head,
.reports-page[dir="ltr"] .modal,
.reports-page[dir="ltr"] .print-report {
  text-align: left;
}

.reports-page[dir="ltr"] .report-filter-box label,
.reports-page[dir="ltr"] table th,
.reports-page[dir="ltr"] table td {
  text-align: left;
}

.reports-page[dir="rtl"] .report-filter-box label,
.reports-page[dir="rtl"] table th,
.reports-page[dir="rtl"] table td {
  text-align: right;
}

/* Keep and emphasize the Ministry of Health logo */
.report-logo-side:first-child {
  display: flex;
  align-items: center;
  justify-content: center;
}

.yemen-logo {
  display: block;
  width: auto;
  max-width: 108px;
  max-height: 92px;
  object-fit: contain;
}

.mobile-report-cards {
  display: none;
}

@media (max-width: 900px) {
  .reports-page {
    padding: 16px 10px 28px !important;
  }

  .section-head {
    margin-bottom: 16px;
  }

  .section-head h2 {
    font-size: 23px !important;
    line-height: 1.5;
  }

  .section-head .desc {
    font-size: 13px;
    line-height: 1.8;
  }

  .report-grid {
    grid-template-columns: 1fr !important;
    gap: 12px !important;
  }

  .report-card {
    min-width: 0;
    padding: 15px !important;
    border-radius: 16px !important;
  }

  .rc-top {
    align-items: flex-start !important;
    gap: 10px !important;
  }

  .rc-top h4 {
    min-width: 0;
    font-size: 15px !important;
    line-height: 1.7;
    overflow-wrap: break-word;
  }

  .rc-num {
    flex-shrink: 0;
  }

  .rc-meta {
    display: grid !important;
    grid-template-columns: 1fr !important;
    gap: 7px !important;
  }

  .btn {
    width: 100%;
    min-height: 44px;
  }

  .modal-backdrop {
    align-items: flex-start !important;
    padding: 8px !important;
    overflow-y: auto;
  }

  .modal {
    width: 100% !important;
    max-width: none !important;
    max-height: none !important;
    margin: 0 !important;
    border-radius: 15px !important;
  }

  .m-head {
    position: sticky;
    top: 0;
    z-index: 100;
    gap: 10px;
    padding: 13px !important;
    background: #fff;
    border-bottom: 1px solid #e2e8f0;
  }

  .m-head h3 {
    min-width: 0;
    font-size: 16px !important;
    line-height: 1.6;
  }

  .close-x {
    min-width: 42px;
    min-height: 42px;
    flex-shrink: 0;
  }

  .m-body {
    padding: 10px !important;
  }

  .print-report {
    padding: 12px !important;
  }

  .report-logos {
    display: grid !important;
    grid-template-columns: 1fr !important;
    justify-items: center;
    gap: 8px !important;
  }

  .report-logo-center,
  .report-logo-side:last-child {
    display: none !important;
  }

  .report-logo-side:first-child {
    display: flex !important;
    width: 100%;
  }

  .yemen-logo {
    max-width: 92px;
    max-height: 82px;
  }

  .official-report-title h1 {
    font-size: 19px !important;
    line-height: 1.6;
  }

  .report-system-name,
  .pr-head .sub {
    font-size: 12px !important;
    line-height: 1.7;
  }

  .report-reference {
    display: grid !important;
    grid-template-columns: 1fr !important;
    gap: 6px !important;
  }

  .report-filter-box,
  .equipment-report-filters,
  .facility-selector {
    padding: 12px !important;
    border-radius: 13px !important;
  }

  .filter-grid,
  .identity-grid,
  .axis-summary-grid,
  .intervention-grid,
  .cost-summary-grid,
  .equipment-summary-grid {
    grid-template-columns: 1fr !important;
  }

  .report-filter-box select,
  .report-filter-box input,
  .equipment-report-filters select,
  .equipment-report-filters input,
  .facility-select,
  .equipment-btn {
    width: 100% !important;
    min-width: 0 !important;
    min-height: 44px;
    box-sizing: border-box;
  }

  .table-scroll {
    width: 100%;
    overflow-x: visible;
    border-radius: 10px;
  }

  .table-scroll table,
  .print-report > table {
    min-width: 720px;
  }

  .identity-card,
  .axis-summary-card,
  .intervention-period-card,
  .scenario-card,
  .equipment-summary-card {
    min-width: 0;
  }

  .key-findings-box,
  .methodology-note,
  .empty-section,
  .loading-box,
  .error-box {
    padding: 12px !important;
    font-size: 12px !important;
    line-height: 1.8;
  }

  .facility-report-header h1,
  .equipment-facility-header h2 {
    font-size: 18px !important;
    line-height: 1.6;
  }
}

@media (max-width: 420px) {
  .reports-page {
    padding-inline: 8px !important;
  }

  .report-card,
  .print-report {
    padding: 10px !important;
  }

  .official-report-title h1 {
    font-size: 17px !important;
  }
}

@media print {
  /* Ministry logo remains visible in printed/PDF reports */
  .report-logo-side:first-child,
  .yemen-logo {
    display: block !important;
    visibility: visible !important;
  }
}

/* =========================================================
   Final mobile modal fix
   يجعل نافذة التقرير كاملة الشاشة على الهاتف
========================================================= */
@media screen and (max-width: 768px) {
  .modal-backdrop {
    position: fixed !important;
    inset: 0 !important;
    z-index: 99999 !important;
    display: block !important;
    width: 100% !important;
    height: 100dvh !important;
    padding: 0 !important;
    overflow: hidden !important;
    background: #f4f6f9 !important;
    backdrop-filter: none !important;
  }

  .modal {
    position: relative !important;
    display: flex !important;
    flex-direction: column !important;
    width: 100% !important;
    max-width: none !important;
    height: 100dvh !important;
    max-height: 100dvh !important;
    margin: 0 !important;
    overflow: hidden !important;
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    animation: none !important;
  }

  .m-head {
    position: relative !important;
    z-index: 20 !important;
    display: flex !important;
    flex: 0 0 auto !important;
    align-items: center !important;
    justify-content: space-between !important;
    gap: 10px !important;
    min-height: 58px !important;
    padding: 10px 12px !important;
    background: #ffffff !important;
    border-bottom: 1px solid #dbe3ee !important;
    box-shadow: 0 2px 8px rgba(15, 35, 64, 0.06) !important;
  }

  .m-head h3 {
    min-width: 0 !important;
    margin: 0 !important;
    font-size: 15px !important;
    line-height: 1.55 !important;
    white-space: normal !important;
    overflow-wrap: anywhere !important;
  }

  .close-x {
    display: grid !important;
    width: 42px !important;
    min-width: 42px !important;
    height: 42px !important;
    padding: 0 !important;
    place-items: center !important;
    color: #334155 !important;
    font-size: 19px !important;
    background: #f1f5f9 !important;
    border: 1px solid #dbe3ee !important;
    border-radius: 10px !important;
  }

  .m-body {
    flex: 1 1 auto !important;
    min-width: 0 !important;
    min-height: 0 !important;
    width: 100% !important;
    max-height: none !important;
    padding: 8px !important;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    background: #f4f6f9 !important;
  }

  .print-report {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    margin: 0 !important;
    padding: 12px !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
    background: #ffffff !important;
    border: 1px solid #dbe3ee !important;
    border-radius: 12px !important;
    box-shadow: none !important;
  }

  .official-report-header {
    width: 100% !important;
    min-width: 0 !important;
  }

  .report-logos {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100% !important;
    min-width: 0 !important;
  }

  .report-logo-side:first-child {
    display: flex !important;
    justify-content: center !important;
    width: 100% !important;
  }

  .report-logo-center,
  .report-logo-side:last-child {
    display: none !important;
  }

  .yemen-logo {
    width: auto !important;
    max-width: 86px !important;
    max-height: 78px !important;
    object-fit: contain !important;
  }

  .official-report-title,
  .facility-report-header,
  .equipment-facility-header {
    width: 100% !important;
    min-width: 0 !important;
    padding-inline: 0 !important;
  }

  .official-report-title h1,
  .facility-report-header h1,
  .equipment-facility-header h2 {
    font-size: 17px !important;
    line-height: 1.65 !important;
    white-space: normal !important;
    overflow-wrap: anywhere !important;
  }

  .report-system-name,
  .report-reference,
  .pr-head .sub {
    font-size: 11px !important;
    line-height: 1.7 !important;
  }

  .report-reference {
    display: grid !important;
    grid-template-columns: 1fr !important;
    gap: 5px !important;
  }

  .report-filter-box,
  .equipment-report-filters,
  .facility-selector {
    display: grid !important;
    width: 100% !important;
    min-width: 0 !important;
    gap: 10px !important;
    padding: 12px !important;
    box-sizing: border-box !important;
  }

  .filter-grid {
    display: grid !important;
    grid-template-columns: 1fr !important;
    width: 100% !important;
    min-width: 0 !important;
    gap: 10px !important;
  }

  .report-filter-box select,
  .report-filter-box input,
  .equipment-report-filters select,
  .equipment-report-filters input,
  .facility-select,
  .equipment-btn {
    display: block !important;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    height: 46px !important;
    box-sizing: border-box !important;
  }

  .identity-grid,
  .axis-summary-grid,
  .intervention-grid,
  .cost-summary-grid,
  .equipment-summary-grid {
    grid-template-columns: 1fr !important;
  }

  .table-scroll {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow-x: visible !important;
    overflow-y: visible !important;
  }

  .table-scroll table {
    width: max-content !important;
    min-width: 680px !important;
    max-width: none !important;
  }

  .print-report > table {
    display: table !important;
    width: 100% !important;
    min-width: 0 !important;
    table-layout: fixed !important;
  }

  .print-report > table td {
    white-space: normal !important;
    overflow-wrap: anywhere !important;
  }

  .m-foot {
    position: relative !important;
    z-index: 20 !important;
    display: flex !important;
    flex: 0 0 auto !important;
    flex-direction: column !important;
    gap: 8px !important;
    padding: 10px 12px !important;
    background: #ffffff !important;
    border-top: 1px solid #dbe3ee !important;
  }

  .m-foot .btn,
  .m-foot button {
    width: 100% !important;
    min-height: 44px !important;
  }
}

@media screen and (max-width: 380px) {
  .m-body {
    padding: 5px !important;
  }

  .print-report {
    padding: 9px !important;
    border-radius: 9px !important;
  }

  .m-head h3 {
    font-size: 14px !important;
  }
}

/* =========================================================
   R07 + عرض جميع جداول التقارير كاملة بدون سكرول أفقي
========================================================= */
@media screen and (min-width: 901px) {
  .modal {
    width: min(92vw, 1280px);
    max-width: 1280px;
  }
}

.print-report .table-scroll,
.print-report .cost-details-table,
.print-report .r07-table-wrap {
  display: block !important;
  width: 100% !important;
  max-width: 100% !important;
  overflow-x: visible !important;
  overflow-y: visible !important;
}

.print-report table,
.print-report .table-scroll table,
.print-report .cost-details-table table,
.print-report .r07-table-wrap table {
  width: 100% !important;
  min-width: 0 !important;
  max-width: 100% !important;
  table-layout: fixed !important;
}

.print-report table th,
.print-report table td {
  min-width: 0 !important;
  padding: 7px 5px;
  white-space: normal !important;
  word-break: break-word;
  overflow-wrap: anywhere !important;
}

.numeric-cell {
  direction: ltr;
  unicode-bidi: isolate;
  text-align: center !important;
  font-variant-numeric: tabular-nums;
  white-space: nowrap !important;
}

.r07-budget-table,
.r07-phasing-table,
.r07-items-table {
  margin-bottom: 18px !important;
}

.r07-budget-table th:first-child {
  width: 4%;
}

.r07-budget-table th:nth-child(2) {
  width: 18%;
}

.r07-budget-table th:nth-child(3) {
  width: 18%;
}

.r07-budget-table th:nth-child(n + 4) {
  width: 10%;
}

.r07-phasing-table th:first-child {
  width: 36%;
}

.r07-phasing-table th:not(:first-child) {
  width: 16%;
}

.r07-items-table th:first-child {
  width: 26%;
}

.r07-items-table th:nth-child(2) {
  width: 6%;
}

.r07-items-table th:nth-child(3),
.r07-items-table th:nth-child(4) {
  width: 10%;
}

.r07-items-table th:nth-child(n + 5) {
  width: 12%;
}

.r07-facility-section {
  margin: 26px 0 34px;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
}

.r07-facility-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
  padding: 12px 14px;
  color: #ffffff;
  background: #123f74;
  border-radius: 8px;
}

.r07-facility-title span {
  font-size: 11px;
}

.r07-facility-title h4 {
  margin: 2px 0 0;
  color: #ffffff;
}

.r07-facility-title .r07-facility-name-en {
  color: #dbeafe;
}

.r07-facility-grand-total {
  flex: 0 0 auto;
  min-width: 170px;
  padding: 8px 12px;
  text-align: center;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 7px;
}

.r07-facility-grand-total span,
.r07-facility-grand-total strong {
  display: block;
}

.r07-facility-grand-total strong {
  direction: ltr;
  unicode-bidi: isolate;
  margin-top: 3px;
  color: #ffffff;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.r07-facility-meta {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 16px;
}

.r07-facility-meta > span {
  min-width: 0;
  padding: 9px 10px;
  color: #475569;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 11px;
}

.r07-facility-meta strong {
  display: inline-block;
  margin-inline-start: 4px;
  color: #0f2747;
}

.r07-facility-phase-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 16px;
}

.r07-facility-phase-grid > div {
  min-width: 0;
  padding: 10px;
  text-align: center;
  background: #f8fafc;
  border: 1px solid #dbe5ef;
  border-top: 3px solid #009ad8;
  border-radius: 6px;
}

.r07-facility-phase-grid span,
.r07-facility-phase-grid strong {
  display: block;
}

.r07-facility-phase-grid span {
  color: #64748b;
  font-size: 10px;
  font-weight: 700;
}

.r07-facility-phase-grid strong {
  direction: ltr;
  unicode-bidi: isolate;
  margin-top: 4px;
  color: #0f2747;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.r07-facility-phase-grid .r07-phase-total {
  color: #ffffff;
  background: #123f74;
  border-color: #123f74;
}

.r07-facility-phase-grid .r07-phase-total span,
.r07-facility-phase-grid .r07-phase-total strong {
  color: #ffffff;
}

.r07-empty-costs {
  margin: 12px 0 0 !important;
}

.r07-cost-group {
  margin-top: 18px;
}

.r07-cost-group-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 7px;
  padding: 8px 10px;
  color: #123f74;
  background: #edf4fb;
  border-right: 4px solid #009ad8;
  border-radius: 5px;
}

.r07-cost-group-title > strong {
  flex: 0 0 auto;
  min-width: 58px;
  padding: 5px 8px;
  color: #ffffff;
  text-align: center;
  background: #123f74;
  border-radius: 5px;
}

.r07-cost-group-title h4 {
  margin: 0;
  color: #123f74;
}

.r07-cost-group-title span {
  display: block;
  margin-top: 2px;
  color: #64748b;
  font-size: 10px;
  font-weight: 700;
}

.r07-facility-name-en,
.r07-money-cell small {
  display: block;
  margin-top: 3px;
  color: #64748b;
  font-size: 9px;
  font-weight: 600;
}

.r07-facility-name-en {
  direction: ltr;
  text-align: right;
}

@media screen and (max-width: 900px) {
  .print-report table,
  .print-report .table-scroll table,
  .print-report .cost-details-table table,
  .print-report .r07-table-wrap table {
    display: table !important;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    table-layout: fixed !important;
    font-size: 8px !important;
  }

  .print-report table th,
  .print-report table td {
    padding: 4px 3px !important;
  }

  .r07-facility-section {
    padding: 8px;
  }

  .r07-facility-meta {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .r07-facility-phase-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media screen and (max-width: 600px) {
  .print-report table,
  .print-report .table-scroll table,
  .print-report .cost-details-table table,
  .print-report .r07-table-wrap table {
    font-size: 6.5px !important;
  }

  .print-report table th,
  .print-report table td {
    padding: 3px 2px !important;
    line-height: 1.45;
  }

  .r07-facility-title {
    align-items: flex-start;
    flex-direction: column;
  }

  .r07-facility-grand-total {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  .r07-facility-meta,
  .r07-facility-phase-grid {
    grid-template-columns: 1fr;
  }
}

@page {
  size: 297mm 210mm;
  margin: 9mm 8mm 11mm;
}

@media print {
  :global(html),
  :global(body),
  :global(#app) {
    width: 100% !important;
    min-width: 0 !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .reports-page {
    position: static !important;
    visibility: visible !important;
    width: 100% !important;
    max-width: 100% !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
    background: #ffffff !important;
  }

  .reports-page > .section-head,
  .reports-page > .report-grid {
    display: none !important;
  }

  .modal-backdrop {
    position: static !important;
    inset: auto !important;
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .modal,
  .m-body,
  .print-report {
    position: static !important;
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    min-height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    box-sizing: border-box !important;
  }

  .reports-page,
  .modal-backdrop,
  .modal,
  .m-body,
  .print-report,
  .print-report .table-scroll,
  .print-report .cost-details-table,
  .print-report .r07-table-wrap {
    overflow: visible !important;
    max-height: none !important;
  }

  .r07-print-report {
    width: 100% !important;
    max-width: 100% !important;
  }

  .r07-details-heading {
    break-before: auto !important;
    page-break-before: auto !important;
    break-after: avoid !important;
    page-break-after: avoid !important;
  }

  .r07-details-heading + .r07-facility-section {
    break-before: auto !important;
    page-break-before: auto !important;
  }

  .r07-facility-section + .r07-facility-section {
    break-before: auto !important;
    page-break-before: auto !important;
  }

  .r07-facility-section {
    margin: 16px 0 22px;
    padding: 8px;
    border-radius: 0;
    overflow: visible !important;
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
    break-inside: auto !important;
    page-break-inside: auto !important;
  }

  .r07-facility-title {
    display: block !important;
    width: auto !important;
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
    break-inside: auto !important;
    page-break-inside: auto !important;
    break-after: avoid !important;
    page-break-after: avoid !important;
  }

  .r07-facility-title > div {
    display: block !important;
    width: auto !important;
  }

  .r07-facility-grand-total {
    min-width: 0 !important;
    margin-top: 7px !important;
    text-align: right !important;
  }

  .r07-facility-meta,
  .r07-facility-phase-grid {
    display: table !important;
    width: 100% !important;
    table-layout: fixed !important;
    border-collapse: separate !important;
    border-spacing: 4px !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  .r07-facility-meta > span,
  .r07-facility-phase-grid > div {
    display: table-cell !important;
    width: 25% !important;
    vertical-align: middle !important;
  }

  .r07-cost-group-title {
    display: block !important;
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
    break-inside: auto !important;
    page-break-inside: auto !important;
    break-after: avoid !important;
    page-break-after: avoid !important;
  }

  .r07-cost-group {
    break-inside: auto !important;
    page-break-inside: auto !important;
  }

  .r07-print-report .r07-items-table {
    font-size: 7px !important;
  }

  .r07-print-report .r07-items-table th,
  .r07-print-report .r07-items-table td {
    padding: 4px 3px !important;
    line-height: 1.35 !important;
  }

  .r07-print-report .numeric-cell {
    white-space: normal !important;
  }
}

/* R13/R14/R15: readable totals, complete facility details and print output. */
.inventory-report .inventory-summary-grid {
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
}
.inventory-report.medical-equipment-report .inventory-summary-grid {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}
.inventory-report.medical-equipment-report .equipment-summary-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  min-height: 138px;
  box-sizing: border-box;
  break-inside: avoid;
}
.inventory-report.medical-equipment-report .equipment-summary-card span {
  margin-bottom: 12px;
  font-size: 14px;
}
.inventory-report.medical-equipment-report .equipment-summary-card strong {
  font-size: 30px;
  line-height: 1.2;
}
@media screen and (max-width: 1100px) {
  .inventory-report.medical-equipment-report .inventory-summary-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media screen and (max-width: 600px) {
  .inventory-report.medical-equipment-report .inventory-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media print {
  .inventory-report.medical-equipment-report .inventory-summary-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
  }
  .inventory-report.medical-equipment-report .equipment-summary-card {
    min-height: 80px;
  }
  .inventory-report.medical-equipment-report .equipment-summary-card span {
    margin-bottom: 6px;
    font-size: 10pt;
  }
  .inventory-report.medical-equipment-report .equipment-summary-card strong {
    font-size: 18pt;
  }
}
.inventory-report .equipment-summary-card small,
.inventory-report .inventory-cell-note {
  display: block;
  margin-top: 5px;
  color: #64748b;
  font-size: 11px;
  line-height: 1.6;
  font-weight: 500;
}
.inventory-report .equipment-summary-card.old,
.inventory-report .equipment-summary-card.insufficient {
  border-top-color: #b45309;
}
.inventory-report .equipment-summary-card.out_of_stock {
  border-top-color: #b91c1c;
}
.inventory-report .equipment-summary-card.sufficient {
  border-top-color: #15803d;
}
.inventory-mode {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 7px 16px;
}
.inventory-mode > span {
  padding: 3px 10px;
  border-radius: 999px;
  background: #eaf0f7;
  color: #23486d;
  font-size: 12px;
}
.inventory-summary-note {
  color: #64748b;
  font-size: 12px;
}
.medicine-facility-summary {
  margin-top: 20px;
  break-inside: avoid;
}
.medicine-facility-summary > h3 {
  margin-bottom: 6px;
}
.medicine-availability-table th:nth-child(2) {
  width: 30%;
}
.medicine-availability-table th:nth-child(3) {
  width: 20%;
}
.medicine-availability-table th:nth-child(4) {
  width: 50%;
}
.medicine-availability-table .empty-cell {
  padding: 18px;
  color: #64748b;
  text-align: center;
}
.inventory-report .inventory-table-scroll {
  max-width: 100%;
  overflow-x: auto;
}
.inventory-report .inventory-table {
  width: 100%;
  min-width: 720px;
  table-layout: auto;
}
.inventory-report .inventory-detail-table {
  min-width: 1060px;
}
.inventory-report .inventory-table th {
  color: #fff;
  background: #15355c;
  padding: 10px 7px;
}
.inventory-report .inventory-table th,
.inventory-report .inventory-table td {
  border: 1px solid #d7e1ec;
  text-align: start;
  vertical-align: top;
  line-height: 1.7;
  white-space: normal;
  overflow-wrap: anywhere;
}
.inventory-report .inventory-table .inventory-index {
  width: 28px;
  min-width: 28px;
  padding: 8px 5px;
  text-align: center;
}
.inventory-report .inventory-table .inventory-numeric {
  text-align: center;
}
.inventory-dates {
  display: grid;
  gap: 4px;
  min-width: 120px;
}
.inventory-status-badge {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 7px;
  background: #e2e8f0;
  color: #334155;
  font-size: 11px;
  font-weight: 800;
}
.inventory-status-old,
.inventory-status-fair,
.inventory-status-insufficient {
  background: #fef3c7;
  color: #92400e;
}
.inventory-status-broken,
.inventory-status-no,
.inventory-status-out_of_stock {
  background: #fee2e2;
  color: #991b1b;
}
.inventory-status-yes,
.inventory-status-available,
.inventory-status-fully_available,
.inventory-status-good,
.inventory-status-excellent,
.inventory-status-sufficient {
  background: #dcfce7;
  color: #166534;
}
.inventory-status-partially_available,
.inventory-status-sometimes_available,
.inventory-status-on_call {
  background: #fef3c7;
  color: #92400e;
}
.inventory-status-not_available,
.inventory-status-unavailable {
  background: #fee2e2;
  color: #991b1b;
}
.inventory-status-mixed {
  background: #dbeafe;
  color: #1e40af;
}
@media screen and (max-width: 700px) {
  .print-report .inventory-report .inventory-table {
    display: block !important;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    font-size: 12px !important;
    border: 0;
  }
  .inventory-report .inventory-table thead {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }
  .inventory-report .inventory-table tbody {
    display: grid;
    gap: 12px;
  }
  .inventory-report .inventory-table tbody tr {
    display: block;
    overflow: hidden;
    border: 1px solid #d7e1ec;
    border-radius: 9px;
    background: #fff;
  }
  .print-report .inventory-report .inventory-table tbody td {
    display: grid;
    grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
    align-items: start;
    gap: 4px 12px;
    width: auto !important;
    min-width: 0 !important;
    padding: 9px 12px !important;
    text-align: start !important;
    font-size: 12px !important;
    border: 0;
    border-bottom: 1px solid #e8eef5;
  }
  .inventory-report .inventory-table tbody td:last-child {
    border-bottom: 0;
  }
  .inventory-report .inventory-table tbody td::before {
    content: attr(data-label);
    grid-column: 1;
    grid-row: 1 / span 3;
    font-weight: 700;
    color: #475569;
  }
  .inventory-report .inventory-table tbody td > * {
    grid-column: 2;
    min-width: 0;
  }
  .inventory-report .inventory-table .inventory-index {
    background: #eef4fa;
  }
  .inventory-report .inventory-table .inventory-cell-note {
    margin-top: 0;
  }
  .inventory-report .inventory-table .inventory-dates {
    min-width: 0;
  }
  .print-report .inventory-report .inventory-table tbody td.empty-table {
    display: block;
  }
  .inventory-report .inventory-table .empty-table::before {
    content: none;
  }
}
@media print {
  .inventory-report .inventory-table-scroll {
    overflow: visible !important;
  }
  .inventory-report .inventory-table,
  .inventory-report .inventory-detail-table {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    table-layout: fixed !important;
    font-size: 8pt !important;
  }
  .inventory-report .inventory-table th,
  .inventory-report .inventory-table td {
    padding: 4px !important;
    line-height: 1.4 !important;
  }
  .inventory-report .inventory-table tr {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .inventory-report .inventory-table thead {
    display: table-header-group;
  }
  .inventory-report .inventory-cell-note,
  .inventory-report .inventory-status-badge {
    font-size: 7pt !important;
  }
  .inventory-report .inventory-dates {
    min-width: 0;
  }
  .inventory-report .inventory-table .inventory-index {
    width: 18px !important;
    min-width: 0 !important;
  }
}
</style>
